/**
 * Pre-written RAMS templates. Each template provides the standard scope,
 * hazards, controls, sequence and PPE for a common construction task.
 *
 * The user fills in site-specific placeholders (plot/area, specific scope
 * additions, etc.) and the app generates a populated PDF.
 *
 * IMPORTANT: review and adapt content before using on a real site. These are
 * starter templates, not legally vetted documents.
 */

export interface RamsHazard {
  hazard: string;
  risk: 'High' | 'Med' | 'Low';
  controls: string;          // semicolon-separated control measures
}

export interface RamsTemplate {
  id: string;
  title: string;
  trades: string[];          // primary trade is trades[0]; include other trades for cross-discovery
  description: string;       // short blurb for picker card
  defaultHazards: string[];  // chip values from HAZARD_PRESETS
  scope: string;             // standard scope text
  hazards: RamsHazard[];
  sequence: string[];        // numbered steps
  equipment: string[];
  ppe: string[];
  emergency: string;         // standard emergency arrangements
}

export const RAMS_TEMPLATES: RamsTemplate[] = [
  // ============================================================
  {
    id: 'site_setup',
    title: 'Site setup & welfare establishment',
    trades: ['Groundworks', 'Foundations & concrete'],
    description: 'First-day-on-site activities: welfare, hoarding, signage, services, traffic management.',
    defaultHazards: ['Manual handling', 'Plant / pedestrian interaction', 'Electrical', 'Slips / trips'],
    scope:
      'Establish the construction site for the project — install hoarding to the perimeter, erect site signage, position welfare and storage units, energise temporary supplies, and put in place pedestrian / vehicle traffic management before any productive work commences.',
    hazards: [
      { hazard: 'Manual handling of welfare units, hoarding panels and signage', risk: 'Med', controls: 'Two-person lifts above 25 kg; mechanical aid (telehandler, HIAB) for cabins and large panels; route planned and walked first.' },
      { hazard: 'Plant moving on site for cabin / unit positioning', risk: 'High', controls: 'Banksman in hi-vis directing all reverses; pedestrians cleared of swing/lift area; reverse alarm and beacon working before any movement.' },
      { hazard: 'Public access to construction area', risk: 'High', controls: '1.8m secure hoarding before work starts; site signage at every entry point; locked gates outside hours; signs warning of construction.' },
      { hazard: 'Trip hazards from cables and trailing leads', risk: 'Med', controls: 'Site lighting routed overhead where possible; cables in covers across pedestrian routes; daily housekeeping check.' },
      { hazard: 'Energising temporary electrical supply', risk: 'High', controls: 'Qualified electrician only; RCD on supply; 110V centre-tapped earth where practical; PAT testing before plug-in; isolation lock-out for any work on supply.' },
      { hazard: 'Existing services (gas, water, drainage)', risk: 'High', controls: 'Service drawings reviewed; CAT-scan before any excavation; meter locations marked; no breaking ground before signed-off Permit-to-Dig.' },
    ],
    sequence: [
      'Pre-start meeting with main contractor and site manager — confirm welfare location, hoarding line, traffic plan.',
      'Banksman positioned for any reversing or lifting plant.',
      'Erect perimeter hoarding to agreed line; install site safety signage at all entrances.',
      'Position welfare unit, storage container and material laydown areas using telehandler / HIAB.',
      'Connect welfare to temporary water and electrical supplies (qualified electrician + plumber).',
      'Energise lighting; check RCD trips on test button.',
      'Mark service runs from drawings (gas, water, drainage); CAT-scan and confirm before any digging.',
      'Stock first aid kit, fire extinguishers, eyewash. Display emergency contacts in cabin.',
      'Brief all early-arriving subcontractors on the site rules and emergency arrangements.',
    ],
    equipment: [
      'Telehandler / HIAB for unit positioning',
      'Hand tools for hoarding assembly',
      'CAT-scan and Genny for service location',
      'PPE per task',
      'First aid kit, eyewash, fire extinguishers (CO₂ + foam)',
    ],
    ppe: [
      'Hard hat',
      'Safety boots (mid-cut, mid-sole protection)',
      'Hi-vis jacket / vest (Class 2 minimum)',
      'Gloves (cut-resistant for hoarding panels)',
      'Eye protection when drilling fixings',
    ],
    emergency:
      'Nearest A&E: as per site induction notice. Site manager carries phone with address and what3words location. First aiders identified on the welfare cabin door. In the event of serious injury — call 999, then notify the Director.',
  },

  // ============================================================
  {
    id: 'strip_out',
    title: 'Strip-out & soft demolition',
    trades: ['Demolition', 'Carpentry & joinery'],
    description: 'Removal of internal fittings, fixtures, partitions and surfaces in preparation for refurbishment.',
    defaultHazards: ['Asbestos', 'Manual handling', 'Silica dust', 'Slips / trips', 'Electrical', 'Working at height'],
    scope:
      'Remove specified internal fixtures, fittings, finishes and partitions to prepare the building for refurbishment / extension works. Excludes structural elements unless covered by a specific structural alterations RAMS.',
    hazards: [
      { hazard: 'Asbestos-containing materials (ACMs) — pipe lagging, AIB, artex, floor tiles, asbestos cement', risk: 'High', controls: 'Refurb/Demolition Survey on file before any disturbance; do not strip suspect materials; if uncovered unexpectedly — STOP, photograph, mark, contact Director; licensed contractor for any licensable work.' },
      { hazard: 'Lead paint on pre-1992 paint surfaces', risk: 'Med', controls: 'Test pre-1992 paint before scraping/sanding; use HEPA vac and FFP3 if positive; wash skin and overalls before breaks.' },
      { hazard: 'Manual handling of fittings, panels, debris', risk: 'High', controls: 'Two-person lifts; sack truck / chute for heavy items; clear chuted debris from below before next drop; load skip from outside the building.' },
      { hazard: 'Silica dust from cutting / breaking', risk: 'High', controls: 'Wet cutting or on-tool extraction (M-class); FFP3 mask; do not dry-sweep; HEPA vacuum after cutting.' },
      { hazard: 'Live electrical circuits in walls and ceilings', risk: 'High', controls: 'Isolate the building supply at consumer unit before any wall / ceiling penetration; lock-out tag at MCB; voltage tester on suspect cables; competent electrician for any uncertain circuits.' },
      { hazard: 'Working at height during ceiling and high-level strip', risk: 'High', controls: 'Mobile tower or podium step; no ladders for two-handed work; check tower stability and toe-boards.' },
      { hazard: 'Slips/trips on debris', risk: 'Med', controls: 'End-of-shift clearance non-negotiable; walkways kept clear during the shift; bagged debris removed continuously, not at the end.' },
      { hazard: 'Vermin / biological hazards in older buildings', risk: 'Low', controls: 'Gloves and overalls; report droppings / nests to site manager; pest control if needed before further work.' },
    ],
    sequence: [
      'Verify Refurb/Demolition Survey is on file. Confirm no licensable ACMs in scope.',
      'Isolate electrical supply to working area at the consumer unit. Lock out and tag.',
      'Isolate water supply if removing sanitaryware or pipework. Drain down where practical.',
      'Clear the room of loose furniture, fittings and stored items. Salvage agreed items per the spec.',
      'Strip carpets, underlay, vinyl. Inspect floor for ACM tiles before continuing.',
      'Remove sanitaryware, kitchen units, doors and ironmongery.',
      'Strip skirtings, architraves, dado / picture rails.',
      'Remove non-structural partitions and stud walls (per architect\'s drawings).',
      'Strip ceilings to joists where required. Use mobile tower; bag rockwool / debris immediately.',
      'Continuous loading of skip; bagged dust waste sealed before leaving the building.',
      'Final clean-down with HEPA vac; surfaces ready for next trades.',
    ],
    equipment: [
      'Hand tools — pry bars, hammers, wrecking bars',
      'M-class HEPA vacuum',
      'On-tool extraction for any sawing / breaking',
      'Mobile tower or podium step',
      'Sack truck and bagged-debris chute (or load directly outside)',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant and grip gloves',
      'FFP3 mask for any cutting / breaking',
      'Eye protection',
      'Disposable overalls (Type 5/6) when working in dust',
    ],
    emergency:
      'STOP IMMEDIATELY if suspect ACMs are uncovered. Cordon the area; ventilate; notify the site manager who will pause work and arrange a sample test. Other emergencies — 999 then Director. Eyewash, first aid kit and welfare cabin location displayed at site entrance.',
  },

  // ============================================================
  {
    id: 'excavation_foundations',
    title: 'Excavation & foundations',
    trades: ['Excavation', 'Foundations & concrete', 'Groundworks'],
    description: 'Mechanical and hand excavation for footings, slabs and underground services. Reinforcement and concrete pours.',
    defaultHazards: ['Excavation', 'Plant / pedestrian interaction', 'Manual handling', 'Electrical', 'COSHH (chemicals)', 'Slips / trips'],
    scope:
      'Mechanical excavation for foundations, footings and underground services as per the structural engineer\'s drawings. Hand excavation in service zones. Lay reinforcement, formwork and pour concrete to specification.',
    hazards: [
      { hazard: 'Underground services (gas, electric, water, comms)', risk: 'High', controls: 'Service drawings on site; CAT-scan + Genny used before mechanical dig; Permit-to-Dig signed for each excavation; hand-dig last 300 mm to known services.' },
      { hazard: 'Excavation collapse', risk: 'High', controls: 'Sides battered back at safe angle for soil type, OR shoring boxes installed >1.2 m depth; daily inspection by competent person before entry; weekly recorded inspection.' },
      { hazard: 'Persons / plant falling into excavation', risk: 'High', controls: 'Pedestrian barriers + lighting around all open excavations; barriers to ground level when out of sight; never leave excavation unattended without barriers.' },
      { hazard: 'Plant / excavator and people in same area', risk: 'High', controls: 'No-go zones marked on ground; banksman in hi-vis controls all manoeuvres; eye contact with operator before entering swing radius; reverse alarms and beacons mandatory.' },
      { hazard: 'Manual handling of reinforcement bars and formwork', risk: 'Med', controls: 'Two-person lifts for steel; cut to length on the side, not over the dig; mechanical aid for heavy formwork sections.' },
      { hazard: 'Cement / concrete burns and dermatitis', risk: 'Med', controls: 'Long-sleeved overalls; nitrile gloves; eye protection during pour; barrier cream; wash splashes immediately; wash hands and forearms thoroughly before lunch and at end of shift.' },
      { hazard: 'Water in excavation', risk: 'Med', controls: 'Sump and pump if water table is high; do not enter standing water without dewatering; consider sides destabilised if water is present.' },
      { hazard: 'Slips on muddy edges', risk: 'Med', controls: 'Spoil heaps positioned >1 m from edge; access steps cut into spoil where used; clean boots before entering welfare.' },
    ],
    sequence: [
      'Check service drawings; mark service runs in spray paint at ground level.',
      'CAT-scan and Genny over the excavation footprint; mark any anomalies.',
      'Issue Permit-to-Dig with named operator and start/stop times.',
      'Mechanical excavation to within 300 mm of any known service.',
      'Hand dig the final 300 mm in service zones with insulated tools.',
      'Install shoring or batter back to engineer\'s specification.',
      'Erect ground-level pedestrian barriers around the excavation.',
      'Daily inspection by competent person; recorded weekly.',
      'Lay binding, reinforcement and formwork to drawings.',
      'Pour concrete with banksman / pump operator coordination; wear full PPE during pour.',
      'Cure / strike formwork per spec; backfill in compacted layers.',
    ],
    equipment: [
      'Tracked / wheeled excavator with operator',
      'CAT-scan and Genny',
      'Manual digging tools — insulated where near services',
      'Shoring boxes / trench supports',
      'Concrete pump or skip-and-bucket',
      'Vibrating poker',
      'Rebar cutters / benders',
    ],
    ppe: [
      'Hard hat',
      'Safety boots with mid-sole and ankle support',
      'Hi-vis jacket (Class 3 if working alongside operating plant)',
      'Cut-resistant gloves; nitrile gloves under for cement work',
      'Eye protection during cutting and pouring',
      'FFP3 dust mask if breaking concrete',
    ],
    emergency:
      'Service strike — STOP immediately, evacuate excavation, notify site manager. Gas: stop ignition sources, ventilate, call gas emergency 0800 111 999. Electric: do not approach live cables, isolate at meter if safe, call DNO. Person trapped in collapse — DO NOT enter to rescue; dig from outside while emergency services arrive (999).',
  },

  // ============================================================
  {
    id: 'brickwork',
    title: 'Brickwork & blockwork',
    trades: ['Bricklaying'],
    description: 'Construction of internal and external masonry walls from DPC level upwards.',
    defaultHazards: ['Working at height', 'Manual handling', 'COSHH (chemicals)', 'Silica dust', 'Slips / trips'],
    scope:
      'Lay external facing brickwork and internal blockwork to engineer\'s and architect\'s details, from DPC level to roof plate. Includes wall ties, cavity insulation, lintels and flashings.',
    hazards: [
      { hazard: 'Working at height — scaffold or platform', risk: 'High', controls: 'Scaffold tagged within current week before use; toe-boards, guard-rails, mid-rails complete; no working from blocks or planks balanced on scaffold; harness for any unguarded lift.' },
      { hazard: 'Manual handling of blocks (often >25 kg) and bricks', risk: 'High', controls: 'Two-person lift for any block >20 kg; rotate bricklayers; mechanical lift platform / hoist for high-level deliveries; staged drops, not single big lifts.' },
      { hazard: 'Cement burns and dermatitis from mortar', risk: 'Med', controls: 'Nitrile gloves under work gloves; long-sleeved overalls; wash splashes immediately; barrier cream; wash thoroughly before breaks; check skin weekly for cracks/redness.' },
      { hazard: 'Silica dust when cutting bricks / blocks', risk: 'High', controls: 'Wet cutting saw (water suppression) only; FFP3 mask; cut downwind of others; HEPA vac residual dust; never dry-sweep.' },
      { hazard: 'Falling materials onto people below', risk: 'High', controls: 'Toe-boards on all working platforms; debris netting where the public are nearby; kickboards on scaffold; "no-entry" zones below working area; helmets mandatory.' },
      { hazard: 'Eye injury from cement / mortar splashes and chips', risk: 'Med', controls: 'Eye protection during all cutting; eyewash within 30 seconds reach; immediate flush if splashed.' },
      { hazard: 'Slip / trip on debris and wet surfaces', risk: 'Med', controls: 'Daily clearance of brick offcuts and mortar drops from working platform and ground; non-slip platform decking; clean boots before climbing.' },
    ],
    sequence: [
      'Check scaffold tag is current. Inspect platform — toe-boards, guard-rails, mid-rails, kickboards.',
      'Confirm DPC level and string lines with site manager.',
      'Position bricks and blocks on scaffold in safe stacks; do not overload (max ~250 kg / m²).',
      'Mix mortar to spec; mortar boards positioned safely on platform.',
      'Lay first three courses to alignment; check level, plumb and gauge.',
      'Continue laying courses; install ties at correct centres per Building Regs Part A.',
      'Cut bricks/blocks at ground level on wet saw; wear FFP3 and eye protection.',
      'Install lintels per structural engineer\'s schedule.',
      'Install cavity insulation, fire stops and DPC at openings.',
      'Strike joints to specification.',
      'End-of-day clearance: brush down platform, remove offcuts, cover walls if rain forecast.',
    ],
    equipment: [
      'Scaffold (independently tagged)',
      'Forced-action mortar mixer',
      'Trowels, lines, plumb-bobs, levels',
      'Wet-cut bench saw (with water supply)',
      'Forklift / telehandler for material lifts',
      'HEPA vacuum',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant grip gloves; nitrile gloves under for mortar work',
      'Eye protection during cutting',
      'FFP3 mask for cutting',
      'Long-sleeved overalls',
    ],
    emergency:
      'Cement in eye — flush continuously with water for at least 15 minutes, attend A&E. Cuts from cutting equipment — pressure dressing, attend A&E if deep. Fall from height — do not move casualty unless airway / breathing compromised; call 999 immediately. Site emergency contacts displayed in welfare cabin.',
  },

  // ============================================================
  {
    id: 'roof_tiling',
    title: 'Roof structure & tiling',
    trades: ['Roofing', 'Carpentry & joinery'],
    description: 'Erect roof structure (trusses), fix roofing felt, battens and tiles. Includes ridges, valleys, leadwork.',
    defaultHazards: ['Working at height', 'Manual handling', 'Plant / pedestrian interaction', 'Slips / trips', 'Cold conditions', 'Wet weather'],
    scope:
      'Erect roof structure (pre-fabricated trusses or cut roof per drawings); install breathable membrane, counter-battens and battens to falls and centres; tile and finish roof including ridges, hips, valleys and leadwork to abutments.',
    hazards: [
      { hazard: 'Falls from roof edge or through open eaves', risk: 'High', controls: 'Edge protection (scaffold with handrail at gutter line) before any roof work begins; fall-arrest harness anchored to structural element if edge protection not practical for any task; no work if wind speed >25 mph or in lightning.' },
      { hazard: 'Falls through fragile roof areas (rooflights, old felt)', risk: 'High', controls: 'No standing on rooflights or unsupported felt; crawl boards across fragile areas; mark unsafe areas with signage and physical barriers.' },
      { hazard: 'Lifting trusses and tiles up to roof level', risk: 'High', controls: 'Telehandler with truss hook for trusses; tile hoist or pallet lift to platform — never throw tiles up; banksman to control swing.' },
      { hazard: 'Manual handling of tiles, ridges, lead rolls', risk: 'High', controls: 'Maximum batch sizes; rotate the roofers; lead rolled in shorter lengths; mechanical lift to roof level not by hand.' },
      { hazard: 'Slips on wet or icy battens / membrane', risk: 'High', controls: 'No work in wet conditions on uncovered membrane; ice = stop; clean tiles of moss / lichen at start; non-slip safety boots required.' },
      { hazard: 'Working below the roof — falling tiles, debris, tools', risk: 'High', controls: 'Toe-boards, debris netting around scaffold edge; "no-entry" cordon below working area; tools on lanyards; helmets mandatory below.' },
      { hazard: 'Hot leadwork (welding) creating fire risk', risk: 'High', controls: 'Hot Works Permit; fire watch person + extinguisher present; combustibles cleared 10m radius; no hot works in last hour of shift.' },
      { hazard: 'UV exposure (long days outside)', risk: 'Low', controls: 'SPF30+ on neck, ears, arms; long-sleeved hi-vis; hat with neck protection.' },
    ],
    sequence: [
      'Confirm scaffold edge protection at gutter line is in place and tagged.',
      'Position truss hoist / telehandler. Brief banksman.',
      'Lift trusses sequentially per truss drawing; install bracing as you go (do not skip bracing to "do later").',
      'Install joist hangers / nailer plates per spec.',
      'Fix breathable membrane to falls; lap and tape per manufacturer.',
      'Install counter-battens and tiling battens to centres per tile schedule.',
      'Lift tiles to roof on hoist or telehandler; never throw or carry up scaffold by hand in volume.',
      'Lay tiles working from eave to ridge; nail per fixing schedule (perimeter and exposure zones).',
      'Install ridges and hips, bedded and pointed (or dry-fix per spec).',
      'Lead flashings and soakers to all abutments. Hot works permit if welding.',
      'Final inspection: no broken tiles visible, no gaps, gutters clear.',
    ],
    equipment: [
      'Telehandler with truss hook',
      'Tile hoist or pallet lift',
      'Nail guns (gas or compressed air)',
      'Tin snips, lead dressing tools',
      'Slate cutters / wet saw for cuts',
      'Hot works kit if leadwelding (gas torch + extinguisher + heat mat)',
    ],
    ppe: [
      'Hard hat',
      'Safety boots with non-slip sole',
      'Hi-vis (Class 3 when working with operating plant)',
      'Fall-arrest harness if edge protection not in place',
      'Cut-resistant gloves; leather gloves for handling lead',
      'Eye protection',
      'Sun protection in summer',
    ],
    emergency:
      'Fall from height: do not move casualty; call 999 immediately. Fire from hot works: fight only if immediate; otherwise evacuate, call 999, fire watch alerts site manager. Wind exceeds 25 mph or storm rolls in — STOP, secure loose tiles and materials, descend.',
  },

  // ============================================================
  {
    id: 'scaffolding_erect',
    title: 'Scaffolding erection & dismantling',
    trades: ['Scaffolding'],
    description: 'Erection, alteration and striking of tube-and-fitting or system scaffold for residential works.',
    defaultHazards: ['Working at height', 'Manual handling', 'Plant / pedestrian interaction', 'Wet weather', 'Cold conditions'],
    scope:
      'Erect, alter and dismantle scaffolding to TG20 / NASC compliant configuration. Includes ground inspection, foot protection, lifts, ties, bracing, edge protection, ladders / stair towers and tagging. Scaffold designed for the loading required by the trades following on.',
    hazards: [
      { hazard: 'Falls during erection / dismantling', risk: 'High', controls: 'CISRS-carded scaffolders only; advance guard-rail system used as each lift goes up; harnesses with twin lanyards anchored to a sound part of the structure when 2.0m+ and no edge protection in place; never pull boards / guard-rails before the lift below is complete.' },
      { hazard: 'Falling materials onto people below', risk: 'High', controls: 'Exclusion zone around base — barriers + signs; brick guards / fans on town sites; tools on lanyards; deliveries timed when public not nearby; debris netting if working over a footpath.' },
      { hazard: 'Unstable ground / sole boards', risk: 'High', controls: 'Sole boards on every standard regardless of surface; check for soft / made-up ground; spread load on timber pads; never bear directly on tarmac in summer; confirm with site manager if standards are over drains or shallow services.' },
      { hazard: 'Manual handling of tubes, boards, beams', risk: 'Med', controls: 'Two-person lifts for boards >25 kg; mechanical lift for beams and ladder beams to upper lifts; rotate tasks; pre-bundle by lift to avoid dragging up.' },
      { hazard: 'Public / pedestrian interaction at street scaffolds', risk: 'High', controls: 'Pedestrian gantry with covered walkway where required by the council licence; reflective signs, lighting; daily check that any banksman / public protection is in place before lifts.' },
      { hazard: 'Adverse weather — wind, ice, lightning', risk: 'Med', controls: 'STOP at sustained wind >25 mph or gusts >35 mph; ice on platforms = stop; thunderstorm overhead = clear scaffold; wind monitoring on tall jobs.' },
      { hazard: 'Incomplete scaffold being used by others', risk: 'High', controls: 'Yellow "Incomplete — Do Not Use" tag on every access until handover; locked / removed access ladders at end of shift; green "Safe to Use" tag only when fully signed off in writing.' },
    ],
    sequence: [
      'Pre-start: review the scaffold drawing, ground / overhead service plan, council licence, and weather forecast.',
      'Set sole boards / base plates; check for pavement gradient and soft ground.',
      'Erect first lift to standards, transoms and ledgers; install foot ties.',
      'Build successive lifts using advance guard-rail system; install ties to structure at correct frequency.',
      'Install boards, toe-boards, double guard-rail and mid-rail at each working lift.',
      'Install ladder access / stair tower; ladders extending min 1m above platform.',
      'Brick guards on perimeter where stacked materials are stored on the platform.',
      'Sign-off scaffold inspection register; tag green "Safe to Use".',
      'Re-inspect every 7 days, after any alteration, and after adverse weather; record in the inspection register.',
      'Dismantle in reverse order; never drop tubes / boards from height — lower with a gin wheel or pass down hand-to-hand.',
    ],
    equipment: [
      'Scaffold tubes, fittings, boards, beams (or system scaffold)',
      'Sole boards / base plates',
      'Advance guard-rail system',
      'Harnesses with twin lanyards (recovery / emergency lowering plan in place)',
      'Spanners (correct torque)',
      'Gin wheel / hoist for lowering materials',
      'Brick guards, debris netting',
    ],
    ppe: [
      'Hard hat with chin-strap (working at height)',
      'Safety boots',
      'Hi-vis (Class 3 if road-side)',
      'Gloves — cut / grip rated',
      'Harness + twin lanyards (when 2.0m+ without edge protection)',
      'Eye protection during cutting / drilling',
    ],
    emergency:
      'Fall arrest activation — recover the casualty within 15 minutes (suspension trauma). Site manager has the rescue plan; nominate the rescue team at start of shift. Other emergencies — 999, then notify the Director.',
  },

  // ============================================================
  {
    id: 'carpentry_first_fix',
    title: 'Carpentry — 1st fix',
    trades: ['Carpentry & joinery'],
    description: 'Floor joists, stud partitions, door linings, roof timbers and structural carpentry before plastering.',
    defaultHazards: ['Working at height', 'Manual handling', 'Loud noise (>85 dB)', 'Hand-arm vibration (HAVS)', 'Slips / trips'],
    scope:
      'Install structural and pre-plaster carpentry — floor joists and decking, stud partitions, door linings, noggins, soffits, fascias, fire blocking and any 1st fix timber works. Excludes structural roof carpentry covered by a separate RAMS.',
    hazards: [
      { hazard: 'Cuts from circular saws, chop saws, oscillating tools', risk: 'High', controls: 'Guards in place — never removed; riving knives on bench saws; push-sticks for short pieces; never freehand a circular saw on offcuts; trained operator only; emergency stops accessible.' },
      { hazard: 'Nail-gun discharge into hand or foot', risk: 'High', controls: 'Sequential-trip nailers only (not bump-fire) for hand-held work; never carry with finger on trigger; disconnect when leaving the bench; eye and hearing protection mandatory.' },
      { hazard: 'Manual handling of joists, sheets, lintels', risk: 'High', controls: 'Two-person lifts for joists >2.4 m and any 18 mm ply / OSB sheet; sheets carried on edge with grip handles; mechanical lift to upper floors.' },
      { hazard: 'Working at height for stud / joist / fascia work', risk: 'High', controls: 'Mobile tower or podium step (>1.5 m); never work two-handed off a step ladder; harness for any unguarded work at fascia / soffit level.' },
      { hazard: 'Hand-arm vibration from prolonged sawing / drilling', risk: 'Med', controls: 'Low-vibration tools chosen; trigger-time logged informally; rotate tasks; report tingling / whiteness to site manager; gloves help warmth not vibration.' },
      { hazard: 'Loud noise from cutting and nailing', risk: 'Med', controls: 'SNR-rated hearing protection; quieter tools where available; cutting station sited away from non-trade areas; signage where >85 dB.' },
      { hazard: 'Wood dust (softwood / engineered timber)', risk: 'Med', controls: 'On-tool extraction (M-class); FFP3 mask for prolonged cutting indoors; HEPA vac for clean-down; never blow off with compressed air.' },
      { hazard: 'Trip hazards from offcuts and trailing leads', risk: 'Med', controls: 'Continuous tidy as you work; offcut bin within reach; cables routed up walls or in covers; 110V transformer leads kept off floor.' },
    ],
    sequence: [
      'Set out from drawings; mark wall plates and joist positions per the structural engineer.',
      'Install joist hangers / restraint straps to engineer\'s drawings.',
      'Hang and fix joists; check level; install noggins and bracing.',
      'Lay deck — glued and screwed; rotate joins; cut for service penetrations.',
      'Erect stud partitions; soleplate fixed first, then head plate, then studs at 400 / 600 c/c per drawing.',
      'Install door linings plumb; check the diagonal.',
      'Fit fire blocking, intumescent collars and noggins around penetrations to keep fire-stopping intact.',
      'Fascia, soffit and barge boards from a tower / scaffold.',
      'Continuous housekeeping; offcuts to skip end-of-shift minimum.',
    ],
    equipment: [
      'Circular saw, mitre saw, jigsaw, oscillating multitool',
      'Sequential-trip nailers (gas / battery)',
      'Cordless drill / drivers (110V transformer for site-supply)',
      'M-class extractor, HEPA vacuum',
      'Mobile tower / podium step',
      'Hand tools (square, level, tape, marking knife)',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant grip gloves',
      'Eye protection for all power-tool use',
      'SNR ≥27 hearing protection during cutting / nailing',
      'FFP3 mask for prolonged cutting indoors',
    ],
    emergency:
      'Major laceration — pressure dressing, raise the limb, call 999 if arterial. Nail-gun strike to hand or foot — leave fastener in place, attend A&E. Eye injury from offcut — eyewash, attend A&E if symptomatic. Site emergency contacts displayed in welfare cabin.',
  },

  // ============================================================
  {
    id: 'carpentry_second_fix',
    title: 'Carpentry — 2nd fix',
    trades: ['Carpentry & joinery'],
    description: 'Skirtings, architraves, door hanging, ironmongery, stairs, kitchens and finishing carpentry after plastering.',
    defaultHazards: ['Manual handling', 'Loud noise (>85 dB)', 'Hand-arm vibration (HAVS)', 'Slips / trips'],
    scope:
      'Install finishing carpentry to plastered, painted and floored rooms — skirtings and architraves, door hanging and ironmongery, fitted stairs, balustrades and handrails, fitted wardrobes, finishing trims. Protect finished surfaces throughout.',
    hazards: [
      { hazard: 'Cuts from mitre / circular saws and chisels', risk: 'High', controls: 'Mitre saw on stable cutting station; no freehand cuts; guards intact; chisels sharp (blunt = slip); push-sticks for short pieces.' },
      { hazard: 'Nail-gun strikes (hands holding the moulding)', risk: 'High', controls: 'Sequential-trip only; keep the holding hand 150 mm clear of the firing line; mark the line before firing; eye protection mandatory.' },
      { hazard: 'Manual handling of doors, stairs, wardrobes', risk: 'Med', controls: 'Two-person lifts for fire doors and pre-built stair flights; door dollies for moving around the site; clear route planned.' },
      { hazard: 'Damage to finished surfaces causing rework risk and fall hazards', risk: 'Low', controls: 'Floor protection laid before any 2nd fix; plastic edge protectors on door reveals; skirting cushions; vacuum sawdust off polished floors immediately to avoid slips.' },
      { hazard: 'Hand-arm vibration from prolonged routing / sanding', risk: 'Med', controls: 'Low-vibration tools; rotate tasks; report symptoms; logged as part of HAVS health surveillance.' },
      { hazard: 'Lifting glass-panelled doors', risk: 'Med', controls: 'Suction cups for any glass door; two-person; no lift onto flat surfaces — onto soft pad or rubber stops only; never lean against a wall on a single edge.' },
    ],
    sequence: [
      'Confirm rooms are dust-free, painted to mist coat, and ready for 2nd fix.',
      'Lay floor protection (Correx + tape) to all rooms being worked.',
      'Hang internal doors — check FD30 fire doors have intumescent strips and smoke seals fitted to spec.',
      'Fit ironmongery — handles, hinges, latches, locks, door closers (closers tested and adjusted).',
      'Fit skirtings — scribe internal corners or 45° mitres; nail and glue.',
      'Fit architraves — mitred at heads.',
      'Install fitted wardrobes / cupboards if in scope.',
      'Stair balustrades and handrails — verify regs-compliant 100 mm sphere rule on infill.',
      'Final adjustment, snag walk, vacuum, lift floor protection.',
    ],
    equipment: [
      'Mitre saw and saw bench',
      'Sequential-trip nailer / pin gun',
      'Cordless drill / driver',
      'Plane, chisels, files',
      'Sash cramps for door hanging',
      'Floor protection (Correx, dust sheets)',
      'M-class extractor',
    ],
    ppe: [
      'Hard hat (where required by site rules)',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant grip gloves',
      'Eye protection',
      'SNR ≥27 hearing protection during cutting / nailing',
      'FFP3 mask for sanding',
    ],
    emergency:
      'Cut to hand — pressure, raise, A&E if deep. Nail-gun strike — leave fastener, A&E. Falling door — drop and step back; never try to catch. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'drylining_plastering',
    title: 'Drylining & plastering',
    trades: ['Drylining & plastering'],
    description: 'Plasterboard fixing, taping & jointing, two-coat plastering and screeding.',
    defaultHazards: ['Manual handling', 'COSHH (chemicals)', 'Working at height', 'Slips / trips'],
    scope:
      'Plasterboard to stud partitions and ceilings, tape and joint OR two-coat plaster finish to walls and ceilings. Includes corner beads, scrim, jointing compound and finish skim. Floor screeding where in scope.',
    hazards: [
      { hazard: 'Manual handling of plasterboard sheets (often 25–35 kg)', risk: 'High', controls: 'Two-person lifts; board lifter for ceiling work; mechanical hoist to upper floors; never carry single-handed up stairs.' },
      { hazard: 'Working at height for ceiling boarding / skimming', risk: 'High', controls: 'Mobile tower or stilts (only for trained users on level floors); podium step for low ceiling work; never stilts on a bare joist deck or near openings.' },
      { hazard: 'Cement / gypsum burns and dermatitis', risk: 'Med', controls: 'Nitrile gloves under work gloves for cement-based work; long sleeves; immediate wash-off; barrier cream; check skin weekly.' },
      { hazard: 'Eye splashes from plaster / jointing compound', risk: 'Med', controls: 'Eye protection during ceiling work; eyewash within 30s; flush immediately if splashed.' },
      { hazard: 'Slips on wet floors during plastering', risk: 'Med', controls: 'Drips controlled with wet-floor signs; tarps over finished floors; non-slip footwear; clean drops as they happen.' },
      { hazard: 'Dust during board cutting / sanding joints', risk: 'Med', controls: 'On-tool extraction for board cutting; HEPA vac for sanding; FFP3 mask; never dry-sweep.' },
    ],
    sequence: [
      'Confirm 1st fix carpentry, electrical and plumbing complete and signed off.',
      'Hoist boards to floor; stack on dry surface, supported flat.',
      'Set out boards — stagger joints; orient long edge perpendicular to studs / joists.',
      'Fix boards with collated screws to centres per manufacturer.',
      'Install corner beads, fix arch beads, set scrim to all joints and angles.',
      'Tape and joint OR apply scratch coat (browning / hardwall) and skim coat per spec.',
      'Stilts may be used for skimming ceilings only by trained users on a level continuous floor.',
      'Final sand of T&J or final trowel of plaster as appropriate.',
      'Sweep and HEPA vac at end of each shift; never dry-sweep gypsum.',
      'Floor protection re-laid before next trade enters.',
    ],
    equipment: [
      'Board lifter / hoist',
      'Stilts (trained users only)',
      'Collated screw gun',
      'Mixing bucket and paddle',
      'Hawk, trowel, jointing knives',
      'Sanding pole with M-class extraction',
      'Mobile tower / podium step',
    ],
    ppe: [
      'Hard hat (when site rules require)',
      'Safety boots',
      'Hi-vis vest',
      'Nitrile gloves under cut-resistant for cement-based',
      'Eye protection during ceiling work',
      'FFP3 mask during cutting / sanding',
      'Long-sleeved overalls',
    ],
    emergency:
      'Plaster / cement in eye — flush continuously 15 minutes minimum, A&E. Cement burn to skin — wash thoroughly, A&E if blistered. Fall from stilts — call 999 if head impact or unconsciousness. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'electrical_first_fix',
    title: 'Electrical — 1st fix',
    trades: ['Electrical'],
    description: 'Cabling, back-boxes, consumer-unit prep and conduit before plastering.',
    defaultHazards: ['Electrical', 'Working at height', 'Manual handling', 'Slips / trips'],
    scope:
      'Install 1st fix electrical cabling, back-boxes, conduit, earth bonding and consumer-unit termination ready for the dwelling to be 2nd fixed and tested. All work to BS 7671 18th Edition.',
    hazards: [
      { hazard: 'Live circuits where existing supply is in place', risk: 'High', controls: 'Isolate at the meter / origin before any work on existing circuits; lock-out tag at the MCB; voltage tester (proven dead, then dead, then proven dead) to GS38; competent person only; permit-to-work for any energised work.' },
      { hazard: 'Drilling into hidden services (gas, water, other circuits)', risk: 'High', controls: 'Service drawings reviewed; cable detector on every penetration through walls / floors; safe zones per BS 7671; never drill blind.' },
      { hazard: 'Working at height for ceiling cabling / lighting drops', risk: 'High', controls: 'Mobile tower / podium step; never stand on top platform of step ladder; harness for any unguarded work at height.' },
      { hazard: 'Cuts from cable strippers and hand tools', risk: 'Med', controls: 'Sharp insulated tools; cut away from the body; concentrate when stripping; first aid kit accessible.' },
      { hazard: 'Manual handling of cable drums and consumer units', risk: 'Med', controls: 'Drum stand for cables; two-person carry of CUs; pre-cut runs at the bench, not on the wall.' },
      { hazard: 'Trip hazards from running cables across floors', risk: 'Med', controls: 'Cables routed through joists / studs as soon as installed; clip up not laid loose; covers across walkways pending termination.' },
    ],
    sequence: [
      'Confirm scope with M&E drawings; mark socket / switch / fitting positions.',
      'Isolate any existing supply; lock and tag.',
      'Drill back-boxes; CAT-detect first.',
      'Run cables in safe zones to all outlets; clip and protect with cable capping where they pass through finishes.',
      'Drop cables to consumer-unit position; loop, label and leave.',
      'Install main earth bond and any supplementary bonding.',
      'Mark cable ends clearly with circuit labels.',
      'Site CU back-box / mounting platform.',
      'Continuity test on every circuit before sign-off of 1st fix.',
      'Tidy ends and protect from plastering wet — bag CU and exposed cores.',
    ],
    equipment: [
      'Drum stand and cable rolls',
      'Insulated hand tools',
      'Cable detector / stud detector',
      'Voltage tester (GS38 compliant)',
      'Multifunction tester (MFT) for continuity',
      'Mobile tower / podium step',
      'Cordless drill with hole saws',
      'Lock-off kit and tags',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis vest',
      'Insulated gloves rated for the voltage if any energised work',
      'Eye protection when drilling',
      'FFP3 mask if drilling masonry / breaking finishes',
      'Arc-rated clothing if working near a live distribution board',
    ],
    emergency:
      'Electric shock — DO NOT touch the casualty if still in contact; isolate the supply first; call 999; CPR if non-responsive. Cable strike to a service — STOP; isolate the supply; ventilate if gas; notify site manager. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'electrical_second_fix',
    title: 'Electrical — 2nd fix & testing',
    trades: ['Electrical'],
    description: 'Faceplates, accessories, consumer-unit installation, energisation, inspection & testing.',
    defaultHazards: ['Electrical', 'Working at height', 'Slips / trips'],
    scope:
      'Install 2nd fix electrical accessories, terminate consumer unit, energise installation, perform Inspection & Testing per BS 7671 18th Edition, and issue Electrical Installation Certificate. Domestic dwellings.',
    hazards: [
      { hazard: 'Working on or near live conductors during commissioning', risk: 'High', controls: 'Energise only after dead-test pass on every circuit; lock-out at supply when working in CU; voltage tester proven dead-live-dead before any conductor work; never work alone on energised CU; signed permit-to-work for any specifically energised testing.' },
      { hazard: 'Incorrect terminations causing later fault', risk: 'High', controls: 'Torque screwdriver for CU lugs; visual check for trapped insulation; pull-test on accessory terminals; insulation resistance and continuity recorded for every circuit on EIC.' },
      { hazard: 'RCD / RCBO mis-selection', risk: 'High', controls: 'CU schedule reviewed against final circuits; AFDDs where required; Type A RCDs minimum for socket circuits; verified at testing.' },
      { hazard: 'Working at height for ceiling fittings', risk: 'Med', controls: 'Tower / podium step; never the top step of a stepladder; eye protection when reaching upward.' },
      { hazard: 'Slips on offcuts / packaging during faceplate fitting', risk: 'Med', controls: 'Continuous tidy; offcut bin per room; vacuum at end of shift.' },
    ],
    sequence: [
      'Confirm building works complete; surfaces ready for accessories.',
      'Fit faceplates and accessories to all back-boxes; torque terminals; check for trapped insulation.',
      'Install consumer unit; terminate circuits to CU schedule.',
      'Pre-energisation Inspection — visual check of every circuit, polarity, ring continuity.',
      'Dead tests — continuity (R1+R2), insulation resistance (≥1 MΩ before energising), polarity, ring continuity (R1+Rn / R2).',
      'Energise CU — main switch first, then individual MCBs / RCBOs sequentially.',
      'Live tests — earth fault loop impedance (Zs), prospective fault current (Ipf), RCD operation (½×, 1×, 5×, ramp).',
      'Functional test of every switch, socket, light, RCD, AFDD, smoke alarm.',
      'Issue EIC; client copy + retained copy; building control notified.',
      'Final tidy and clean.',
    ],
    equipment: [
      'Multifunction tester (MFT) — calibrated, in date',
      'Insulation resistance tester (if separate)',
      'Voltage tester (GS38)',
      'Torque screwdriver',
      'Cordless drill',
      'Tower / podium step',
      'Lock-off kit',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis vest',
      'Insulated gloves rated for voltage during energised work',
      'Eye protection',
      'Arc-rated clothing during CU energisation',
    ],
    emergency:
      'Electric shock — isolate supply, do not touch casualty until isolated, 999, CPR if needed. Burns from arc flash — cool with water, do not remove stuck clothing, A&E. Smoke from CU — isolate, evacuate, call 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'plumbing_first_fix',
    title: 'Plumbing — 1st fix',
    trades: ['Plumbing'],
    description: 'Hot, cold, waste and heating pipework chased and run before plastering.',
    defaultHazards: ['Manual handling', 'Hot works', 'Working at height', 'COSHH (chemicals)', 'Slips / trips'],
    scope:
      'Run hot, cold, waste and heating pipework to all fixtures; install soil and vent stack; pressure-test before plastering. Boiler flue position marked. Mains and supply points capped pending 2nd fix.',
    hazards: [
      { hazard: 'Hot works (soldering, brazing) — fire risk', risk: 'High', controls: 'Hot Works Permit signed by site manager; combustibles cleared 5–10 m; fire extinguisher (CO₂) within reach; heat mat behind any solder joint; fire watch 60 min after last flame; no hot works in last hour of shift.' },
      { hazard: 'Drilling into hidden services or finished walls', risk: 'High', controls: 'CAT-detect before drilling; service drawings to hand; never drill blind; safe zones for pipe routes per Part L / Part G.' },
      { hazard: 'Manual handling of soil pipe, water tanks, boilers', risk: 'High', controls: 'Two-person carry; tanks lifted via hoist or telehandler to upper floors; route checked first; never single-handed up stairs.' },
      { hazard: 'Working at height for stack and ceiling pipework', risk: 'Med', controls: 'Tower / podium step; never the top step of a stepladder; eye protection when reaching upward.' },
      { hazard: 'Pipe-freezing kit (CO₂ or liquid N₂) — cold burns', risk: 'Med', controls: 'Gloves rated for cryogenic work; eye protection; ventilation if N₂ used; never seal the system whilst frozen — pressure release.' },
      { hazard: 'Pressure-test failure causing water damage', risk: 'Med', controls: 'Pressure-test in stages; visible test gauge; cap or isolate adjacent finished areas; sheet down before energising mains.' },
    ],
    sequence: [
      'Confirm mechanical & electrical drawings, boiler flue position with main contractor.',
      'Install soil and vent stack; bracket per spec.',
      'Run hot and cold to each appliance position; cap and label.',
      'Run heating circuits — radiator drops, manifold to underfloor.',
      'Mark and protect mains / stop-tap location.',
      'Hot works permit before any soldering / brazing.',
      'Pressure-test all installed systems; record on test sheet.',
      'Capping and labelling for plasterers — cores wrapped in tape.',
      'Tidy and remove offcuts.',
    ],
    equipment: [
      'Pipe-press tool / soldering kit / push-fit tools',
      'Hot works kit (heat mat, CO₂ extinguisher, lighter / blowtorch)',
      'CAT-detector',
      'Pressure-test pump and gauge',
      'Pipe-freezing kit',
      'Tower / podium step',
      'Cordless drill, hole saws',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant grip gloves; cryo gloves for pipe freezing',
      'Eye protection',
      'FFP3 mask during chasing / cutting',
    ],
    emergency:
      'Burn from soldering — cool under running water 20 min; A&E if blistered. Cryo burn — same; do not rub. Fire from hot works — fight if immediate (CO₂); otherwise evacuate, 999. Major leak — isolate stop-tap, contain. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'plumbing_second_fix',
    title: 'Plumbing — 2nd fix & commissioning',
    trades: ['Plumbing'],
    description: 'Sanitaryware, taps, boiler install, gas commissioning, system pressure-test and balance.',
    defaultHazards: ['Manual handling', 'COSHH (chemicals)', 'Hot works', 'Slips / trips'],
    scope:
      'Install sanitaryware (WCs, basins, baths, showers), terminate taps and showers, install and commission boiler / cylinder, balance heating, fill and dose system, pressure-test and issue Benchmark / commissioning certificates. Gas connection by Gas Safe registered engineer only.',
    hazards: [
      { hazard: 'Gas connection / commissioning — explosion / CO risk', risk: 'High', controls: 'Gas Safe registered engineer ONLY; tightness test on every joint; flue analysis at commissioning; never sign off without CO₂/CO ratio in spec; gas certificate issued; CO alarm fitted in occupied dwellings.' },
      { hazard: 'Manual handling of baths, basins, boilers', risk: 'High', controls: 'Two-person lifts; stair trolley for basins / WCs; hoist for cylinders to upper floors; never single-lift a cast bath.' },
      { hazard: 'Cement / silicone / chemical exposure', risk: 'Med', controls: 'Nitrile gloves; ventilation when applying solvents / silicones; eye protection during overhead silicone application; SDS on file for products in use.' },
      { hazard: 'Slips on wet floors during system filling', risk: 'Med', controls: 'Drip trays under fittings; clean spills as they happen; wet floor signs.' },
      { hazard: 'Pressure / scalding during commissioning', risk: 'Med', controls: 'TMV at every basin / shower per Part G; outlet temperature checked at commissioning ≤43°C bath, ≤41°C shower; system filled cold first.' },
    ],
    sequence: [
      'Confirm 2nd fix electrics complete and tested.',
      'Install sanitaryware — WCs, basins, baths, showers; level and bracket; isolation valves at every appliance.',
      'Terminate taps, showers, mixers; check flow and temperature.',
      'Install boiler — Gas Safe engineer connects gas, tightness tests, commissions; flue analysis recorded.',
      'Install cylinder; G3 unvented certificate where applicable.',
      'Fill and dose heating system; bleed radiators; balance flow rates.',
      'Pressure-test heating; pressure-test mains; record on commissioning sheet.',
      'TMV check at every blended outlet; record temperatures.',
      'Issue Benchmark / commissioning certs; copy to client and retained.',
      'Final tidy; remove tape and protectors from finishes.',
    ],
    equipment: [
      'Pipe-press / push-fit / soldering kit',
      'Manometer for gas tightness',
      'Flue gas analyser (calibrated, in date) — Gas Safe',
      'Pressure pump and gauge',
      'Stair trolley / hoist for sanitaryware',
      'Torque wrench for tap fittings',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant grip gloves; nitrile under for chemical work',
      'Eye protection',
      'FFP3 mask if cutting tile / cement boards',
    ],
    emergency:
      'Gas leak — isolate at the ECV, ventilate, no ignition sources, evacuate, call National Gas Emergency 0800 111 999. CO suspected — evacuate, fresh air, 999. Burn from hot water — cool 20 min, A&E if blistered. Major leak — isolate stop-tap, contain. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'tiling',
    title: 'Floor & wall tiling',
    trades: ['Tiling'],
    description: 'Wall and floor tiling — bathrooms, kitchens, splashbacks. Includes cutting, laying, grouting and silicone.',
    defaultHazards: ['Silica dust', 'Manual handling', 'COSHH (chemicals)', 'Working at height', 'Slips / trips'],
    scope:
      'Set out and lay wall and floor tiles to bathrooms, kitchens, splashbacks and tiled rooms per spec. Includes substrate prep (boarding / SLC), cutting, fixing, grouting and silicone seal.',
    hazards: [
      { hazard: 'Silica dust from tile cutting (especially porcelain)', risk: 'High', controls: 'Wet saw with continuous water suppression — never dry-cut indoors; FFP3 mask when cutting; on-tool extraction (M-class) for any dry trim; HEPA vac for clean-down; never dry-sweep.' },
      { hazard: 'Manual handling of large-format tiles and bagged adhesive', risk: 'High', controls: 'Two-person lifts for tiles >600 mm; suction handles for porcelain; mechanical aid for pallets of adhesive; pre-position bags to point of use.' },
      { hazard: 'Adhesive / grout / silicone — dermatitis and eye irritation', risk: 'Med', controls: 'Nitrile gloves; long sleeves; eye protection when mixing; barrier cream; immediate wash of skin contact; SDS on file.' },
      { hazard: 'Working at height for high-level wall tiling', risk: 'Med', controls: 'Mobile tower or podium step; never balance on bath or vanity; eye protection during overhead grout / silicone.' },
      { hazard: 'Slips on wet adhesive / SLC during pour', risk: 'Med', controls: 'Wet floor signage; tarps over adjacent finished areas; clean drops as they happen.' },
      { hazard: 'Cuts from tile edges and offcuts', risk: 'Med', controls: 'Cut-resistant gloves; offcuts to bin not under foot; protect cut tile edges (sharp).' },
    ],
    sequence: [
      'Confirm 1st & 2nd fix complete; substrate dry, level and primed per adhesive maker.',
      'Set out from datum line; lay full tiles around the room; cuts to less-visible edges.',
      'Wet-cut tiles outdoors / in extracted area; FFP3 worn; never dry-cut indoors.',
      'Spread adhesive to manufacturer\'s notch trowel size; lay tiles, beat in, level checked across joints.',
      'Tile-spacers; bay-by-bay; expansion movement joints per spec.',
      '24 hr cure (or fast-set spec) before grouting.',
      'Grout joints; remove residue with damp sponge; final polish with cloth.',
      'Silicone to perimeters and movement joints; tool with finisher.',
      'Final tidy; bagged dust / offcuts to skip.',
    ],
    equipment: [
      'Wet bench saw with water supply',
      'Hand tile cutter / score & snap',
      'Notched trowels (sized per spec)',
      'Mixing paddle / drill',
      'Spirit level, straight edge, tile-spacers',
      'M-class extraction / HEPA vac',
      'Mobile tower / podium step',
      'Suction handles for large-format tiles',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis vest (where site rules require)',
      'Nitrile gloves under cut-resistant',
      'Eye protection — always when cutting or working overhead',
      'FFP3 mask during cutting',
    ],
    emergency:
      'Tile-saw cut — pressure dressing, A&E if deep. Adhesive in eye — flush 15 min, A&E. Slip injury on wet floor — assess, A&E if needed. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'decorating',
    title: 'Painting & decorating',
    trades: ['Decorating'],
    description: 'Mist coat, undercoat, top coats, woodwork, wallpaper. Includes prep — sanding, filling, masking.',
    defaultHazards: ['COSHH (chemicals)', 'Working at height', 'Manual handling', 'Slips / trips'],
    scope:
      'Prepare and decorate walls, ceilings, woodwork and metalwork — sanding, filling, mist coat, undercoat, top coats. Wallpaper to feature walls per spec.',
    hazards: [
      { hazard: 'Solvent / paint fumes — VOCs', risk: 'Med', controls: 'Water-based paints by default; ventilate by opening windows / using extraction; eye protection when using solvents; spray-painting requires RPE per the spec.' },
      { hazard: 'Falls from steps / towers', risk: 'High', controls: 'Tower for ceilings; podium step for high walls; never the top platform of a stepladder; harness for any unguarded fascia / soffit.' },
      { hazard: 'Sanding dust — silica from filler, lead from old paint', risk: 'High', controls: 'Test pre-1992 paint for lead; if positive use HEPA vac and FFP3; on-tool extraction for power sanders; never dry-sweep.' },
      { hazard: 'Slips on dustsheets / wet paint', risk: 'Med', controls: 'Anti-slip dustsheets only; clean wet drips; non-slip footwear.' },
      { hazard: 'Eye injury from spatter or sanding debris', risk: 'Med', controls: 'Eye protection during sanding and spraying; eyewash within 30s.' },
      { hazard: 'Manual handling of paint kettles, paper rolls, sprayers', risk: 'Low', controls: 'Decant from large tubs into kettles to keep loads light; trolley for moving sprayers.' },
    ],
    sequence: [
      'Confirm rooms ready — plaster dry, screwed and stopped, surfaces clean.',
      'Mask floors, sockets, switches, ironmongery.',
      'Fill and sand any defects; M-class extraction or HEPA vac after.',
      'Mist coat to new plaster; allow to dry.',
      'Undercoat woodwork after caulking and priming knots.',
      'Topcoat — typically two coats walls and ceilings; multiple coats woodwork.',
      'Hang wallpaper to feature walls per spec; cut around services with sharp knife.',
      'Cut-in, snag walk; touch-ups.',
      'Remove masking; final clean and floor sweep.',
    ],
    equipment: [
      'Brushes, rollers, paint kettles',
      'Power sander with HEPA extraction',
      'Mobile tower / podium step',
      'Masking tape, dustsheets (anti-slip)',
      'Wallpaper paste table and pasting kit',
      'Airless sprayer (if in scope) with appropriate RPE',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis vest (where site rules require)',
      'Nitrile gloves',
      'Eye protection during sanding / spraying / overhead work',
      'FFP3 mask during sanding; A1 P3 cartridge mask for solvent spray',
    ],
    emergency:
      'Paint / solvent in eye — flush 15 min, A&E. Slip / fall — assess; A&E if any head impact. Solvent fume overcome — fresh air, 999 if non-responsive. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'insulation',
    title: 'Insulation install',
    trades: ['Insulation'],
    description: 'Mineral wool, PIR / Celotex, sheep\'s wool to floors, walls, lofts. Cavity insulation and breather membranes.',
    defaultHazards: ['Manual handling', 'COSHH (chemicals)', 'Working at height', 'Slips / trips'],
    scope:
      'Install thermal and acoustic insulation to floors, walls and lofts — mineral wool batts, PIR / Celotex board, sheep\'s wool. Includes cavity insulation, breather membranes and air-tightness taping per spec.',
    hazards: [
      { hazard: 'Mineral wool fibres — skin and respiratory irritation', risk: 'Med', controls: 'Disposable overalls (Type 5) with hood; FFP3 mask; nitrile gloves under work gloves; long sleeves cuffed; cool / shower after shift; do NOT brush off fibres — vacuum.' },
      { hazard: 'PIR off-gassing during cutting (isocyanates)', risk: 'Med', controls: 'Cut outdoors or in well-ventilated area; FFP3 mask; do not burn; SDS on file.' },
      { hazard: 'Falls through ceiling / loft joists', risk: 'High', controls: 'Crawl boards across joists in lofts; never step off the boards onto plasterboard; lighting in loft; tower or podium for high walls.' },
      { hazard: 'Manual handling of board / roll bundles', risk: 'Med', controls: 'Pre-cut to size at the bench; rolls broken down at point of use; no above-shoulder lifting from a stack.' },
      { hazard: 'Working in confined / hot loft', risk: 'Med', controls: 'Hydration breaks every 60 min in summer; pair work — never alone in a loft; phone within reach; loft hatch open for ventilation.' },
      { hazard: 'Slips on offcuts / packaging', risk: 'Med', controls: 'Continuous tidy; offcut bag in the loft; vacuum at end of day.' },
    ],
    sequence: [
      'Confirm 1st fix electrical and plumbing in place; service penetrations marked.',
      'Lay crawl boards across loft joists.',
      'Install lower layer between joists (e.g. 100 mm mineral wool), tight to perimeter.',
      'Install upper layer perpendicular over joists where two-layer system specified.',
      'Wall insulation — between studs; cut snug, no gaps; fix breather membrane to outer face per detail.',
      'Floor insulation — between joists or on suspended deck per drawings.',
      'Air-tightness taping at junctions, penetrations, eaves; verify by visual inspection.',
      'Loft hatch insulation and draught seal.',
      'Final HEPA vac and tidy.',
    ],
    equipment: [
      'Insulation cutting saw / serrated knife',
      'Crawl boards',
      'Tower / podium step',
      'M-class HEPA vacuum',
      'Stapler / tape gun for membranes',
      'Cordless drill for batten fixings',
    ],
    ppe: [
      'Hard hat (where site rules require)',
      'Safety boots',
      'Hi-vis vest',
      'Disposable overalls Type 5 with hood',
      'Nitrile gloves under cut-resistant',
      'Eye protection',
      'FFP3 mask',
    ],
    emergency:
      'Fibre / dust in eye — flush 15 min, A&E if irritation persists. Severe overheating in loft — STOP, descend, hydrate, cool down, 999 if symptoms persist. Fall through ceiling — assess, do not move if back / neck pain, 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'windows_doors',
    title: 'Windows & external doors install',
    trades: ['Carpentry & joinery'],
    description: 'Install timber, uPVC or aluminium windows and external doors to weather-tight standard.',
    defaultHazards: ['Manual handling', 'Working at height', 'Slips / trips', 'Wet weather'],
    scope:
      'Install windows and external doors to FENSA / Building Regs standard — set in opening with appropriate fixings, expanding tape and sealants; reveals dressed; hardware fitted and adjusted.',
    hazards: [
      { hazard: 'Glass breakage during handling', risk: 'High', controls: 'Two-person lift for any pane >1 m²; suction cups for glazed units; padded trolley; heavy gloves rated for glass; never lay panes flat — store on edge.' },
      { hazard: 'Manual handling of frames and glass', risk: 'High', controls: 'Mechanical aid (telehandler / hoist) for upper floors and for any large unit; planned route; clear of trip hazards before lifting.' },
      { hazard: 'Working at height with weight of frame / glass', risk: 'High', controls: 'Scaffold edge protection at gutter; never out of a window during install; harness if scaffold not viable; banksman to receive frames lifted by mechanical means.' },
      { hazard: 'Falls into the opening before frame fitted', risk: 'High', controls: 'Temporary edge protection (handrail or boarding) across every opening before frame removal; never leave an opening unguarded at end of shift.' },
      { hazard: 'Cuts from old glass / metalwork during refurb removal', risk: 'Med', controls: 'Heavy cut-resistant gloves; long sleeves; eye protection; tape over panes before removal; bin glass into rigid waste skip.' },
      { hazard: 'Wet / cold weather during fitting', risk: 'Med', controls: 'No fitting in driving rain; sealants need surfaces dry; PPE for cold; reschedule if conditions unsuitable.' },
    ],
    sequence: [
      'Pre-survey — measure on the day, confirm sizes against opening.',
      'Remove existing windows / doors (refurb only); cap services; install temporary handrail across opening.',
      'Position DPC and cill / threshold; set frame plumb and level; pack and fix per manufacturer.',
      'Apply expanding airtight tape internally and externally per detail.',
      'Foam fill perimeter where applicable; trim flush.',
      'Hardware fit and adjust; check operation of every sash / leaf.',
      'External seal — silicone or low-modulus sealant; tool to neat finish.',
      'Internal reveal dressed (plasterboard / re-skim) — by following trade.',
      'Test water shed; confirm self-closing on FD30 doors.',
      'Issue FENSA cert (where applicable).',
    ],
    equipment: [
      'Suction cups for glazed units',
      'Spirit level, plumb-bob, packers',
      'Cordless drill, hammer drill for fixings',
      'Foam gun, sealant gun',
      'Telehandler / hoist for upper-floor units',
      'Tower / scaffold for external work',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis Class 2 minimum (Class 3 with operating plant)',
      'Heavy cut-resistant gloves rated for glass',
      'Eye protection',
      'FFP3 mask if breaking out concrete cills',
    ],
    emergency:
      'Glass cut — pressure dressing, A&E. Frame falling out of opening — clear below; never try to catch; call site manager. Fall from opening — 999, do not move casualty if back / neck pain. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'external_render',
    title: 'External render',
    trades: ['Drylining & plastering'],
    description: 'Sand-and-cement, monocouche or thin-coat polymer render to external walls.',
    defaultHazards: ['Working at height', 'Manual handling', 'COSHH (chemicals)', 'Wet weather', 'UV exposure'],
    scope:
      'Apply external render to walls per spec — sand-and-cement scratch + topcoat, monocouche, or thin-coat polymer on EWI. Includes beads, mesh, expansion joints and curing protection.',
    hazards: [
      { hazard: 'Working at height — full elevation render', risk: 'High', controls: 'Scaffold tagged within current week; double guard-rail and toe-boards; rails in place at every working lift; never two-handed work from a step-up; harness for unguarded gable work.' },
      { hazard: 'Manual handling of render bags (25 kg)', risk: 'High', controls: 'Mechanical hoist of bags to upper lifts; never carry bags up scaffold by hand in volume; mortar mixed centrally and lifted up.' },
      { hazard: 'Cement burns and dermatitis', risk: 'Med', controls: 'Nitrile gloves; long sleeves; immediate wash; barrier cream; check skin weekly.' },
      { hazard: 'Falling render / tools onto people below', risk: 'High', controls: 'Toe-boards; debris netting on residential streets; no-go zones below; tools on lanyards; helmets mandatory below.' },
      { hazard: 'Adverse weather — rain washes off, heat dries too fast', risk: 'Med', controls: 'No render in heavy rain or temperatures <5°C / >30°C; protect freshly applied work with hessian and water in heat; cover with sheeting in light rain.' },
      { hazard: 'UV exposure on long days outside', risk: 'Low', controls: 'SPF30+ on neck, ears, arms; long-sleeved hi-vis; hat with neck protection; hydration.' },
    ],
    sequence: [
      'Confirm scaffold tagged. Inspect platform.',
      'Substrate prep — clean, dampen if specified, prime / dub-out.',
      'Install beads — corner, bell, stop, expansion — fixed plumb and true.',
      'Mix render to spec; lift to platform mechanically.',
      'Apply scratch coat; rule off; key with comb / scratcher.',
      '24 hr+ cure before topcoat (per spec).',
      'Apply topcoat; finish to spec — sponge, scraped, dashed, stippled.',
      'Protect from rain / sun for the cure period.',
      'Final clean down of beads, edges, surroundings.',
    ],
    equipment: [
      'Scaffold (independently tagged)',
      'Forced-action mortar mixer',
      'Hawk, trowel, float, ruler, scratchers',
      'Bead-cutting snips',
      'Hoist for materials',
      'Render-spray pump (where applicable)',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis vest',
      'Nitrile gloves under cut-resistant',
      'Eye protection during application',
      'FFP3 mask if dry-mixing / spraying',
      'Sun protection',
    ],
    emergency:
      'Cement in eye — flush 15 min minimum, A&E. Fall from scaffold — 999, do not move casualty. Heat exhaustion — shade, hydrate, 999 if non-responsive. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'drainage',
    title: 'Foul & surface drainage',
    trades: ['Groundworks'],
    description: 'Below-ground drainage runs, manholes, gullies, soakaways and connections to mains sewer.',
    defaultHazards: ['Excavation', 'Manual handling', 'Plant / pedestrian interaction', 'COSHH (chemicals)', 'Slips / trips'],
    scope:
      'Install below-ground foul and surface water drainage per architect / engineer\'s drawings — runs, manholes, gullies, rainwater pipes to soakaway / connection points. Pressure-test and CCTV (where required) before backfill.',
    hazards: [
      { hazard: 'Excavation collapse', risk: 'High', controls: 'Battering or trench boxes >1.2 m depth; daily inspection by competent person; weekly recorded; never enter unsupported above 1.2 m.' },
      { hazard: 'Underground services in dig area', risk: 'High', controls: 'Service drawings reviewed; CAT-scan before mechanical dig; Permit-to-Dig per excavation; hand-dig last 300 mm to known services.' },
      { hazard: 'Persons / plant in same area', risk: 'High', controls: 'Banksman; no-go zone marked; pedestrian barriers around live excavations.' },
      { hazard: 'Foul water / sewage exposure (refurb connections)', risk: 'High', controls: 'Wash hands and forearms thoroughly; nitrile gloves under work gloves; never handle without barrier; cover any cut on skin; tetanus check current.' },
      { hazard: 'Manual handling of pipes, manhole rings, bedding', risk: 'Med', controls: 'Mechanical aid (excavator) for manhole rings; two-person carry of long pipe sections; pre-cut at the bench.' },
      { hazard: 'Confined-space entry to chambers >1.2 m', risk: 'High', controls: 'Risk-assess each chamber; never enter without atmospheric monitoring; tripod and harness; permit-to-enter; rescue plan and rescuer present.' },
    ],
    sequence: [
      'Set out runs from drawings; mark on ground.',
      'CAT-scan over each run; Permit-to-Dig.',
      'Excavate trench to depth and gradient; bedding granular per spec.',
      'Lay pipework on bedding to falls; jointed per manufacturer.',
      'Install manholes / inspection chambers; benching in concrete.',
      'Air or water test each section; record results.',
      'Backfill in compacted layers; granular surround min 100 mm above pipe; native fill above.',
      'Restore surface — paving, kerbs, soft landscaping.',
      'CCTV survey if required by spec.',
    ],
    equipment: [
      '360° excavator with operator',
      'CAT-scan + Genny',
      'Pipe-cutting saw',
      'Compaction plate / wacker',
      'Tripod, harness and gas monitor (for any chamber entry)',
      'Pressure / vac test kit',
    ],
    ppe: [
      'Hard hat',
      'Safety boots with mid-sole and ankle support',
      'Hi-vis Class 3',
      'Nitrile gloves under cut-resistant',
      'Eye protection',
      'FFP3 mask in dusty conditions',
      'Tetanus and Hep A vaccinations recommended for any foul-water work',
    ],
    emergency:
      'Excavation collapse — DO NOT enter to rescue; dig from outside while emergency services arrive (999). Sewage exposure to wound — wash, cover, A&E. Service strike — STOP, isolate, ventilate if gas, 999 / 0800 111 999 (gas). Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'hot_works',
    title: 'Hot works (welding / cutting / grinding)',
    trades: ['Steel erection', 'Plumbing'],
    description: 'Welding, cutting, brazing, grinding — generic hot works permit-driven RAMS for use across trades.',
    defaultHazards: ['Hot works', 'Loud noise (>85 dB)', 'Hand-arm vibration (HAVS)', 'COSHH (chemicals)', 'Working at height'],
    scope:
      'Carry out hot works (welding, brazing, cutting, grinding) under a Hot Works Permit. Generic RAMS — to be used alongside the trade-specific RAMS for the work being done.',
    hazards: [
      { hazard: 'Fire from sparks / hot metal', risk: 'High', controls: 'Hot Works Permit signed before work starts; combustibles cleared 10 m radius; fire-resistant blanket / heat mat behind / below the work; CO₂ extinguisher within reach; fire watch person present and 60 min after last spark; no hot works in last hour of shift.' },
      { hazard: 'Welding fume — metal fume fever, lung damage', risk: 'High', controls: 'On-tool extraction; LEV where indoors; FFP3 minimum, RPE per the metal (e.g. stainless steel = chrome / nickel); ventilate; medical surveillance for regular welders.' },
      { hazard: 'Arc flash and UV burn to eyes', risk: 'High', controls: 'Auto-darkening welding helmet; face shield for grinding; screen the bay; warn anyone in the area before strike; never look at the arc.' },
      { hazard: 'Compressed gas cylinders (oxy / acetylene / argon)', risk: 'High', controls: 'Cylinders upright, secured by chain; flashback arrestors fitted; check hoses for damage daily; close valves at end of shift; transport with caps fitted.' },
      { hazard: 'Hand-arm vibration from grinding', risk: 'Med', controls: 'Low-vibration grinder; rotate operator; trigger-time logged; HAVS health surveillance for regular grinders.' },
      { hazard: 'Loud noise (>100 dB grinding)', risk: 'High', controls: 'SNR ≥30 hearing protection; cordon off the work area; signage.' },
      { hazard: 'Working at height during welding / cutting', risk: 'High', controls: 'Tower or scaffold — never a stepladder; harness if unguarded; spark catch tray below; cover combustibles below.' },
    ],
    sequence: [
      'Pre-start — Hot Works Permit issued by site manager; hazards walked.',
      'Clear 10 m radius of combustibles; cover anything that can\'t be moved with a fire blanket.',
      'Set up extraction / LEV; check air monitoring if confined.',
      'Position fire extinguisher and ensure fire-watch person is briefed and ready.',
      'PPE check — helmet, gloves, leathers, RPE, hearing.',
      'Carry out the work; never leave the tool unattended whilst hot.',
      'On finishing — cool the workpiece; visual fire-watch 60 min minimum.',
      'Permit closed by site manager; final inspection of the area.',
    ],
    equipment: [
      'MIG / TIG / arc welder OR oxy-acetylene cutting set',
      'Angle grinder (110V or battery)',
      'Welding helmet (auto-darkening)',
      'CO₂ extinguisher (5 kg minimum)',
      'Fire blankets / heat mats',
      'On-tool extraction / LEV',
      'Flashback arrestors on gas hoses',
    ],
    ppe: [
      'Welding helmet (auto-darkening) for arc work',
      'Welding leathers / FR overall',
      'Welding gloves',
      'Safety boots',
      'Hearing protection SNR ≥30',
      'FFP3 RPE minimum (higher for stainless / galvanised)',
      'Eye protection (under welding helmet)',
    ],
    emergency:
      'Fire — fight only if confined to the immediate area (CO₂); otherwise evacuate, alarm, 999. Burn — cool 20 min under running water; A&E if blistered or large area. Arc eye — pad both eyes, A&E. Gas leak — close cylinder valve at source if safe, ventilate, 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'mewp',
    title: 'MEWP (cherry picker / scissor lift) operations',
    trades: ['Telehandler / lift operations'],
    description: 'Mobile elevating work platform use — boom lift or scissor lift — for high-level access.',
    defaultHazards: ['Working at height', 'Plant / pedestrian interaction', 'Wet weather', 'Slips / trips'],
    scope:
      'Use of MEWPs (boom or scissor lift) for high-level access. Operator IPAF carded; pre-use inspection; ground conditions verified; rescue plan in place.',
    hazards: [
      { hazard: 'Overturn from soft ground / outrigger failure', risk: 'High', controls: 'Ground conditions checked before deployment — load-spreading mats on soft ground; outriggers fully extended on level surface; never deploy on a slope beyond machine spec; check for buried services, drains, manholes under outriggers.' },
      { hazard: 'Crushing between basket and structure / overhead hazard', risk: 'High', controls: 'Survey overhead before raising; banksman in radio contact; emergency stop within reach of operator and ground person; trapped-against-structure procedure briefed; pole-saw or other long tools managed separately.' },
      { hazard: 'Fall from basket', risk: 'High', controls: 'Twin-lanyard fall-restraint harness anchored to manufacturer\'s point inside the basket; never climb on the basket rails; door closed and latched before raising; operator IPAF carded.' },
      { hazard: 'Operator unable to descend (mechanical failure / unconscious)', risk: 'High', controls: 'Ground rescue person trained on the emergency lowering / control transfer procedure for the specific machine; rescue plan briefed every shift; phone on the operator and on the ground person.' },
      { hazard: 'Plant / pedestrian interaction', risk: 'High', controls: 'No-go zone around the machine; barriers + signs; banksman for any travelling movement; reverse alarm and beacon working.' },
      { hazard: 'Wind speed exceeding machine rating', risk: 'Med', controls: 'Stop and lower at sustained wind >12.5 m/s (28 mph) or per machine plate, whichever is lower; anemometer on machine; never use as a wind block.' },
    ],
    sequence: [
      'Operator confirms IPAF card valid and category correct (1b or 3b).',
      'Pre-use inspection — chassis, basket, controls, emergency lowering, alarms; recorded in log.',
      'Survey work area — overhead, ground conditions, services.',
      'Position machine; deploy outriggers (if scissor / boom requires); check level.',
      'Brief ground rescue person on emergency lowering for this machine.',
      'Operator harnessed and clipped on inside basket; door closed; raise basket.',
      'Carry out work; second person remains at ground level until basket lowered.',
      'Lower basket; isolate keys; lock the area at end of shift.',
    ],
    equipment: [
      'MEWP (scissor or boom lift) — current LOLER cert',
      'Twin-lanyard fall-restraint harness inspected within current 6 months',
      'Outrigger pads / load-spreaders',
      'Anemometer (boom lifts)',
      'Pedestrian barriers',
      'Two-way radios',
    ],
    ppe: [
      'Hard hat with chin-strap',
      'Safety boots',
      'Hi-vis Class 3',
      'Twin-lanyard harness',
      'Cut-resistant gloves',
      'Eye protection',
      'Hearing protection if combined with grinding etc.',
    ],
    emergency:
      'Operator trapped or unconscious — ground rescue person uses ground-level emergency lowering controls; if power fails, follow the manual descent procedure for the specific machine; 999 in parallel. Overturn — clear the area, 999. Mechanical failure mid-air — descend via emergency lowering; do not climb out of basket. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'kitchen_install',
    title: 'Kitchen install',
    trades: ['Carpentry & joinery'],
    description: 'Cabinet install, worktops (laminate / stone / solid wood), appliance fit and connection.',
    defaultHazards: ['Manual handling', 'Silica dust', 'Loud noise (>85 dB)', 'Hand-arm vibration (HAVS)', 'Slips / trips'],
    scope:
      'Install kitchen carcases, doors, worktops, appliances and accessories. Includes worktop joining (mason\'s mitre or butt-and-bolt), under-mount sink cut-outs, appliance offer-up. Final connection of services by Plumber / Electrician.',
    hazards: [
      { hazard: 'Manual handling of cabinets, worktops, appliances', risk: 'High', controls: 'Two-person lift for stone worktops and any large appliance; suction handles for stone; appliance trolley; clear route planned; lifting through windows assessed separately.' },
      { hazard: 'Silica dust from stone / quartz worktop cutting', risk: 'High', controls: 'Stone cut OFF-SITE wherever possible; if cut on site — wet saw with continuous water suppression, FFP3, outdoors only, dedicated cutting bay; never dry-cut quartz or stone indoors.' },
      { hazard: 'Damage to finished surfaces (floors, walls)', risk: 'Low', controls: 'Floor protection (Correx + tape) before any installation; corner protectors on doorways; vacuum offcuts immediately to avoid scratching floors.' },
      { hazard: 'Cuts from worktop saw and routing', risk: 'High', controls: 'Bench saw with riving knife; router with sharp bit and dust extraction; clamp the work, not the off-hand; eye protection mandatory.' },
      { hazard: 'Connecting appliances to live services', risk: 'High', controls: 'Final water and electric connection by Plumber and Electrician; gas hob / cooker by Gas Safe engineer only; never make permanent connections without the registered trade.' },
      { hazard: 'Working in tight space — unfamiliar postures, repetitive', risk: 'Med', controls: 'Knee pads; rotate tasks; breaks every 60 min; report any back / knee pain.' },
    ],
    sequence: [
      'Confirm 1st & 2nd fix electric, plumb, plaster, paint, floor complete; rooms ready.',
      'Lay floor protection.',
      'Set out cabinets from a level datum; mount wall cabs to brackets; set base cabs on legs; level and link.',
      'Hang doors and adjust; install drawers; soft-close adjusted.',
      'Template worktops (or trial-fit if stock); cut OFF-SITE preferably.',
      'Fit worktops; mason\'s-mitre joints with bolts and biscuit; sealant per spec.',
      'Sink and hob cut-outs; sealed; final connections by Plumber / Electrician / Gas Safe as appropriate.',
      'Offer up integrated appliances; secure per manufacturer.',
      'Plinths, cornices, end panels, splashbacks.',
      'Snag walk; clean; lift floor protection.',
    ],
    equipment: [
      'Cordless drills, drivers, jigsaw',
      'Worktop bench, router, jig set',
      'Sash cramps, vacuum suction handles',
      'Spirit level (long), laser level',
      'Wet saw outdoors only for any on-site stone cutting',
      'M-class extractor, HEPA vac',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis vest (where site rules require)',
      'Cut-resistant grip gloves',
      'Eye protection',
      'SNR ≥27 hearing protection during cutting / routing',
      'FFP3 mask if cutting / routing indoors',
      'Knee pads',
    ],
    emergency:
      'Cut from saw / router — pressure dressing, A&E if deep. Stone worktop dropped on foot — assess; A&E if any deformity. Crushing between worktop and wall during install — call site manager; 999 if conscious or breathing affected. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'slate_roofing',
    title: 'Slate roofing',
    trades: ['Roofing'],
    description: 'Natural or fibre-cement slate roof — battens, slates, ridges, hips, valleys.',
    defaultHazards: ['Working at height', 'Manual handling', 'Wet weather', 'Cold conditions', 'Slips / trips'],
    scope:
      'Lay natural or fibre-cement slates to spec — head-nailed or hook-fixed per pitch, with sarking felt, counter-battens, battens, ridges, hips, valleys and lead abutments. Includes leadwork to chimneys and abutments.',
    hazards: [
      { hazard: 'Falls from roof edge or eaves', risk: 'High', controls: 'Scaffold edge protection at gutter line tagged within current week before any roof work; harness for any task without edge protection; never work in wind >25 mph or storm conditions.' },
      { hazard: 'Slips on slate / wet membrane', risk: 'High', controls: 'Slate-laying boots with grip soles; never work on slates in rain or frost; clear moss / lichen at start; cleats and crawl boards on steep pitches.' },
      { hazard: 'Lifting bundles of slate to roof level', risk: 'High', controls: 'Telehandler or hoist for slate bundles — never thrown or hand-carried up scaffold in volume; banksman to control swing.' },
      { hazard: 'Sharp edges of slate cut by hammer / cutter', risk: 'Med', controls: 'Cut-resistant gloves rated for slate; eye protection; cut downwind on slate cutter; HEPA vac slate dust.' },
      { hazard: 'Falling slates / tools onto people below', risk: 'High', controls: 'Toe-boards, debris netting, no-go zone below; tools on lanyards; helmets mandatory for anyone in catchment area.' },
      { hazard: 'Slate / silica dust when cutting', risk: 'Med', controls: 'Cut on slate cutter (low-dust) by preference; FFP3 mask if angle-grinding; on-tool extraction; never dry-sweep dust.' },
      { hazard: 'Hot leadwork at abutments', risk: 'High', controls: 'Hot Works Permit; fire-watch with extinguisher; combustibles cleared 10 m; no hot works in last hour of shift.' },
    ],
    sequence: [
      'Confirm scaffold edge protection in place and tagged.',
      'Position slate bundles on roof — hoist or telehandler; spread load to avoid point-loading rafters.',
      'Fix sarking felt to falls; lap and nail per spec; tape penetrations.',
      'Counter-batten and batten to gauge for slate size and pitch.',
      'Set first slate course at eaves with shorter undereave course; head-nail per pitch.',
      'Continue laying upwards; bond slates with half-slate offset; check gauge per course.',
      'Cut slates to verge / valley with slate cutter; lead soakers at junctions.',
      'Lead flashings at chimneys, abutments and valleys — Hot Works Permit if welding.',
      'Ridge and hip slates — bedded or dry-fix per spec.',
      'Final inspection — no broken slates, no daylight, gutters clear.',
    ],
    equipment: [
      'Slate cutter (manual)',
      'Slate hook driver',
      'Telehandler / slate hoist',
      'Tin snips and lead dressing tools',
      'Scaffold (independently tagged)',
      'Hot works kit (where lead welding)',
    ],
    ppe: [
      'Hard hat with chin-strap',
      'Slate-laying boots (grip sole, ankle support)',
      'Hi-vis jacket',
      'Cut-resistant gloves',
      'Eye protection',
      'Harness if edge protection not in place',
      'FFP3 mask for slate cutting',
    ],
    emergency:
      'Fall from height — do not move casualty; 999. Wind exceeds 25 mph — STOP, secure loose slates and materials, descend. Hot works fire — fight if confined (CO₂); otherwise evacuate, 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'flat_roof_felt',
    title: 'Flat roof — torch-on / built-up felt',
    trades: ['Roofing'],
    description: 'Torch-on or pour-and-roll built-up felt flat roof system. Includes hot works, capping, abutments.',
    defaultHazards: ['Hot works', 'Working at height', 'Manual handling', 'COSHH (chemicals)', 'Wet weather'],
    scope:
      'Strip existing covering where applicable, prepare deck, apply vapour barrier, insulation, underlay and torch-on cap sheet. Includes upstands, abutments, drips, gutters and roof-light kerbs. Hot-works driven RAMS.',
    hazards: [
      { hazard: 'Fire from gas torch — felt or building structure', risk: 'High', controls: 'Hot Works Permit before each shift; CO₂ + foam extinguishers within reach; combustibles cleared 10 m; never torch within 300 mm of timber upstands; fire watch person + 60 min watch after last flame; no hot works in final 90 min of working day.' },
      { hazard: 'LPG / propane cylinder leak', risk: 'High', controls: 'Cylinders upright, secured, valved closed at end of shift; check hoses for damage daily; never store cylinders inside the building; gas-tightness check before every lighting.' },
      { hazard: 'Falls from roof edge', risk: 'High', controls: 'Edge protection (handrail or perimeter scaffold) before any work above 2 m; harness if not feasible; rooflights and openings barriered.' },
      { hazard: 'Hot bitumen burns', risk: 'Med', controls: 'Long-sleeved overalls; heavy gloves; never carry molten material whilst walking backwards.' },
      { hazard: 'Manual handling of felt rolls (often >25 kg)', risk: 'Med', controls: 'Two-person lift; mechanical hoist to roof level; store flat, not on edge.' },
      { hazard: 'Bitumen fume', risk: 'Med', controls: 'Work upwind; FFP3 minimum or A1P3 cartridge mask in confined areas; rotate operator.' },
      { hazard: 'Wet weather risking slips and torch failure', risk: 'Med', controls: 'No torching on wet membrane; pause for rain; cover laps if rain forecast.' },
    ],
    sequence: [
      'Hot Works Permit issued; brief team; fire-watch person nominated.',
      'Strip existing covering if refurb; bag for disposal.',
      'Prepare deck — clean, dry, primed per spec.',
      'Lay vapour control layer; lap and seal per detail.',
      'Lay insulation boards tight to perimeter; tape joints if spec requires.',
      'Lay underlay; bond per spec (torch / cold-bond / self-adhesive).',
      'Lay cap sheet; torch-bond laps; press out air pockets; dress upstands min 150 mm.',
      'Cut and form abutments, drips, kerbs; metal cappings to upstands.',
      'Cool before fire-watch ends; visual inspection of every joint.',
      'Pack down LPG; close permit at end of fire-watch.',
    ],
    equipment: [
      'Propane torch + LPG cylinder (with flashback arrestor)',
      'CO₂ + foam extinguishers',
      'Heat-resistant blankets',
      'Knife / shears for felt',
      'Hot bitumen kettle (if pour-and-roll spec)',
      'Mechanical hoist for materials',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis jacket',
      'Heavy heat-resistant gloves',
      'Eye protection',
      'FFP3 minimum / A1P3 in confined areas',
      'Long-sleeved cotton overalls',
    ],
    emergency:
      'Fire from torch — fight if confined (CO₂ then foam); otherwise evacuate, 999, alert site manager. Burn — cool 20 min, A&E if blistered or large. Fall from roof — 999, do not move casualty if back / neck pain. Gas leak — close cylinder, ventilate, evacuate. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'flat_roof_single_ply',
    title: 'Flat roof — single-ply membrane (EPDM / TPO)',
    trades: ['Roofing'],
    description: 'Single-ply rubber or thermoplastic membrane flat roof — adhesive or hot-air welded.',
    defaultHazards: ['Working at height', 'COSHH (chemicals)', 'Manual handling', 'Wet weather'],
    scope:
      'Install single-ply EPDM / TPO / PVC membrane to flat roof — mechanically fixed or fully bonded with adhesive. Hot-air weld laps for thermoplastic systems. Cold-bond for EPDM.',
    hazards: [
      { hazard: 'Falls from roof edge / through openings', risk: 'High', controls: 'Edge protection in place (perimeter scaffold or freestanding handrail) before any work; rooflights and openings barriered; harness for any unguarded task.' },
      { hazard: 'Solvent / contact adhesive fume', risk: 'Med', controls: 'Work upwind; A1P3 cartridge mask for prolonged adhesive use; ventilate confined areas; SDS on file; barrier cream on hands; nitrile gloves.' },
      { hazard: 'Hot-air weld gun — burns and fire', risk: 'Med', controls: 'Never set down hot end on the membrane; rest on metal stand; combustibles cleared from immediate work area; let cool fully before packing away.' },
      { hazard: 'Manual handling of large membrane rolls', risk: 'Med', controls: 'Two-person; mechanical hoist to roof; store flat on supports.' },
      { hazard: 'Wet weather — adhesives / welds fail', risk: 'Med', controls: 'No installation in rain; surfaces dry before bonding / welding; check forecast at start of shift.' },
      { hazard: 'Slips on smooth membrane', risk: 'Med', controls: 'Non-slip footwear; tidy as you work; mark walkways with tape during install.' },
    ],
    sequence: [
      'Confirm edge protection and openings barriered.',
      'Strip / prepare deck; clean and dry.',
      'Lay vapour control layer where spec requires.',
      'Lay insulation; tight to perimeter; tape joints if spec.',
      'Lay separation fleece if required.',
      'Roll out membrane; allow to relax; trim to shape.',
      'Bond per spec — adhesive (cold) or mechanical fixings.',
      'Lap joints — hot-air welded for thermoplastic, taped for EPDM.',
      'Form upstands and details with primer + flashing strip.',
      'Test lap integrity (probe with flat blade); inspect.',
    ],
    equipment: [
      'Hot-air welder (TPO / PVC)',
      'Membrane rollers and sealing rollers',
      'Notched applicators for adhesive',
      'Primer and lap-tape',
      'Mechanical hoist',
      'Edge protection / harness',
    ],
    ppe: [
      'Hard hat',
      'Safety boots (non-slip)',
      'Hi-vis',
      'Nitrile gloves under cut-resistant',
      'Eye protection',
      'A1P3 cartridge mask for solvent adhesive',
    ],
    emergency:
      'Fall from edge — 999. Solvent fume overcoming operator — fresh air, 999 if non-responsive. Burn from hot-air welder — cool 20 min, A&E if blistered. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'grp_roof',
    title: 'GRP / fibreglass flat roof',
    trades: ['Roofing'],
    description: 'Glass-reinforced plastic flat roof — laminated in-situ from resin and fibreglass matting.',
    defaultHazards: ['COSHH (chemicals)', 'Working at height', 'Hot works', 'Wet weather'],
    scope:
      'Strip existing covering; lay OSB / ply deck; install GRP edge trims; laminate fibreglass matting and resin; topcoat with pigmented gel-coat. All works in non-rainy and >5°C conditions.',
    hazards: [
      { hazard: 'Resin fume — styrene exposure', risk: 'High', controls: 'A1P3 cartridge mask MANDATORY; ventilate; never indoors without LEV; max work duration per shift per HSE WEL; SDS on file; barrier cream and nitrile gloves.' },
      { hazard: 'Resin / catalyst skin irritation and chemical burns', risk: 'High', controls: 'Long-sleeved Type 5/6 overalls; nitrile gloves; eye protection during mixing and application; immediate wash if splashed.' },
      { hazard: 'Fire from catalyst (MEKP) — exothermic if mismixed', risk: 'High', controls: 'Mix per manufacturer ratio strictly; never mix more than will be used in pot life; never combine resin and catalyst in metal containers; sand bucket and water on hand; CO₂ extinguisher.' },
      { hazard: 'Falls from roof edge', risk: 'High', controls: 'Edge protection in place; harness for unguarded tasks; never work alone on a GRP roof — pair work.' },
      { hazard: 'Wet / cold weather causing cure failure', risk: 'Med', controls: 'No application below 5°C or above 28°C; no application if rain forecast in next 6 hrs; humidity check; only laminate when conditions in spec.' },
    ],
    sequence: [
      'Confirm weather window; check temperature and humidity.',
      'Strip existing; clean and dry deck.',
      'Lay OSB3 deck where required; screw to falls.',
      'Install GRP edge trims and upstands per detail.',
      'Cut fibreglass matting to size; pre-position.',
      'Mix resin and catalyst per ratio; apply with brush / roller; consolidate matting with stippler.',
      'Allow first lay-up to cure to "B-stage".',
      'Apply topcoat with mixed catalyst per spec; achieve uniform thickness.',
      'Pot life and coverage strictly per maker.',
      'Final inspection — no dry patches, no air pockets, no curing defects.',
    ],
    equipment: [
      'Resin / catalyst kit',
      'Fibreglass matting (chopped strand)',
      'Brushes, rollers, stipplers',
      'Mixing buckets (plastic)',
      'GRP edge trims and details',
      'A1P3 RPE',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis',
      'Type 5/6 chemical overall',
      'Nitrile gloves',
      'A1P3 cartridge mask (organic vapour + particulate)',
      'Eye protection (chemical splash rating)',
    ],
    emergency:
      'Resin / catalyst splash to eye — flush 15 min minimum, A&E. Skin contact — wash thoroughly, A&E if irritation persists. Fire from mismixed catalyst — sand or CO₂; never water onto burning resin; evacuate, 999. Solvent fume overcoming operator — fresh air, 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'leadwork',
    title: 'Leadwork — flashings, soakers, valleys',
    trades: ['Roofing'],
    description: 'Lead flashings, soakers, valleys, hips, parapet cappings — bossed, welded, dressed.',
    defaultHazards: ['Hot works', 'Working at height', 'Manual handling', 'COSHH (chemicals)', 'Loud noise (>85 dB)'],
    scope:
      'Form and install lead flashings, soakers, valleys, hips and parapet cappings to roofs. Hot lead welding for joints. Bossing and dressing to detail.',
    hazards: [
      { hazard: 'Hot works — gas torch ignition of structure / felt', risk: 'High', controls: 'Hot Works Permit; combustibles cleared 10 m; CO₂ and foam extinguishers; heat-resistant blanket behind; fire watch with permit hold time.' },
      { hazard: 'Lead fume / particulate — Pb exposure', risk: 'High', controls: 'A1P3 cartridge mask MANDATORY when welding lead; FFP3 minimum for hot dressing; never grind hardened lead without LEV; wash hands and forearms before food / smoking; blood-lead surveillance for regular leadworkers.' },
      { hazard: 'Manual handling of lead rolls (heavy)', risk: 'High', controls: 'Lead supplied in shorter lengths; mechanical hoist to roof; never carry full rolls up scaffold by hand; sized at the bench, not on the roof.' },
      { hazard: 'Falls during dressing on pitched roof', risk: 'High', controls: 'Edge protection in place; harness if unguarded; cleats / crawl boards on steep pitch; never work in rain.' },
      { hazard: 'Hand-arm vibration from dressing tools', risk: 'Med', controls: 'Trigger-time managed; rotate; HAVS surveillance.' },
      { hazard: 'Loud noise from tin-snips and dressing', risk: 'Med', controls: 'SNR ≥27 hearing protection; cordon off if noisy work near public.' },
    ],
    sequence: [
      'Issue Hot Works Permit; brief team; fire-watch.',
      'Set out lead courses to spec (Code 4 / 5 / 6 / 7 / 8 by application).',
      'Cut lead at bench from sized rolls.',
      'Bossing / dressing of soakers, hips, valleys to detail.',
      'Tack the lead in position; ensure expansion joints in long runs.',
      'Lead-weld seams as required; cool before fire-watch end.',
      'Patination oil applied to seal cut edges (reduces white run-off staining).',
      'Final inspection of flashings — secure, dressed tight, no gaps.',
      'Close Hot Works Permit after fire-watch.',
    ],
    equipment: [
      'Oxy-propane torch (or hot-air welder for joining)',
      'Bossing sticks, mallets, dressers',
      'Tin snips, lead knife',
      'CO₂ + foam extinguishers',
      'Heat-resistant blanket',
      'Patination oil',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis',
      'Welding leathers / FR overall when welding',
      'Welding helmet (if arc weld) or shaded eye protection',
      'A1P3 cartridge mask when welding / heating lead',
      'FFP3 minimum during dressing',
      'Hearing protection',
    ],
    emergency:
      'Lead fume exposure — fresh air, blood-lead test if symptoms persist. Hot works fire — fight if confined (CO₂); otherwise evacuate, 999. Burn — cool 20 min, A&E if blistered. Fall from height — 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'chimney_works',
    title: 'Chimney repair / rebuild',
    trades: ['Roofing', 'Bricklaying'],
    description: 'Chimney rebuild, repointing, leadwork to abutment, cowl install, flue lining.',
    defaultHazards: ['Working at height', 'Manual handling', 'Hot works', 'Silica dust', 'Wet weather'],
    scope:
      'Strip down chimney to a sound course or fully demolish, rebuild in matching brick / stone, install new flaunching, pot, lead apron and abutment flashings, repoint or relime as required.',
    hazards: [
      { hazard: 'Falls from chimney scaffold', risk: 'High', controls: 'Independent chimney scaffold tagged; double guard-rail; toe-boards; harness for any unguarded task; never lean against the chimney whilst working.' },
      { hazard: 'Falling debris during demolition', risk: 'High', controls: 'Debris chute or controlled bucket-down only; never throw; no-go zone below; helmets mandatory below.' },
      { hazard: 'Manual handling of bricks, flue liners, pot', risk: 'High', controls: 'Two-person carry up scaffold; mechanical hoist (gin wheel / telehandler); pot lifted and seated by two people.' },
      { hazard: 'Mortar / silica dust during repointing / cutting', risk: 'High', controls: 'Wet cutting; FFP3 mask; HEPA vac for clean-down; never dry-sweep.' },
      { hazard: 'Hot works for lead apron at abutment', risk: 'Med', controls: 'Hot Works Permit; fire-watch; clear combustibles; CO₂ extinguisher; cool before pack-up.' },
      { hazard: 'Wind exposure on tall chimneys', risk: 'High', controls: 'STOP at sustained wind >25 mph or gusts >35; secure loose materials; descend.' },
    ],
    sequence: [
      'Erect independent chimney scaffold; tag.',
      'Survey existing — identify where to strip back to.',
      'Strip down course-by-course; bag debris and lower controlled.',
      'Set out new courses on existing or new lead-tray DPC.',
      'Build up brickwork to height; corbel at oversail to spec.',
      'Install flue liner sections from below or above per system.',
      'Set chimney pot in mortar flaunching; pot bedded and pointed.',
      'Lead apron, soakers and back-gutter to abutment.',
      'Repoint joints; rake and brush off.',
      'Final inspection; remove scaffold once mortar set.',
    ],
    equipment: [
      'Independent chimney scaffold',
      'Forced-action mortar mixer',
      'Brick saw (wet-cut)',
      'Trowels, levels, plumb-bob',
      'Hoist / gin wheel for materials',
      'Hot works kit (where lead welding)',
    ],
    ppe: [
      'Hard hat with chin-strap',
      'Safety boots',
      'Hi-vis',
      'Cut-resistant gloves; nitrile under for cement',
      'Eye protection',
      'FFP3 mask during cutting',
      'Harness for unguarded tasks',
    ],
    emergency:
      'Fall from chimney — 999. High wind — STOP, descend, secure materials. Cement in eye — flush 15 min, A&E. Falling debris hits person below — 999, do not move casualty if back / neck pain. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'gutters_fascias',
    title: 'Gutters, fascias & soffits',
    trades: ['Roofing', 'Carpentry & joinery'],
    description: 'Strip, replace and install uPVC or timber gutters, fascias, soffits and downpipes.',
    defaultHazards: ['Working at height', 'Manual handling', 'Slips / trips', 'Wet weather'],
    scope:
      'Remove existing gutters / fascia / soffit; install replacement timber or uPVC system; falls verified to outlet; downpipes connected to drainage. Includes cap-over and bird-mesh where in scope.',
    hazards: [
      { hazard: 'Falls from edge or off ladder', risk: 'High', controls: 'Tower or scaffold preferred; ladders only for short tasks <30 min and tied / footed; harness for any unguarded task at >2m.' },
      { hazard: 'Sharp metal / asbestos cement on existing soffits', risk: 'Med', controls: 'Pre-1999 buildings — assume soffit may be AC; test before disturbance; do not break — un-screw and lower whole; bag and dispose as ACM if positive.' },
      { hazard: 'Manual handling of long fascia lengths and gutter runs', risk: 'Med', controls: 'Two-person carry; lift to platform with hoist; pre-cut at bench.' },
      { hazard: 'Sharp gutter / fascia cut edges', risk: 'Med', controls: 'Cut-resistant gloves; deburr after cutting; eye protection during cutting.' },
      { hazard: 'Wet weather slipping on roof / scaffold', risk: 'Med', controls: 'STOP if scaffold slippery; wait until dry; non-slip footwear.' },
    ],
    sequence: [
      'Survey existing — confirm condition, AC test if pre-1999.',
      'Set up scaffold or tower; tag.',
      'Strip existing gutter and fascia / soffit; lower controlled.',
      'Inspect rafter ends; replace any rotten timber per spec.',
      'Install fascia board or uPVC fascia trim; level and fixed per maker.',
      'Install soffit board / vented soffit with continuous airflow.',
      'Install gutter brackets to falls (1:600 typical).',
      'Run gutter lengths; clip into brackets; stop-ends and outlets.',
      'Install downpipes to drainage; bracketed every 1.8 m.',
      'Test with hose; check no leaks.',
    ],
    equipment: [
      'Mobile tower / scaffold',
      'Hand-saws, mitre saw, snips',
      'Cordless drill / driver',
      'Spirit level (long), laser level',
      'Pop-rivet gun if metal gutters',
      'Hoist / line for long lengths',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis',
      'Cut-resistant gloves',
      'Eye protection',
      'FFP3 mask if cutting / sanding (and esp. if AC suspected)',
    ],
    emergency:
      'Fall from height — 999. Suspected AC strip — STOP, isolate, bag, contact site manager / asbestos contractor. Sharp cut — pressure dressing, A&E if deep. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'rooflight_install',
    title: 'Rooflight / Velux install',
    trades: ['Roofing', 'Carpentry & joinery'],
    description: 'Install Velux or similar rooflight into pitched tile / slate roof.',
    defaultHazards: ['Working at height', 'Manual handling', 'Wet weather', 'Slips / trips'],
    scope:
      'Cut opening through rafters and roof covering, trim opening structurally, install rooflight unit to manufacturer\'s detail with weather-tight flashing, integrate with existing tile / slate roof.',
    hazards: [
      { hazard: 'Falls through the opening once cut', risk: 'High', controls: 'Edge protection / handrail across opening BEFORE cutting; lower scaffold lift below the opening to catch any drop; harness if unguarded; openings boarded at end of every shift.' },
      { hazard: 'Falls from roof edge', risk: 'High', controls: 'Scaffold edge protection at gutter line tagged; harness if unguarded; never work in wind >25 mph.' },
      { hazard: 'Manual handling of rooflight unit (often >25 kg, awkward)', risk: 'High', controls: 'Two-person lift up scaffold or hoist to roof; never single-lift; opening prepared and ready to receive.' },
      { hazard: 'Cuts from saw and tile / slate edges', risk: 'Med', controls: 'Cut-resistant gloves; eye protection; sharp blades.' },
      { hazard: 'Weather ingress whilst opening is exposed', risk: 'Med', controls: 'Tarp ready before cut; never leave exposed at end of shift; cut-and-fit in single weather window.' },
      { hazard: 'Cutting into existing rafter / structural failure', risk: 'High', controls: 'Trimmer joists installed BEFORE cutting through; structural advice if cutting >1 rafter; never cut without trimmer.' },
    ],
    sequence: [
      'Confirm scaffold edge protection and access.',
      'Set out opening from inside; mark on rafters; verify centre lines.',
      'Install trimmer joists to support cut rafter — TO MANUFACTURER\'S DETAIL.',
      'Erect handrail / boarding across the working opening.',
      'Cut roofing felt and battens; remove tiles around the opening.',
      'Cut rafter to size; verify trimmers carrying load.',
      'Lift rooflight unit to roof — two-person or hoist.',
      'Set unit in opening; fix to manufacturer fixings; weather-tight to tile / slate using flashing kit.',
      'Re-tile / re-slate around unit; finish ridge tile / lead apron at head.',
      'Internal lining, plasterboard reveal, decoration (separate trades).',
    ],
    equipment: [
      'Reciprocating saw / circular saw',
      'Mobile hoist for unit',
      'Spirit level, square, tape',
      'Cordless drill, impact driver',
      'Slate / tile cutter',
      'Tarp for weather cover',
    ],
    ppe: [
      'Hard hat with chin-strap',
      'Safety boots',
      'Hi-vis',
      'Cut-resistant gloves',
      'Eye protection',
      'Harness if unguarded',
      'FFP3 mask during cutting',
    ],
    emergency:
      'Fall through opening — 999, do not move. Wet weather mid-install — emergency tarp; postpone if windy. Cut to hand from saw — pressure dressing, A&E if deep. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'dormer_construction',
    title: 'Dormer construction',
    trades: ['Carpentry & joinery', 'Roofing'],
    description: 'Full dormer build — structural carpentry, roof, cladding, weather-tight finish.',
    defaultHazards: ['Working at height', 'Manual handling', 'Hot works', 'Wet weather', 'Slips / trips'],
    scope:
      'Construct flat-roof or pitched dormer to existing roof — open up roof, install structural trimming, build dormer carcass, weatherproof, clad, install windows, internal lining ready for plaster.',
    hazards: [
      { hazard: 'Structural failure of opened-up roof', risk: 'High', controls: 'Structural engineer\'s drawings followed exactly; trimmers and props installed BEFORE removing existing rafters; sequence agreed with engineer.' },
      { hazard: 'Falls from working platform / opened roof', risk: 'High', controls: 'Full-perimeter scaffold edge protection; full handrail on the dormer opening edge; harness for any unguarded task.' },
      { hazard: 'Weather ingress to inside of property below', risk: 'High', controls: 'Roof cover system (e.g. shrink-wrap or scaffold tarpaulin roof) for full duration of build; cover everything below with sheets; daily check before leaving site.' },
      { hazard: 'Manual handling of large structural members and panels', risk: 'High', controls: 'Mechanical hoist for trusses, cassette panels, large sheets; two-person for rafters >2.4 m; clear route planned.' },
      { hazard: 'Hot works for lead apron / leadwork', risk: 'Med', controls: 'Hot Works Permit; fire watch; combustibles cleared; cool before pack-up.' },
      { hazard: 'Working in tight spaces against existing structure', risk: 'Med', controls: 'Plan sequence to keep clear access; never trap yourself behind work; phone always on operator.' },
    ],
    sequence: [
      'Pre-start — engineer\'s drawings, weather forecast, scaffold tagged, weather covers staged.',
      'Erect scaffold edge protection and overhead weather cover.',
      'Cover the inside of the property below with dust sheets.',
      'Install temporary props / strongbacks to existing roof structure.',
      'Strip tiles, felt, battens, sarking from area to be opened.',
      'Cut existing rafters; install trimmer rafters and dormer head plate per drawings.',
      'Erect dormer cheek studs and roof structure.',
      'Sheath; weather-tight breathable membrane; counter-batten and tile / weatherproof finish.',
      'Install dormer window unit; weather flashings; lead apron and soakers (Hot Works Permit).',
      'Strip overhead covers; final dressing.',
    ],
    equipment: [
      'Telehandler / hoist for materials',
      'Reciprocating saw, circular saw, mitre saw',
      'Cordless drills',
      'Sash cramps, levels, squares',
      'Weather covers (shrink-wrap or scaffold tarp)',
      'Hot works kit (if lead)',
    ],
    ppe: [
      'Hard hat with chin-strap',
      'Safety boots',
      'Hi-vis Class 3',
      'Harness for unguarded tasks',
      'Cut-resistant gloves',
      'Eye protection',
      'FFP3 mask during cutting',
    ],
    emergency:
      'Structural movement / collapse — clear the area, 999, contact engineer. Fall from height — 999, do not move. Heavy rain mid-build — emergency tarp; never leave open at end of shift. Hot works fire — fight if confined, otherwise 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'roof_repair',
    title: 'Roof repair & maintenance',
    trades: ['Roofing'],
    description: 'Patch leaks, replace broken tiles / slates, repoint ridges, clear gutters, re-bed flashings.',
    defaultHazards: ['Working at height', 'Slips / trips', 'Wet weather', 'Manual handling'],
    scope:
      'Diagnose and repair localised roof defects — broken tiles / slates, slipped tiles, failed pointing, leaks at flashings, blocked gutters. Short-duration tasks on residential pitched roofs.',
    hazards: [
      { hazard: 'Falls from roof edge', risk: 'High', controls: 'Scaffold or tower for any work >2m; never freehand on a sloping roof without edge protection or harness; ladders only for inspection / short access — tied and footed.' },
      { hazard: 'Slips on wet, mossy or icy roof', risk: 'High', controls: 'Roof condition assessed before stepping; clear moss / lichen first if needed; never work in rain or frost; non-slip footwear.' },
      { hazard: 'Falling tiles and tools onto people / property below', risk: 'High', controls: 'No-go zone below; tools on lanyards; tiles lowered controlled never thrown; warn property occupiers.' },
      { hazard: 'Manual handling of replacement tiles and pointing materials', risk: 'Low', controls: 'Carried in small batches; bag for offcuts; never single-load over shoulders.' },
      { hazard: 'Asbestos cement on older roofs / soffits', risk: 'High', controls: 'Pre-1999 buildings — assume potential AC; do not break or grind; minor lifting only; sample test before any larger works; bag any fragments and dispose as ACM if positive.' },
    ],
    sequence: [
      'Survey from ground / loft; identify the defect.',
      'Set up access — tower / scaffold / ladder per the task.',
      'Brief any property occupiers; cordon off below.',
      'Replace broken tiles / slates with matching units; clip / nail per pitch.',
      'Repoint ridges with mortar to spec; rake and brush.',
      'Re-bed flashings; lead-clip per detail.',
      'Clear gutters and outlets; flush with hose to confirm.',
      'Final inspection from below; document with photos.',
    ],
    equipment: [
      'Tile / slate ripper',
      'Tile clips / nails',
      'Pointing trowel and mortar',
      'Tower / ladders',
      'Bucket / lanyard for tools',
    ],
    ppe: [
      'Hard hat with chin-strap',
      'Safety boots (non-slip sole)',
      'Hi-vis',
      'Cut-resistant gloves',
      'Eye protection',
      'Harness for any unguarded work',
      'FFP3 mask if disturbing dust',
    ],
    emergency:
      'Fall from roof — 999. Suspected AC disturbance — STOP, isolate, contact site manager. Slips / sprains — assess; A&E if needed. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'cut_roof',
    title: 'Cut roof — traditional rafter & purlin',
    trades: ['Carpentry & joinery', 'Roofing'],
    description: 'Traditional cut roof — wall plates, ceiling joists, rafters, purlins, ridge, hips, valleys.',
    defaultHazards: ['Working at height', 'Manual handling', 'Loud noise (>85 dB)', 'Hand-arm vibration (HAVS)'],
    scope:
      'Construct cut roof to architect / engineer\'s drawings — wall plates strapped, ceiling joists, principal rafters, purlins, common rafters, ridge board, hips and valleys. Excludes covering (separate RAMS).',
    hazards: [
      { hazard: 'Falls from open roof structure', risk: 'High', controls: 'Independent perimeter scaffold with edge protection at gutter line; harness for all work above ceiling joist level; never walk on joists alone — boarded crawl path.' },
      { hazard: 'Manual handling of timber sections (long, heavy)', risk: 'High', controls: 'Two-person carry for any rafter / purlin >2.4 m; mechanical hoist (telehandler) for ridge boards and large purlins; planned route.' },
      { hazard: 'Cuts from saws (circular, mitre, hand)', risk: 'High', controls: 'Guards intact; sharp blades; never freehand cut a rafter — clamp to bench / cutting station; eye protection mandatory.' },
      { hazard: 'Falling tools / timber onto people below', risk: 'High', controls: 'No-go zone below; tools on lanyards; off-cuts cleared continuously; helmets mandatory below.' },
      { hazard: 'Hand-arm vibration from prolonged sawing', risk: 'Med', controls: 'Low-vibration tools; rotate operators; HAVS surveillance.' },
      { hazard: 'Wind loading on partially-framed roof', risk: 'High', controls: 'STOP at sustained wind >15 mph during structural assembly; brace every section as it goes up; never leave roof unbraced overnight.' },
    ],
    sequence: [
      'Pre-start — drawings reviewed, scaffold tagged, telehandler banksman briefed.',
      'Wall plates set out, levelled, strapped per Building Regs Part A.',
      'Ceiling joists installed and strapped at gable ends.',
      'Mark out rafter cuts on a template; cut all of one length together.',
      'Erect ridge board on temporary props.',
      'Install pairs of common rafters from a temporary deck or boarded path.',
      'Install purlins — supported on intermediate walls or strut bracing per drawings.',
      'Install principal / hip / valley rafters as required.',
      'Brace fully — diagonal bracing per Part A; never leave unbraced.',
      'Final inspection by engineer / building control; sign off.',
    ],
    equipment: [
      'Telehandler with banksman',
      'Circular saw, mitre saw, reciprocating saw',
      'Cordless drill / impact',
      'Sequential-trip nailer',
      'Spirit levels, tape, square',
      'Temporary props',
    ],
    ppe: [
      'Hard hat with chin-strap',
      'Safety boots',
      'Hi-vis Class 3',
      'Harness',
      'Cut-resistant gloves',
      'Eye protection',
      'SNR ≥27 hearing protection',
    ],
    emergency:
      'Fall from height — 999, do not move. Wind exceeds 15 mph during erection — STOP, brace, descend. Cut to hand from saw — pressure, A&E if deep. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'trussed_roof',
    title: 'Trussed rafter roof — erection',
    trades: ['Carpentry & joinery', 'Roofing'],
    description: 'Pre-fabricated truss erection — bracing, binders, hangers, gable end framing.',
    defaultHazards: ['Working at height', 'Manual handling', 'Plant / pedestrian interaction', 'Wet weather'],
    scope:
      'Erect pre-fabricated trusses to wall plates per truss layout drawing. Install diagonal, longitudinal and chevron bracing per BS 5268 / TRADA. Gable end framing.',
    hazards: [
      { hazard: 'Truss collapse before bracing complete', risk: 'High', controls: 'Bracing installed AS YOU GO — not after; never erect more than 4 trusses without diagonal bracing; first truss strongly braced as datum.' },
      { hazard: 'Falls from open roof structure', risk: 'High', controls: 'Edge protection in place; harness anchored to braced section; boarded path for movement.' },
      { hazard: 'Lifting trusses (long, awkward, often >50 kg)', risk: 'High', controls: 'Telehandler with truss hook OR mobile crane; banksman; never hand-erect anything beyond 6 m span; lift from designated points only.' },
      { hazard: 'Plant / pedestrian interaction during lifting', risk: 'High', controls: 'No-go zone under lift radius; banksman; reverse alarms.' },
      { hazard: 'Wind during erection', risk: 'High', controls: 'STOP at sustained wind >15 mph; trusses act as a sail until braced; secure any partly-erected work.' },
      { hazard: 'Truss damaged by mishandling on site', risk: 'Med', controls: 'Trusses stored vertically on bearers, not flat (deformation); two-point lift; never drag along ground.' },
    ],
    sequence: [
      'Pre-start — truss layout drawing, bracing schedule, banksman briefed.',
      'Wall plates set out and strapped.',
      'Erect first truss; brace strongly to gable / datum.',
      'Erect successive trusses; diagonal bracing installed every 4 trusses MAX.',
      'Install longitudinal bracing per layout drawing.',
      'Install chevron bracing per BS 5268 / TRADA.',
      'Gable end framing — studs and noggings.',
      'Install ceiling binders / restraint straps per Part A.',
      'Inspect — fully braced before any covering trade goes up.',
    ],
    equipment: [
      'Telehandler with truss hook (or mobile crane)',
      'Truss bracing timbers (35 × 97 typical)',
      'Sequential-trip nailer',
      'Spirit levels and plumb bobs',
      'Cordless drill',
    ],
    ppe: [
      'Hard hat with chin-strap',
      'Safety boots',
      'Hi-vis Class 3',
      'Harness',
      'Cut-resistant gloves',
      'Eye protection',
    ],
    emergency:
      'Truss collapse — clear area, 999, contact engineer. Fall from height — 999. Wind exceeds 15 mph — STOP, brace, descend. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'joist_install',
    title: 'Floor joist install (i-joist / metalweb / solid)',
    trades: ['Carpentry & joinery'],
    description: 'Install floor joists — engineered i-joist, metal-web (posi-joist) or solid timber.',
    defaultHazards: ['Working at height', 'Manual handling', 'Slips / trips'],
    scope:
      'Install floor joists per structural engineer\'s layout — i-joist, metal-web posi-joist or solid. Includes hangers, restraint straps, noggings, web stiffeners, blocking and trimmers around openings.',
    hazards: [
      { hazard: 'Falls through joist gaps (worker walking on tops)', risk: 'High', controls: 'Boarded crawl path / staging across the joists; never walk on joist tops without a boarded path; harness for any unguarded edge work.' },
      { hazard: 'Manual handling of long joists (often 4–6 m)', risk: 'High', controls: 'Two-person carry; mechanical hoist to upper floors; pre-cut to length at the bench.' },
      { hazard: 'I-joist web damage from incorrect cutting / penetrations', risk: 'Med', controls: 'NEVER cut into the flange or top chord of an i-joist; service penetrations only at pre-engineered locations or per maker\'s rules; metal-web — services through web only between zones marked.' },
      { hazard: 'Hangers fixed with wrong nails / screws', risk: 'High', controls: 'Hanger-specific nails / screws (not standard nails) — full pattern to maker\'s spec; check on every joist before deck.' },
      { hazard: 'Cuts from saws and hand tools', risk: 'Med', controls: 'Sharp blades; clamp work; eye protection; first aid kit accessible.' },
    ],
    sequence: [
      'Pre-start — engineer\'s drawings; layout marked on wall plate / spreader.',
      'Install hangers / wall plates per drawings.',
      'Lay first joist; align; check for level.',
      'Continue at design centres; install hangers fully nailed.',
      'Trimmers around openings (hatches, stair voids) per drawings.',
      'Install web stiffeners on i-joists at supports.',
      'Noggings / blocking at mid-span per spec.',
      'Restraint straps to gable wall per Part A.',
      'Inspect before deck goes down — every hanger fully fixed.',
    ],
    equipment: [
      'Mechanical hoist for joists',
      'Sequential-trip nailer (palm nailer for hangers)',
      'Hanger-specific nails (per manufacturer)',
      'Circular saw, jigsaw',
      'Cordless drill',
      'Spirit level',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis',
      'Harness if unguarded edge',
      'Cut-resistant gloves',
      'Eye protection',
      'Hearing protection during cutting',
    ],
    emergency:
      'Fall through joists — 999, do not move if back / neck pain. Cut to hand — pressure, A&E. Falling joist — clear area; 999 if struck. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'stairs_install',
    title: 'Staircase install — timber',
    trades: ['Carpentry & joinery'],
    description: 'Install pre-fabricated timber staircase including balustrade, handrails and infill.',
    defaultHazards: ['Manual handling', 'Working at height', 'Slips / trips'],
    scope:
      'Install pre-fabricated timber staircase — set out, lift in, fix to upper trimmer, fit treads / risers, balustrade and handrails to Part K dimensions. Includes glass / metal balustrade where specified.',
    hazards: [
      { hazard: 'Manual handling of pre-fabricated staircase (heavy, awkward)', risk: 'High', controls: 'Two-person minimum, three for wider stairs; route checked first; mechanical hoist for upper-floor stair to first floor; never single-lift.' },
      { hazard: 'Falls into stairwell during install / before balustrade', risk: 'High', controls: 'Stairwell guarded with handrail BEFORE any work on stair install; harness when working at the upper trimmer; never leave open stairwell at end of shift.' },
      { hazard: 'Cuts from saw and hand tools', risk: 'Med', controls: 'Sharp blades; clamp work; eye protection.' },
      { hazard: 'Damage to finished surfaces (walls, floors, soft strings)', risk: 'Low', controls: 'Floor protection; corner protectors; soft-string covers retained until final fit.' },
      { hazard: 'Building Regs non-compliance (rise / going / handrail / spindle)', risk: 'Med', controls: 'Set out checked against Part K — rise ≤220, going ≥220, pitch ≤42°; spindles ≤100mm gap; handrail 900–1000 mm; verified before sign-off.' },
    ],
    sequence: [
      'Confirm structural opening per drawings; trimmer fully fixed.',
      'Lay floor protection; cover finished walls with corner-protectors.',
      'Lift staircase to position; offer up.',
      'Fix top fixing to trimmer; pack and shim plumb.',
      'Fix bottom string to floor / kicker.',
      'Install treads, risers, balusters / spindles, newel posts and handrail.',
      'Install glass infill (if spec) — never carry single-handed.',
      'Snag check against Part K dimensions.',
      'Stairwell guard left in place until decoration / 2nd fix complete.',
    ],
    equipment: [
      'Mechanical hoist (for upper floors)',
      'Cordless drill / impact',
      'Spirit level (long), plumb bob',
      'Sash cramps',
      'Hand saws, mitre saw',
    ],
    ppe: [
      'Hard hat (where site rules require)',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves',
      'Eye protection',
      'Harness if unguarded stairwell',
    ],
    emergency:
      'Fall into stairwell — 999, do not move. Stair drops on operative — clear, 999, do not lift the stair off if conscious. Cut to hand — pressure dressing, A&E. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'timber_frame_erect',
    title: 'Timber frame erection',
    trades: ['Carpentry & joinery'],
    description: 'Erect pre-fabricated timber-frame panels — sole plates, walls, floor cassettes.',
    defaultHazards: ['Working at height', 'Manual handling', 'Plant / pedestrian interaction', 'Wet weather'],
    scope:
      'Erect pre-fabricated timber-frame house panels — sole plates, ground floor wall panels, intermediate floor cassettes, upper floor walls, gable panels per the frame engineer\'s drawings.',
    hazards: [
      { hazard: 'Panel collapse before propping', risk: 'High', controls: 'Temporary props installed AS panels go up; never erect a panel without immediately propping; panels braced before next panel offered up.' },
      { hazard: 'Falls from upper floor edge / openings', risk: 'High', controls: 'Edge protection / handrail across every external edge before panels go on upper floor; openings (windows / doors) barriered until permanent fit.' },
      { hazard: 'Lifting heavy wall panels and floor cassettes', risk: 'High', controls: 'Mobile crane or telehandler with banksman; lifting points marked; never hand-lift a wall panel >1.5 m wide; certified slings.' },
      { hazard: 'Wind acting on partially-erected panels', risk: 'High', controls: 'STOP at sustained wind >15 mph during erection; once propped and braced, monitor; secure or take down any unstable panels at end of shift.' },
      { hazard: 'Weather ingress to a partly-erected frame', risk: 'Med', controls: 'Weather forecast checked daily; tarpaulins ready; never leave open at end of shift if rain forecast; sole plates DPC sealed to prevent rising damp.' },
      { hazard: 'Manual handling of straps, fixings, props', risk: 'Med', controls: 'Stocked at point of use; pre-cut to length where possible.' },
    ],
    sequence: [
      'Pre-start — engineer\'s drawings, lift plan, banksman, weather forecast.',
      'Sole plates set out level; DPC; bolted to slab per spec.',
      'First wall panel offered up; bolted / nailed to sole plate; PROP IMMEDIATELY.',
      'Successive panels — bolt to adjoining; prop; brace within 4 panels.',
      'Floor cassettes laid; bolted; panel-to-cassette per detail.',
      'Upper floor wall panels; same propping discipline.',
      'Gable panels — taller and more wind-loaded; extra propping.',
      'Roof carcassing / trusses (separate RAMS).',
      'Frame inspected by frame engineer / building control before covering trades.',
    ],
    equipment: [
      'Mobile crane / telehandler with certified slings',
      'Banksman',
      'Temporary props (push-pull adjustable)',
      'Sequential-trip nailers',
      'Cordless impact drivers',
      'Spirit level (long), plumb bob',
    ],
    ppe: [
      'Hard hat with chin-strap',
      'Safety boots',
      'Hi-vis Class 3',
      'Harness on upper floor',
      'Cut-resistant gloves',
      'Eye protection',
    ],
    emergency:
      'Panel collapse — clear, 999. Wind >15 mph — STOP, brace, descend. Fall from height — 999. Suspended load drop — clear, 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'structural_studwork',
    title: 'Structural / load-bearing studwork',
    trades: ['Carpentry & joinery'],
    description: 'Load-bearing internal stud walls — sole plates, head plates, studs, lintels per engineer\'s spec.',
    defaultHazards: ['Manual handling', 'Loud noise (>85 dB)', 'Working at height'],
    scope:
      'Construct load-bearing internal stud partitions per structural engineer\'s drawings — sole plates fixed to slab, head plates, studs at design centres, double studs at openings, lintels above openings, noggings and bracing.',
    hazards: [
      { hazard: 'Drilling sole plates — hidden services in slab', risk: 'High', controls: 'Service drawings reviewed; CAT-detect over slab before drilling; never drill blind through screed.' },
      { hazard: 'Falls from working at height (top plates, ceiling-height studs)', risk: 'Med', controls: 'Mobile tower or podium step; never two-handed work from a stepladder; harness for unguarded.' },
      { hazard: 'Manual handling of long studs and double-stud lintels', risk: 'Med', controls: 'Two-person carry; mechanical aid for engineered timber lintels.' },
      { hazard: 'Cuts from saws and chisels', risk: 'Med', controls: 'Sharp blades; clamp work; eye protection.' },
      { hazard: 'Wrong stud / lintel size for the load', risk: 'High', controls: 'Engineer\'s drawings followed exactly; substitution requires written sign-off; no "close enough" subs.' },
    ],
    sequence: [
      'Mark out walls on slab and ceiling.',
      'CAT-detect over the marked line; check service drawings.',
      'Fix sole plate to slab; head plate to ceiling joists / floor joists above.',
      'Set studs at design centres (typically 400 / 600 c/c).',
      'Double studs at door / window openings.',
      'Engineered timber lintel above openings per drawings.',
      'Noggings at mid-height for board fixing and rigidity.',
      'Diagonal bracing where drawings require.',
      'Inspect before plasterboard goes on.',
    ],
    equipment: [
      'Sequential-trip nailer',
      'Cordless drill / impact',
      'Mitre saw, hand saw',
      'Spirit level (long), plumb bob',
      'CAT-detector',
      'Mobile tower / podium step',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves',
      'Eye protection',
      'Hearing protection',
    ],
    emergency:
      'Cut to hand from saw — pressure, A&E. Drill into hidden service — STOP, isolate, contact site manager. Fall from tower — 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'floor_decking',
    title: 'Floor decking — chipboard / OSB / engineered',
    trades: ['Carpentry & joinery'],
    description: 'Lay sub-floor decking sheets to floor joists — glued and screwed, T&G joints.',
    defaultHazards: ['Manual handling', 'Loud noise (>85 dB)', 'Slips / trips'],
    scope:
      'Lay tongue-and-groove chipboard, OSB or engineered timber sub-floor decking to floor joists / cassettes. Glued and screwed per maker. Cut for service penetrations. Note: do not lay deck until 1st fix services are complete (or build deck around access hatches).',
    hazards: [
      { hazard: 'Manual handling of full-size sheets (often 2.4 × 0.6, 18 mm)', risk: 'High', controls: 'Two-person carry on edge with grip handles; never single-handed up stairs; mechanical hoist for upper floors.' },
      { hazard: 'Falls through joist gaps before deck laid', risk: 'High', controls: 'Boarded path; lay outwards from access; never walk on joists alone.' },
      { hazard: 'Cuts from saws — circular saw and jigsaw', risk: 'Med', controls: 'Sharp blades; clamp work; eye protection; on-tool dust extraction for chipboard / OSB.' },
      { hazard: 'Wood dust — chipboard formaldehyde', risk: 'Med', controls: 'Cut outdoors / well-ventilated; M-class extraction; FFP3 mask; HEPA vac for clean-down.' },
      { hazard: 'Slips on offcut sheets and adhesive drips', risk: 'Med', controls: 'Continuous tidy; clean drips; offcut bin per room.' },
      { hazard: 'Damage to laid deck from heavy point loads', risk: 'Low', controls: 'Stack materials at supports, not mid-span; spread loads.' },
    ],
    sequence: [
      'Confirm 1st fix services complete; access hatches positioned.',
      'Set out — long edge perpendicular to joists; stagger end joints between courses.',
      'Apply adhesive bead to joist tops and T&G joint per maker.',
      'Lay sheet; tap joints together with rubber mallet through a packer.',
      'Screw to joists at design centres (typically 200 mm to perimeter, 300 c/c to body).',
      'Cut for service penetrations; tight to pipe / cable; firmly screwed each side.',
      'Continue staggered courses; mid-shift housekeeping.',
      'Final HEPA vac; cover with protection if other trades following.',
    ],
    equipment: [
      'Circular saw, jigsaw',
      'Cordless impact driver',
      'Adhesive cartridge gun',
      'Mallet, packers',
      'Spirit level',
      'M-class extractor',
    ],
    ppe: [
      'Hard hat (where site rules require)',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves',
      'Eye protection',
      'SNR ≥27 hearing protection',
      'FFP3 mask during cutting',
    ],
    emergency:
      'Cut from saw — pressure dressing, A&E if deep. Fall through joists — 999, do not move. Sheet dropped on foot — assess, A&E if any deformity. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'timber_decking',
    title: 'External timber decking',
    trades: ['Carpentry & joinery'],
    description: 'Build external decking — frame, posts, deck boards, balustrade — for patios and balconies.',
    defaultHazards: ['Manual handling', 'COSHH (chemicals)', 'Working at height', 'Slips / trips', 'UV exposure'],
    scope:
      'Construct external timber decking — concrete pad / post foundations, frame on bearers, deck boards (composite or treated softwood), balustrade and steps. Patio level OR raised / balcony per drawings.',
    hazards: [
      { hazard: 'Falls from raised / balcony decking edge', risk: 'High', controls: 'Edge protection (handrail) before any work above 600 mm; harness for unguarded balcony work; balustrade installed PROMPTLY after deck.' },
      { hazard: 'CCA / treated timber dust — copper / chromate exposure', risk: 'Med', controls: 'Cut outdoors only; FFP3 mask; gloves; long sleeves; do NOT burn offcuts; dispose responsibly.' },
      { hazard: 'Manual handling of post bases, joists, deck boards', risk: 'Med', controls: 'Two-person carry for posts; deck boards in small batches; pre-cut at the bench.' },
      { hazard: 'Slips on wet decking', risk: 'Med', controls: 'Anti-slip strips on stair noses; cleaning regime; non-slip footwear.' },
      { hazard: 'UV exposure during summer install', risk: 'Low', controls: 'SPF30+; hat; long-sleeved hi-vis; hydration breaks.' },
      { hazard: 'Cuts from saws', risk: 'Med', controls: 'Sharp blades; clamp work; eye protection.' },
    ],
    sequence: [
      'Pre-start — drawings, ground levels, weather window, balustrade design.',
      'Set out posts; dig / install ground anchors or post-base; concrete in.',
      'Install bearers / ledger board to building (with DPC).',
      'Install frame joists at design centres.',
      'Install handrail / balustrade BEFORE laying deck if raised.',
      'Lay deck boards; pre-drill softwood near ends; spaced for drainage.',
      'Install stair stringers and treads if present.',
      'Final inspection — handrail solid, no protrusions.',
    ],
    equipment: [
      'Auger / digging tools',
      'Concrete mixer or bagged mix',
      'Mitre saw, circular saw',
      'Cordless drill / impact',
      'Spirit level (long)',
      'String line and pegs',
    ],
    ppe: [
      'Hard hat (where site rules require)',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves',
      'Eye protection',
      'FFP3 mask during cutting',
      'Sun protection',
    ],
    emergency:
      'Fall from raised deck — 999. Cut to hand — pressure, A&E. Heat exhaustion — shade, hydrate, 999 if non-responsive. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'balcony_balustrade',
    title: 'Balcony / external balustrade install',
    trades: ['Carpentry & joinery'],
    description: 'Glass, metal or timber balustrade to balconies, terraces, stair landings — Part K compliant.',
    defaultHazards: ['Working at height', 'Manual handling', 'COSHH (chemicals)', 'Slips / trips'],
    scope:
      'Install external balustrade to balcony / terrace / stair landing — handrail, posts, infill (glass / metal / timber). Compliant with Building Regs Part K — height, infill, gap sizes, structural loading.',
    hazards: [
      { hazard: 'Falls from balcony edge before balustrade complete', risk: 'High', controls: 'Existing edge protection (scaffold or handrail) MUST remain in place until permanent balustrade is fixed; never strip temp before perm; harness if temp removed.' },
      { hazard: 'Manual handling of glass panels and metal posts', risk: 'High', controls: 'Two-person lift for any glass panel; suction handles; never rest unsupported against a wall — soft pad or rubber stops; sized at the supplier where possible.' },
      { hazard: 'Glass breakage in transit', risk: 'High', controls: 'Crate transport; never lay flat; gloves rated for glass; eye protection; tape over panels until install.' },
      { hazard: 'Wrong loading specification', risk: 'High', controls: 'Engineer-specified posts and fixings; pull-test post-anchor in concrete / steel substrate; never rely on plug-and-screw on concrete edge.' },
      { hazard: 'Sealant / adhesive fume', risk: 'Med', controls: 'Ventilate; nitrile gloves; eye protection during overhead silicone.' },
    ],
    sequence: [
      'Pre-start — Part K dimensions checked (handrail 1100mm balcony / 900mm stair, ≤100mm gap, no climbable infill).',
      'Existing edge protection retained.',
      'Mark out post positions; drill / chemically-anchor post bases per engineer.',
      'Install posts; pull-test before continuing.',
      'Install glass / metal / timber infill panels — never walk past unsupported glass.',
      'Install handrail; verify hand-grip continuous over the run.',
      'Strike temp edge protection ONLY when permanent is fixed.',
      'Final inspection — Part K dimensions; structural pull-test if engineer requires.',
    ],
    equipment: [
      'Hammer drill, diamond core if concrete',
      'Chemical anchor kit',
      'Suction cups for glass',
      'Cordless drill, impact',
      'Spirit level (long), laser level',
      'Pull-test gauge if engineer requires',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis Class 3',
      'Heavy cut-resistant gloves rated for glass',
      'Eye protection',
      'FFP3 mask if drilling concrete',
      'Harness if edge protection compromised',
    ],
    emergency:
      'Fall from balcony — 999. Glass breakage / cut — pressure dressing, A&E. Post pulled out under load — STOP, do not stand on the deck near it, contact engineer. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'glulam_steel_install',
    title: 'Glulam / structural steel install (carpentry side)',
    trades: ['Carpentry & joinery', 'Steel erection'],
    description: 'Install large structural beams — glulam, LVL or steel — as part of carpentry / structural works.',
    defaultHazards: ['Manual handling', 'Plant / pedestrian interaction', 'Working at height', 'Hot works'],
    scope:
      'Install large structural beams (glulam, LVL or steel) per engineer\'s drawings — bearings, padstones, fixings, restraint. Includes the carpentry trades that follow (joist hangers off the beam etc.).',
    hazards: [
      { hazard: 'Crushing during lift / set', risk: 'High', controls: 'Mobile crane or telehandler with banksman; lifting points marked; certified slings; nobody under suspended load; eye contact with operator before approaching.' },
      { hazard: 'Beam dropped or unstable on bearings', risk: 'High', controls: 'Padstones / bearings sized per engineer; verified before lift; pack and shim fully before releasing slings; props in place beneath to catch in case of slip.' },
      { hazard: 'Manual handling of heavy beams (steel often hundreds of kg)', risk: 'High', controls: 'NEVER manhandle steel; always mechanical; glulam may be 2-person for short runs but beyond 4 m always mechanical.' },
      { hazard: 'Working at height to bolt up', risk: 'High', controls: 'Tower or scaffold; harness for unguarded work; never balanced on a beam.' },
      { hazard: 'Hot works for steel adjustments / welding', risk: 'High', controls: 'Hot Works Permit; combustibles cleared; fire-watch; CO₂ extinguisher.' },
      { hazard: 'Beam temporarily propped before bearing complete', risk: 'High', controls: 'Temp props rated and inspected; propping schedule with engineer; never strike props until bearing fully cured / complete.' },
    ],
    sequence: [
      'Pre-start — engineer\'s drawings; lift plan; banksman briefed; weather window confirmed.',
      'Padstones / bearings constructed per drawings; cured.',
      'Temp props in place to take load on either side.',
      'Crane / telehandler lift beam to position; banksman in radio contact.',
      'Pack and shim onto bearings; verify level and centre.',
      'Bolt or strap per drawings; torque to spec.',
      'Verify with engineer before striking props.',
      'Following carpentry — joist hangers, restraint straps off the new beam.',
    ],
    equipment: [
      'Mobile crane or telehandler with banksman',
      'Certified slings, shackles',
      'Adjustable temp props',
      'Spirit level (long), laser level',
      'Torque wrench',
      'Hot works kit (if welding steel)',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis Class 3',
      'Cut-resistant gloves',
      'Eye protection',
      'Welding kit if hot works',
      'Harness for unguarded',
    ],
    emergency:
      'Beam falls / crushing — 999, do not move casualty. Slip from bearings — clear area, prop, contact engineer. Suspended load failure — clear area, 999. Hot works fire — fight if confined (CO₂); otherwise 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'concrete_slab_pour',
    title: 'Concrete slab pour',
    trades: ['Foundations & concrete'],
    description: 'Ground-bearing or suspended concrete slab pour to spec — formed, reinforced and poured.',
    defaultHazards: ['Manual handling', 'COSHH (chemicals)', 'Plant / pedestrian interaction', 'Slips / trips'],
    scope:
      'Pour ground-bearing or suspended concrete slab per engineer\'s drawings. Includes shuttering, edge formwork, mesh / rebar position, vapour barrier, insulation, pour from skip / pump / chute, vibrate, finish (power-float / tamp), and cure.',
    hazards: [
      { hazard: 'Plant / pedestrian interaction during pour', risk: 'High', controls: 'No-go zone marked; banksman in radio contact with truck driver / pump operator; eye contact with operator before approach; reverse alarms.' },
      { hazard: 'Cement burns and dermatitis', risk: 'High', controls: 'Long-sleeved overalls, nitrile gloves under work gloves, knee-length wellies during pour; immediate wash-off if splashed; never kneel in wet concrete; barrier cream.' },
      { hazard: 'Eye splashes from concrete', risk: 'High', controls: 'Eye protection during pour and float; eyewash within 30 seconds reach; flush 15+ minutes if splashed.' },
      { hazard: 'Manual handling of mesh sheets, rebar bundles, formwork', risk: 'Med', controls: 'Two-person lifts; mechanical aid for full mesh sheets; pre-cut at the bench; rotate handlers.' },
      { hazard: 'Slips on wet edges and access points', risk: 'Med', controls: 'Non-slip wellies; clean drips from access; spotter at access points during pour.' },
      { hazard: 'Power float — entanglement / cuts', risk: 'High', controls: 'Trained operator only; no loose clothing; emergency stop within reach; never rest on running float; never adjust blades whilst running.' },
      { hazard: 'Pump line burst', risk: 'High', controls: 'Pump-operator certified; line clamps on every joint; nobody stands within 5 m of a clamp during pump start; pump-pressure monitored; emergency stop briefed.' },
    ],
    sequence: [
      'Pre-start — drawings, weather forecast (no pour <5°C / >30°C / heavy rain forecast); brief team and pump operator; banksman briefed.',
      'Verify reinforcement against drawings; chairs / spacers in place; clean of debris.',
      'Position chute / pump line; clear no-go zone.',
      'Pour from one end working back; never overload one bay before vibrate.',
      'Vibrate (poker) to consolidate; do not over-vibrate.',
      'Tamp / float to level; check with straight edge.',
      'Power-float once concrete supports the weight; sequence panels.',
      'Cover with poly / hessian and water for cure (or curing compound per spec).',
      'Wash equipment off-site or in a bunded area; never into drains.',
    ],
    equipment: [
      'Concrete pump or skip-and-bucket',
      'Vibrating poker',
      'Power float (ride-on or hand)',
      'Tamping bar / straight-edge',
      'Mesh / rebar lifting tools',
      'Curing covers / compound',
    ],
    ppe: [
      'Hard hat',
      'Safety boots / wellies (knee-length during pour)',
      'Hi-vis Class 3',
      'Nitrile gloves under cut-resistant',
      'Eye protection during pour and float',
      'Long-sleeved overalls',
      'Hearing protection during float / vibrate',
    ],
    emergency:
      'Concrete in eye — flush 15 min, A&E. Person trapped under collapsed formwork — DO NOT enter to rescue alone; clear, brace, dig from outside; 999. Power-float entanglement — emergency stop, 999. Pump-line burst — clear area, isolate pump, 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'formwork',
    title: 'Formwork — install & strike',
    trades: ['Foundations & concrete', 'Carpentry & joinery'],
    description: 'Erect, brace and later strike formwork / shuttering for concrete elements (slabs, walls, beams).',
    defaultHazards: ['Manual handling', 'Working at height', 'Loud noise (>85 dB)', 'Slips / trips'],
    scope:
      'Erect timber / proprietary formwork to engineer\'s drawings for slabs, walls, columns, beams. Brace and tie. Strike after sufficient cure. Includes installation of stop-ends, water-bars and kickers.',
    hazards: [
      { hazard: 'Formwork collapse during pour', risk: 'High', controls: 'Engineer-designed formwork; ties at design centres; props rated for the load; no shortcuts; inspection by competent person before pour; nobody under formwork during pour.' },
      { hazard: 'Falls from height during wall / column form', risk: 'High', controls: 'Working platforms with edge protection; harness for unguarded; never work off the form itself.' },
      { hazard: 'Manual handling of forms (heavy panels / beams)', risk: 'High', controls: 'Mechanical aid for large panels; two-person for >25 kg; planned route.' },
      { hazard: 'Cuts from saws and impact tools', risk: 'Med', controls: 'Sharp blades; clamp work; eye protection.' },
      { hazard: 'Strike sequence error — premature load on green concrete', risk: 'High', controls: 'Strike times per engineer\'s schedule; concrete cylinder tests if load-bearing; never strike before sign-off.' },
      { hazard: 'Falling formwork on strike', risk: 'High', controls: 'Strike sequence planned top-down or per engineer; no-go zone below; helmets mandatory; tie release in controlled order.' },
    ],
    sequence: [
      'Set out per drawings; mark centre lines.',
      'Erect formwork panels; clamp / bolt joints; install ties.',
      'Brace fully — diagonals, props per engineer.',
      'Stop-ends, water-bars, kickers in place.',
      'Apply release agent to internal faces.',
      'Inspect by competent person; sign off as ready for pour.',
      'After pour and cure period — stripe per schedule.',
      'Strike: tops first (slabs / soffits) then sides; controlled.',
      'Clean and store formwork; replace damaged sheets.',
    ],
    equipment: [
      'Formwork panels (timber or proprietary)',
      'Adjustable props',
      'Wedges and clamps',
      'Release agent',
      'Cordless drill / impact',
      'Bracing timbers',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis',
      'Cut-resistant gloves',
      'Eye protection',
      'Hearing protection',
      'Harness for unguarded',
    ],
    emergency:
      'Formwork collapse — 999, do not enter to rescue. Fall from height — 999. Cut to hand — pressure, A&E. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'rebar_fix',
    title: 'Rebar fixing',
    trades: ['Foundations & concrete', 'Steel erection'],
    description: 'Cut, bend and fix steel reinforcement — slabs, walls, columns, footings.',
    defaultHazards: ['Manual handling', 'Loud noise (>85 dB)', 'Hand-arm vibration (HAVS)', 'Slips / trips'],
    scope:
      'Cut, bend and fix reinforcement (loose bar, mesh, prefabricated cages) per structural engineer\'s bar bending schedule. Includes spacers, chairs, lap lengths, anchor bond.',
    hazards: [
      { hazard: 'Impalement on protruding rebar', risk: 'High', controls: 'Plastic mushroom caps on every protruding bar end at floor level; bend down ends where above floor; warning tape around large protruding cages.' },
      { hazard: 'Cuts from rebar ends and tying wire', risk: 'High', controls: 'Cut-resistant gloves; eye protection; never carry rebar by hand near the body.' },
      { hazard: 'Manual handling of long bars and heavy mesh', risk: 'High', controls: 'Two-person carry; rebar yard with off-loading bay; mechanical aid for cages; rotate handlers.' },
      { hazard: 'Hand-arm vibration from cut-and-bend tools', risk: 'Med', controls: 'Trigger-time logged informally; rotate; report tingling.' },
      { hazard: 'Slips / trips on rebar mat before deck', risk: 'High', controls: 'Boards laid for access across mat; no walking on rebar tops between chairs; clean offcuts continuously.' },
      { hazard: 'Cut-and-bend machine entanglement', risk: 'High', controls: 'Trained operator only; guards in place; emergency stop within reach; never run loose clothing or jewellery.' },
    ],
    sequence: [
      'Pre-start — bar-bending schedule, drawings, lift plan if cages.',
      'Cut and bend bars per schedule at the cut-and-bend bench.',
      'Set out chairs / spacers on substrate.',
      'Lay bottom mat per drawing; tie at intersections.',
      'Install starter bars / dowels for vertical elements.',
      'Tie top mat; chairs to maintain cover.',
      'Check cover from formwork to steel against engineer\'s drawings.',
      'Cap all protruding ends with mushroom caps.',
      'Inspect by engineer / building control before pour.',
    ],
    equipment: [
      'Rebar cut-and-bend machine',
      'Tying tools (manual or battery)',
      'Tying wire',
      'Plastic chairs and spacers',
      'Mushroom caps',
      'Lifting slings (for cages)',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis',
      'Cut-resistant gloves',
      'Eye protection',
      'Hearing protection during cut',
    ],
    emergency:
      'Impalement / fall onto rebar — DO NOT pull free; cut bar above and below with the casualty stable; 999. Cut from rebar — pressure, A&E. Cut-and-bend entanglement — emergency stop, 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'structural_steel_erect',
    title: 'Structural steel erection',
    trades: ['Steel erection'],
    description: 'Erect structural steel frame — columns, beams, bracing — for residential or light commercial.',
    defaultHazards: ['Working at height', 'Manual handling', 'Plant / pedestrian interaction', 'Hot works'],
    scope:
      'Erect structural steel frame to engineer\'s drawings — columns to base plates, primary and secondary beams, bracing, fin plates, bolted and / or welded connections per spec.',
    hazards: [
      { hazard: 'Crushing during lift / set', risk: 'High', controls: 'Mobile crane with appointed person; lift plan signed off; banksman in radio contact; certified slings; no entry under suspended load.' },
      { hazard: 'Falls from steel before connections complete', risk: 'High', controls: 'Lanyards / fall-restraint to a permanent anchor; never untie until secondary fall protection in place; column handrails / temp edge protection on each lift; experienced steel erectors only.' },
      { hazard: 'Steel collapse during erection (insufficient bracing)', risk: 'High', controls: 'Bracing installed sequentially per erection sequence; never offer up next member without securing previous; wind speed monitored.' },
      { hazard: 'Hot works for site welding', risk: 'High', controls: 'Hot Works Permit; combustibles cleared; CO₂ extinguisher; fire-watch; cool before pack-up.' },
      { hazard: 'Wind acting on steel', risk: 'High', controls: 'STOP at sustained wind >15 mph during lift; never lift large surface area in wind.' },
      { hazard: 'Bolt-up at height', risk: 'Med', controls: 'Powered impact wrench with lanyard; torque wrench for HSFG bolts to spec; bolt bag belt; never carry loose bolts in pockets.' },
    ],
    sequence: [
      'Pre-start — engineer\'s drawings, lift plan, weather; appointed person.',
      'Set base plates onto cured pad foundations; check level.',
      'First column lifted, plumbed and shimmed; bolted to base.',
      'Lift first beam between columns; bolt fin plates; column to second column connection in same lift.',
      'Continue grid frame; brace as you go.',
      'Tighten HSFG bolts to torque spec; record.',
      'Site welds where required (Hot Works Permit).',
      'Strip temp props after final inspection.',
      'Inspect connections; sign off by appointed person / engineer.',
    ],
    equipment: [
      'Mobile crane with certified appointed person',
      'Banksman + radios',
      'Certified slings, shackles, tag lines',
      'Powered impact wrench',
      'Torque wrench',
      'Bolt bag belt',
      'Welder (if site welding) + fire kit',
    ],
    ppe: [
      'Hard hat with chin-strap',
      'Safety boots',
      'Hi-vis Class 3',
      'Twin-lanyard fall-restraint harness',
      'Cut-resistant gloves',
      'Eye protection',
      'Welding kit if site welding',
    ],
    emergency:
      'Fall from steel — 999, recover from harness within 15 min (suspension trauma plan). Suspended load failure — clear area, 999. Hot works fire — fight if confined (CO₂); otherwise 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'precast_install',
    title: 'Pre-cast concrete install',
    trades: ['Steel erection', 'Foundations & concrete'],
    description: 'Install pre-cast concrete elements — lintels, planks, slabs, stairs, balconies.',
    defaultHazards: ['Manual handling', 'Plant / pedestrian interaction', 'Working at height'],
    scope:
      'Install pre-cast concrete elements (lintels, hollowcore planks, stair flights, balcony units) per engineer\'s drawings — lifted into position, bedded, grouted and tied / strapped per detail.',
    hazards: [
      { hazard: 'Crushing during lift / set', risk: 'High', controls: 'Mobile crane with appointed person; certified slings to maker\'s lifting points only; banksman; no entry under suspended load.' },
      { hazard: 'Pre-cast unit slipping or cracking on bearings', risk: 'High', controls: 'Bearings sized per engineer; verify before lift; pack and shim; props beneath until grout cured; never undermine fresh joint.' },
      { hazard: 'Working at height to bolt / strap up', risk: 'High', controls: 'Tower or scaffold; harness for unguarded; certified anchor points.' },
      { hazard: 'Wind on large units (planks, stairs)', risk: 'Med', controls: 'STOP at wind >15 mph; tag lines on big units to control swing.' },
      { hazard: 'Manual handling of bedding mortar / grout', risk: 'Low', controls: 'Pre-mixed delivered; nitrile gloves; barrier cream.' },
    ],
    sequence: [
      'Pre-start — drawings; lift plan; appointed person; banksman briefed.',
      'Build / verify bearings; pads to engineer\'s spec.',
      'Apply bedding mortar.',
      'Lift unit using maker\'s lifting points only; tag line for control.',
      'Set onto bearings; banksman positions.',
      'Pack and shim per spec.',
      'Strap / bolt per drawings.',
      'Grout structural joints; cure per spec.',
      'Final inspection by engineer.',
    ],
    equipment: [
      'Mobile crane',
      'Banksman + radios',
      'Certified slings, shackles, tag lines',
      'Bedding mortar / pre-mixed grout',
      'Adjustable props',
      'Spirit level (long)',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis Class 3',
      'Cut-resistant gloves; nitrile under for grout',
      'Eye protection',
      'Harness for unguarded',
    ],
    emergency:
      'Suspended load failure — clear area, 999. Pre-cast slip from bearings — clear, prop, contact engineer. Crushing — 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'piling',
    title: 'Mini-piling for residential',
    trades: ['Foundations & concrete', 'Groundworks'],
    description: 'Mini-piling for foundations or underpinning where load-bearing strata are deep.',
    defaultHazards: ['Plant / pedestrian interaction', 'Manual handling', 'COSHH (chemicals)', 'Loud noise (>85 dB)', 'Hand-arm vibration (HAVS)'],
    scope:
      'Install mini-piles (typically 150–300 mm CFA, drive or drilled) for foundation support or underpinning. Includes pile cap pour after pile completion.',
    hazards: [
      { hazard: 'Plant / pedestrian in same area', risk: 'High', controls: 'Exclusion zone around the rig; banksman; nobody approaches working rig without eye contact with operator; reverse alarms; rig stable on level ground or mat.' },
      { hazard: 'Hidden services in pile location', risk: 'High', controls: 'Service drawings reviewed; CAT-scan before each pile; trial pit if uncertain; no piling in unverified ground.' },
      { hazard: 'Rig overturn on soft ground', risk: 'High', controls: 'Ground bearing checked; load-spreading mats; never on a slope beyond rig spec; rig operator certified.' },
      { hazard: 'Concrete pump / grout placement', risk: 'High', controls: 'Pump certified; line clamps; nobody within 5 m of a clamp during pump start; emergency stop briefed.' },
      { hazard: 'Loud noise from rig', risk: 'High', controls: 'SNR ≥30 hearing protection; cordon; signage if >85 dB measured.' },
      { hazard: 'Hand-arm vibration', risk: 'Med', controls: 'Operator rotation; HAVS surveillance.' },
    ],
    sequence: [
      'Pre-start — drawings, service plan, rig load capacity vs ground bearing, banksman.',
      'Set out pile positions; CAT-scan over each.',
      'Position rig on level mat / ground.',
      'Drill / drive to design depth; record per pile.',
      'Place rebar cage (if CFA / bored).',
      'Pump grout / concrete to displace.',
      'Cure per spec.',
      'Trim pile head to design level.',
      'Construct pile cap; inspect by engineer.',
    ],
    equipment: [
      'Mini-pile rig with operator',
      'Banksman + radios',
      'Concrete / grout pump',
      'Pile-trim saw',
      'Rebar cages',
      'CAT-detector + Genny',
    ],
    ppe: [
      'Hard hat',
      'Safety boots with mid-sole',
      'Hi-vis Class 3',
      'Hearing protection SNR ≥30',
      'Cut-resistant gloves',
      'Eye protection',
      'FFP3 mask for grout cutting',
    ],
    emergency:
      'Rig overturn — clear area, 999, do not enter. Service strike — STOP, isolate, 0800 111 999 if gas; 999 in serious electrical strike. Pump-line burst — clear, 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'ground_beam',
    title: 'Ground beam install',
    trades: ['Foundations & concrete'],
    description: 'Pre-cast or in-situ ground beams spanning between piles / pads.',
    defaultHazards: ['Manual handling', 'Plant / pedestrian interaction', 'Excavation', 'Slips / trips'],
    scope:
      'Install pre-cast or in-situ reinforced concrete ground beams spanning between pile heads / pad foundations. Includes excavation where needed, formwork (in-situ), rebar fixing, pour or pre-cast lift, level and cure.',
    hazards: [
      { hazard: 'Excavation collapse around pile heads', risk: 'High', controls: 'Battering or trench boxes if >1.2 m deep; daily inspection; never enter unsupported.' },
      { hazard: 'Plant / pedestrian interaction during lift', risk: 'High', controls: 'Banksman; no-go zone; eye contact with operator.' },
      { hazard: 'Lifting pre-cast beams (heavy)', risk: 'High', controls: 'Mobile crane / telehandler with certified slings; lifting points marked; never sling outside rated points.' },
      { hazard: 'Manual handling of formwork, rebar, props', risk: 'Med', controls: 'Two-person where >25 kg; mechanical aid where possible.' },
      { hazard: 'Cement burns / dermatitis (in-situ pour)', risk: 'Med', controls: 'Nitrile gloves under cut-resistant; long-sleeved overalls; immediate wash-off; eye protection.' },
    ],
    sequence: [
      'Pre-start — drawings, lift plan, banksman.',
      'Excavate beam runs to engineer\'s depth.',
      'Inspect / install pile heads / pads.',
      'Pre-cast option — lift, set, bed, strap. In-situ — formwork, rebar cage, pour, cure, strip.',
      'Verify level and centre.',
      'Backfill in compacted layers per spec.',
      'Inspect by engineer / building control.',
    ],
    equipment: [
      'Excavator',
      'Crane / telehandler with banksman',
      'Slings, shackles, tag lines',
      'Formwork timbers (in-situ)',
      'Rebar bench',
      'Concrete vibrator',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis Class 3',
      'Cut-resistant gloves; nitrile under for cement',
      'Eye protection',
      'Hearing protection during vibrate',
    ],
    emergency:
      'Excavation collapse — 999. Suspended load failure — clear, 999. Cement in eye — flush 15 min, A&E. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'screed',
    title: 'Floor screeding (sand-cement / liquid)',
    trades: ['Foundations & concrete', 'Tiling'],
    description: 'Sand-cement traditional or pumped liquid (anhydrite) floor screed over insulation / UFH.',
    defaultHazards: ['Manual handling', 'COSHH (chemicals)', 'Plant / pedestrian interaction', 'Slips / trips'],
    scope:
      'Lay sand-cement traditional screed OR pumped liquid (calcium sulphate / anhydrite) screed to floors over insulation and underfloor heating. Includes edge isolation, levels and dry-time monitoring.',
    hazards: [
      { hazard: 'Cement / lime burns and dermatitis', risk: 'High', controls: 'Nitrile gloves under work gloves; long-sleeved overalls; knee-length wellies in fresh screed; immediate wash-off; barrier cream; never kneel directly in fresh material.' },
      { hazard: 'Pump line burst (liquid screed)', risk: 'High', controls: 'Pump certified; line clamps on every joint; nobody within 5 m of clamp during pump start; emergency stop briefed; pump operator trained.' },
      { hazard: 'Manual handling of bagged sand-cement', risk: 'High', controls: 'Mechanical mixing where possible; pre-mixed silo on larger jobs; rotate operators; bag carry no more than 25 kg.' },
      { hazard: 'Slips on wet screed / overspill', risk: 'High', controls: 'Wet floor signs; clean drips at access; non-slip wellies; spotter at access during pour.' },
      { hazard: 'UFH pipe damage during pour', risk: 'Med', controls: 'UFH pressurised before screed; pressure monitored during and after; never walk on pipes between fixings; spread loads on boards.' },
      { hazard: 'Slow drying and skin / eye risk during follow-on', risk: 'Med', controls: 'Anhydrite — sand off laitance before any tile / vinyl; ventilation during cure; respect 1 mm/day drying rule for moisture.' },
    ],
    sequence: [
      'Pre-start — substrate clean, level checked, edge insulation strip in place, UFH pressurised.',
      'Sand-cement option — mix to spec; barrow / pump to position; lay in bays; rule and float; trowel finish.',
      'Liquid option — pump from outside via flexible line; rake to level; agitate with dappling bar.',
      'Edge protection / level marks set out before pour.',
      'Cover per spec; ventilate to dry.',
      'Test moisture before any floor finish; record.',
      'Sand off laitance (anhydrite) before tile / vinyl.',
    ],
    equipment: [
      'Mortar mixer (sand-cement) OR liquid screed pump',
      'Barrows, dappling bars, smoothing tools',
      'Trowels, floats',
      'Edge insulation strip',
      'Moisture meter',
    ],
    ppe: [
      'Hard hat (where site rules require)',
      'Knee-length wellies during pour',
      'Hi-vis vest',
      'Nitrile gloves under cut-resistant',
      'Eye protection',
      'Long-sleeved overalls',
      'FFP3 mask during dry mix and laitance sanding',
    ],
    emergency:
      'Cement / screed in eye — flush 15 min, A&E. Pump-line burst — clear, isolate, 999. UFH pipe burst (boiling water) — isolate at manifold, 999 if scalding casualty. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'concrete_pump_ops',
    title: 'Concrete pump operations',
    trades: ['Concrete pump'],
    description: 'Operating a static or boom pump for concrete delivery — set up, pump, line clearing, shut down.',
    defaultHazards: ['Plant / pedestrian interaction', 'COSHH (chemicals)', 'Loud noise (>85 dB)', 'Slips / trips'],
    scope:
      'Position, set up and operate a concrete pump (boom or static line) to deliver concrete. Includes outrigger deployment, hopper feed, line clearing, washout.',
    hazards: [
      { hazard: 'Boom contact with overhead lines / structure', risk: 'High', controls: 'Overhead survey before deployment; minimum clearance from live OHL per ENA G39 (typically 9 m unless specifically isolated); banksman; never deploy boom blind; outriggers fully out and pad-supported.' },
      { hazard: 'Pump overturn from outrigger failure', risk: 'High', controls: 'Ground-bearing capacity verified; load-spreading mats / pads on every outrigger; never on a slope beyond machine spec; check for buried services beneath.' },
      { hazard: 'Pump-line burst', risk: 'High', controls: 'Line clamps on every joint; nobody within 5 m of any clamp during pump start; emergency stop within reach of pump operator and the pour leader; pressure monitored; line sized for the spec.' },
      { hazard: 'Whip / hose movement at delivery end', risk: 'High', controls: 'Operator at end of hose holds firmly; trained operator only; never wrap hose around body or arm; nobody else inside the swing arc.' },
      { hazard: 'Cement burns and dermatitis', risk: 'Med', controls: 'Long sleeves, nitrile gloves; immediate wash-off; eye protection at delivery end.' },
      { hazard: 'Washout — chemical and slip risk', risk: 'Med', controls: 'Bunded washout area; never into drains or watercourses; barriers around washout pit.' },
    ],
    sequence: [
      'Pre-start — pump certificate (LOLER if boom), operator licence, lift / pump plan, overhead survey, banksman.',
      'Position pump on level firm ground; load-spreading pads; outriggers fully extended.',
      'Boom-out OR set up static line; clamp every joint; brief end-of-line operative.',
      'Prime line with priming grout per maker.',
      'Pump concrete in coordination with pour team; agreed signals; emergency stop briefed.',
      'Monitor line pressure; pause if blockage suspected — never blast clear under pressure.',
      'On finish — clear line with sponge balls / water; capture washout in bunded area.',
      'Stow boom; lift outriggers; depart.',
    ],
    equipment: [
      'Boom or static concrete pump (in-date LOLER if boom)',
      'Pump licence holder operator',
      'Outrigger pads',
      'Line clamps + spares',
      'Sponge balls for line clearing',
      'Bunded washout container',
      'Two-way radios',
    ],
    ppe: [
      'Hard hat',
      'Safety boots / wellies',
      'Hi-vis Class 3',
      'Cut-resistant gloves; nitrile under for cement',
      'Eye protection',
      'Hearing protection',
      'Long-sleeved overalls',
    ],
    emergency:
      'Boom contact with OHL — DO NOT touch the pump; nobody approaches; isolate by DNO, 999. Outrigger failure / pump tilt — clear area, 999. Line burst — emergency stop, clear, 999 if injury. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'retaining_wall',
    title: 'Retaining wall construction',
    trades: ['Foundations & concrete', 'Bricklaying', 'Groundworks'],
    description: 'Construct retaining wall — concrete, masonry or gabion — for residential level changes.',
    defaultHazards: ['Excavation', 'Manual handling', 'Working at height', 'COSHH (chemicals)'],
    scope:
      'Construct retaining wall (reinforced concrete, masonry / gravity, or gabion) per engineer\'s drawings. Includes excavation, drainage to behind, weep holes, copings and backfill.',
    hazards: [
      { hazard: 'Excavation collapse behind retaining wall', risk: 'High', controls: 'Battering or sheet-piling per engineer; never work below an unsupported face; stop in heavy rain; daily inspection.' },
      { hazard: 'Wall failure during construction (insufficient cure / drainage)', risk: 'High', controls: 'Engineer-designed; props / shoring per drawings; weep pipes installed; backfill staged per cure schedule; never backfill before sufficient cure.' },
      { hazard: 'Manual handling of blocks, mass concrete, gabion baskets', risk: 'High', controls: 'Two-person carry; mechanical aid for filled gabions; pre-cut where possible.' },
      { hazard: 'Cement burns and dermatitis', risk: 'Med', controls: 'Nitrile gloves under work gloves; immediate wash; eye protection.' },
      { hazard: 'Falls from top of higher retaining walls during build', risk: 'High', controls: 'Tower or scaffold; harness for any unguarded; never work off the wall itself.' },
      { hazard: 'Hidden services in dig area', risk: 'High', controls: 'CAT-scan; Permit-to-Dig; hand-dig within 300 mm of known services.' },
    ],
    sequence: [
      'Pre-start — engineer\'s drawings, ground survey, weather forecast.',
      'Excavate to footing depth; batter back or shore.',
      'Pour footing; cure per spec.',
      'Construct stem — concrete (formwork + rebar + pour) or masonry (laid courses with weep pipes).',
      'Install drainage stone, geotextile, perforated pipe behind wall.',
      'Weep holes / pipe outlets to face.',
      'Copings to top.',
      'Backfill in compacted layers per engineer\'s schedule.',
      'Restore surface; inspect.',
    ],
    equipment: [
      'Excavator + operator',
      'Concrete mixer / pump',
      'Formwork / brickwork tools',
      'Compaction plate',
      'Hoist for materials to upper height',
      'CAT-scan',
    ],
    ppe: [
      'Hard hat',
      'Safety boots with mid-sole',
      'Hi-vis Class 3',
      'Cut-resistant gloves; nitrile under for cement',
      'Eye protection',
      'Hearing protection during compaction / cutting',
      'Harness for unguarded work',
    ],
    emergency:
      'Excavation collapse — 999. Wall partial collapse — clear, contact engineer, 999 if injury. Service strike — STOP, isolate, 999 / 0800 111 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'stonework',
    title: 'Stonework — natural stone laying / repair',
    trades: ['Bricklaying'],
    description: 'Natural stone laying or repair — Cotswold, sandstone, limestone, granite walling.',
    defaultHazards: ['Manual handling', 'COSHH (chemicals)', 'Silica dust', 'Working at height', 'Slips / trips'],
    scope:
      'Lay or repair natural stone walling — random rubble, coursed, ashlar — to architect / structural drawings, including lime mortar pointing where appropriate to listed / heritage work.',
    hazards: [
      { hazard: 'Manual handling of stone (very heavy, awkward)', risk: 'High', controls: 'Two-person lift for any stone >25 kg; mechanical aid (telehandler with stone clamp) for large blocks; rotate handlers; pre-position at point of use.' },
      { hazard: 'Silica / RCS dust from cutting stone', risk: 'High', controls: 'Wet cutting saw — never dry-cut; FFP3 mask; on-tool extraction (M-class) for any dry trim; cut downwind of others; HEPA vac residual; never dry-sweep.' },
      { hazard: 'Cement / lime burns and dermatitis', risk: 'Med', controls: 'Lime is more aggressive than cement on skin; nitrile gloves under work gloves; long sleeves; immediate wash; barrier cream.' },
      { hazard: 'Eye injury from stone chips', risk: 'Med', controls: 'Eye protection during all dressing and cutting; eyewash within 30s.' },
      { hazard: 'Working at height for upper courses', risk: 'High', controls: 'Scaffold tagged; toe-boards, double guard-rail, mid-rail; harness for any unguarded.' },
      { hazard: 'Falling stone onto people below', risk: 'High', controls: 'Toe-boards; debris netting where public nearby; no-go zone below; helmets mandatory below.' },
    ],
    sequence: [
      'Confirm scaffold tagged.',
      'Set out from datum line; pre-arrange stones for fit.',
      'Mix mortar (cement OR lime per spec) on a board / mixer.',
      'Lay first course; level checked.',
      'Continue laying; tooling joints to finish.',
      'Cut where required at the wet-cut saw — outdoors / extracted area only.',
      'Repoint joints (where in scope) using a hawk and pointing trowel.',
      'Brush off mortar residue from stone faces; never acid-clean limestone.',
      'Cover lime mortar with hessian / poly to slow cure.',
    ],
    equipment: [
      'Wet bench saw or angle-grinder with diamond blade and water suppression',
      'Forced-action mortar mixer',
      'Stone clamps for telehandler',
      'Hand tools — chisels, mash hammer, pointing trowel',
      'Hawk, levels, line and pins',
      'M-class extraction',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant grip gloves; nitrile under for lime / cement',
      'Eye protection',
      'FFP3 mask during cutting',
      'Hearing protection during cutting',
    ],
    emergency:
      'Cement / lime in eye — flush 15+ min, A&E. Stone dropped on foot — assess, A&E if any deformity. Cut to hand from saw — pressure, A&E. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'repointing',
    title: 'Repointing — lime / cement mortar',
    trades: ['Bricklaying'],
    description: 'Rake out and replace failing mortar joints in brick or stone facade.',
    defaultHazards: ['Working at height', 'Silica dust', 'COSHH (chemicals)', 'Loud noise (>85 dB)', 'Hand-arm vibration (HAVS)'],
    scope:
      'Rake out failed mortar joints in brick or stone facade and replace with appropriate mortar — lime mortar for pre-1919 / heritage work, cement-lime for general. Includes raking, brushing out, dampening, repointing and finishing.',
    hazards: [
      { hazard: 'Silica / RCS dust from raking out joints with grinder / chisel', risk: 'High', controls: 'On-tool extraction (M-class) on any powered raking; FFP3 mask MANDATORY; wet method where possible; HEPA vac for clean-down; never dry-sweep dust.' },
      { hazard: 'Working at height on scaffold or tower', risk: 'High', controls: 'Scaffold tagged within current week before use; double guard-rail and toe-boards; harness for any unguarded.' },
      { hazard: 'Cement / lime burns and dermatitis', risk: 'High', controls: 'Lime is highly alkaline and aggressive — nitrile gloves under cut-resistant; long sleeves; eye protection during overhead work; immediate wash if splashed; barrier cream.' },
      { hazard: 'Hand-arm vibration from grinder', risk: 'Med', controls: 'Low-vibration tools; rotate operator; trigger-time managed; HAVS surveillance.' },
      { hazard: 'Loud noise from grinder', risk: 'High', controls: 'SNR ≥30 hearing protection; cordon; signage; respect adjacent residents (noise hours).' },
      { hazard: 'Falling debris from joints onto people below', risk: 'Med', controls: 'Debris netting on residential frontage; no-go zone; helmets mandatory below; sheet down sills / paving.' },
    ],
    sequence: [
      'Survey — identify failed joints; sample existing mortar for match (heritage).',
      'Set up scaffold / tower; tag.',
      'Sheet down below to catch debris.',
      'Rake out joints to depth (typically 25 mm); never undermine the bed of the unit above.',
      'Brush and dampen joints (especially lime).',
      'Mix mortar to match; press in with pointing trowel.',
      'Tool joint to profile (struck, weather-struck, recessed, flush) per spec.',
      'Sponge / brush off residue from face; never acid-clean limestone.',
      'Cover lime mortar with hessian to slow cure.',
    ],
    equipment: [
      'Mortar raker / angle grinder with raking blade',
      'On-tool M-class extraction',
      'HEPA vacuum',
      'Pointing trowels',
      'Mixing bucket + paddle',
      'Hawk',
      'Spray bottle for dampening',
      'Hessian sheets (lime work)',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis',
      'Nitrile gloves under cut-resistant',
      'Eye protection',
      'FFP3 mask MANDATORY',
      'Hearing protection SNR ≥30',
    ],
    emergency:
      'Lime in eye — flush 15+ min, A&E. Hand-grinder cut — pressure, A&E. Fall from scaffold — 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'party_walls',
    title: 'Party wall construction',
    trades: ['Bricklaying'],
    description: 'Construct or alter party walls per architect / Building Regs Part E (acoustics) and Part B (fire).',
    defaultHazards: ['Manual handling', 'Working at height', 'COSHH (chemicals)', 'Silica dust', 'Slips / trips'],
    scope:
      'Construct or alter party walls between dwellings to comply with Part E (acoustic), Part B (fire 1-hour rating typical) and any structural requirements. Includes wall ties, flexible cavity insulation, fire-stopping at floor and roof junctions.',
    hazards: [
      { hazard: 'Working at height on full party-wall elevation', risk: 'High', controls: 'Scaffold tagged; double guard-rail and toe-boards at every lift; harness for any unguarded.' },
      { hazard: 'Manual handling of bricks, blocks, lintels', risk: 'High', controls: 'Two-person carry for block >20 kg; mechanical lift to platform; rotate.' },
      { hazard: 'Silica dust from cutting blocks', risk: 'High', controls: 'Wet-cut saw outdoors only; FFP3 mask; on-tool extraction; HEPA vac.' },
      { hazard: 'Cement burns and dermatitis', risk: 'Med', controls: 'Nitrile gloves under cut-resistant; immediate wash; eye protection.' },
      { hazard: 'Fire-stop / acoustic detail compromised by penetrations', risk: 'High', controls: 'Penetrations sealed with intumescent / acoustic mastic; verify with building control before plasterboard goes on.' },
      { hazard: 'Falling materials onto people below', risk: 'High', controls: 'Toe-boards; debris netting; no-go zone below; helmets mandatory below.' },
    ],
    sequence: [
      'Set out — verify architect\'s detail against drawings.',
      'Lay first courses to DPC / kicker.',
      'Continue laying; install ties at design centres; flexible cavity insulation between dwellings.',
      'Lintels at openings per structural engineer.',
      'Fire-stop / acoustic seal at floor junctions and at roof line.',
      'Penetrations sealed with intumescent / acoustic mastic.',
      'Inspect with building control before plaster.',
      'Strike joints to spec.',
    ],
    equipment: [
      'Scaffold (independently tagged)',
      'Forced-action mortar mixer',
      'Wet-cut bench saw',
      'Trowels, lines, plumb-bobs, levels',
      'M-class extraction',
      'Acoustic / intumescent sealant guns',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant grip gloves; nitrile under for cement',
      'Eye protection during cutting',
      'FFP3 mask during cutting',
      'Hearing protection',
    ],
    emergency:
      'Cement in eye — flush 15+ min, A&E. Fall from scaffold — 999. Cut to hand from saw — pressure, A&E. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'lintels_padstones',
    title: 'Lintels & padstones',
    trades: ['Bricklaying', 'Foundations & concrete'],
    description: 'Install steel or concrete lintels, padstones and bearing pads above openings or under steel beams.',
    defaultHazards: ['Manual handling', 'Working at height', 'Plant / pedestrian interaction'],
    scope:
      'Install steel (Catnic / equivalent) or pre-cast concrete lintels above openings; install padstones and bearing pads to support steel beams or trusses. Per structural engineer\'s drawings.',
    hazards: [
      { hazard: 'Lintel / padstone falling during install', risk: 'High', controls: 'Temp props beneath the opening BEFORE removing existing structure (refurb); two-person lift; never single-handed; planned route; lifting points if mechanical.' },
      { hazard: 'Manual handling of heavy lintels (often 50–80 kg)', risk: 'High', controls: 'Two-person; mechanical aid (telehandler / lifting strap) for >40 kg; pre-position at point of use.' },
      { hazard: 'Wrong size / load capacity for the opening', risk: 'High', controls: 'Engineer\'s drawings followed exactly; lintel reference matched against schedule; substitution requires written engineer sign-off.' },
      { hazard: 'Working at height to set lintel', risk: 'High', controls: 'Scaffold or tower; harness if unguarded; never work off blocks balanced on a step.' },
      { hazard: 'Cement burns from bedding mortar', risk: 'Low', controls: 'Nitrile gloves; immediate wash if splashed.' },
    ],
    sequence: [
      'Pre-start — engineer\'s drawings; lintel schedule.',
      'For refurb — temp props (acro / strongbacks) BEFORE breakout.',
      'Set out bearings; padstones bedded if required.',
      'Bed lintel onto cleaned bearing surface; pack and shim plumb / level.',
      'Tighten / restrain per detail.',
      'Build up brickwork above lintel as load develops.',
      'Strike temp props per cure schedule.',
      'Inspect by engineer / building control where required.',
    ],
    equipment: [
      'Adjustable temp props',
      'Telehandler / lifting strap (optional)',
      'Spirit level (long), plumb bob',
      'Sash cramps, packing shims',
      'Cordless drill / impact',
      'Mortar bench',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis',
      'Cut-resistant gloves; nitrile under for mortar',
      'Eye protection',
      'Harness if unguarded',
    ],
    emergency:
      'Lintel falls / crushing — 999, do not lift off if conscious / breathing affected. Wall failure / brickwork collapse — clear area, 999, contact engineer. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'cavity_trays',
    title: 'Cavity trays & DPC',
    trades: ['Bricklaying'],
    description: 'Install cavity trays, lead trays, weep vents and DPC at openings, abutments and at ground level.',
    defaultHazards: ['Manual handling', 'Working at height', 'COSHH (chemicals)'],
    scope:
      'Install lead or proprietary cavity trays at openings, abutments and step / stagger junctions; DPC at low level; weep vents at perimeter; ensure water shed to outside.',
    hazards: [
      { hazard: 'Working at height on scaffold', risk: 'Med', controls: 'Scaffold tagged; toe-boards, double guard-rail; harness for unguarded.' },
      { hazard: 'Manual handling of long lead / proprietary tray rolls', risk: 'Med', controls: 'Two-person carry up scaffold; pre-cut to length at the bench.' },
      { hazard: 'Lead exposure (where lead trays used)', risk: 'Med', controls: 'Wash hands and forearms before food; FFP3 if hot-dressing; nitrile gloves; minimise lead handling time.' },
      { hazard: 'Tray detail compromised — leads to damp ingress later', risk: 'High', controls: 'Detail drawings followed exactly; tray dressed to outer leaf; weeps at correct centres; building control inspection before brickwork covers it.' },
      { hazard: 'Cement / mortar burns', risk: 'Low', controls: 'Nitrile gloves; immediate wash.' },
    ],
    sequence: [
      'Set out — verify tray locations against architect\'s drawings.',
      'At openings — tray dressed across cavity, lapped to DPC of inner leaf, returned at jambs, weeps every 450 mm at outer leaf.',
      'At abutments — tray dressed up wall and out to outer leaf.',
      'At ground floor — DPC continuous, lapped, sealed.',
      'Weep vents installed at low level and above openings.',
      'Bed in mortar; build up next courses.',
      'Inspect by building control where required.',
    ],
    equipment: [
      'Tin snips, lead dressing tools',
      'Trowels',
      'Spirit level',
      'Cordless drill if mechanical fixings',
      'Mortar bench',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis',
      'Cut-resistant gloves; nitrile under for mortar',
      'Eye protection',
    ],
    emergency:
      'Cement in eye — flush 15+ min, A&E. Fall from scaffold — 999. Lead exposure symptoms — fresh air; report to occupational health. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'underpinning',
    title: 'Underpinning',
    trades: ['Foundations & concrete', 'Bricklaying', 'Groundworks'],
    description: 'Sequenced bay underpinning to deepen / strengthen existing foundations.',
    defaultHazards: ['Excavation', 'Manual handling', 'Plant / pedestrian interaction', 'Lone working', 'COSHH (chemicals)'],
    scope:
      'Excavate, shutter and pour or build up underpinning bays beneath existing foundations to deepen / strengthen — typically 1m bays in 1-3-5 / 2-4-6 sequence per engineer\'s schedule.',
    hazards: [
      { hazard: 'Building movement during underpinning', risk: 'High', controls: 'Engineer\'s sequence followed exactly — never adjacent bays open at same time; structural movement monitoring (telltales / levels) before, during, after each bay; STOP if movement detected.' },
      { hazard: 'Excavation collapse onto worker', risk: 'High', controls: 'Bay battered or supported; never enter unsupported >1.2 m; daily inspection by competent person; weekly recorded.' },
      { hazard: 'Persons working alone deep in trench', risk: 'High', controls: 'Pair work; never alone in any bay > 1.2 m; communication / phone with the operative.' },
      { hazard: 'Hidden services in bay area', risk: 'High', controls: 'Service drawings; CAT-scan; hand-dig last 300 mm; Permit-to-Dig for each bay.' },
      { hazard: 'Cement / concrete burns during pour', risk: 'Med', controls: 'Nitrile gloves; long sleeves; eye protection; barrier cream.' },
      { hazard: 'Pump line burst or skip overturn during pour', risk: 'Med', controls: 'Pump certified; clamps; banksman; trained pump operator.' },
    ],
    sequence: [
      'Engineer\'s drawings and schedule reviewed; survey marks set up.',
      'CAT-scan bay 1; Permit-to-Dig.',
      'Excavate bay 1 — shore as required; never enter unsupported above 1.2 m.',
      'Clean bottom; shutter where formwork required.',
      'Pour mass concrete OR lay brick / block to engineer\'s build-up; pack tight to existing footing top.',
      'Cure per spec before opening adjacent bays.',
      'Open bay 3 (skip 2); repeat.',
      'After 1-3-5 series complete, return for 2-4-6.',
      'Monitor structural movement throughout — record per shift.',
      'Inspect by engineer; sign off.',
    ],
    equipment: [
      'Excavator / hand digging tools',
      'Trench supports / shoring',
      'Concrete mixer or pump',
      'Formwork timbers',
      'Telltales / movement monitor gauges',
      'CAT-scan + Genny',
    ],
    ppe: [
      'Hard hat',
      'Safety boots with mid-sole and ankle support',
      'Hi-vis Class 3',
      'Cut-resistant gloves; nitrile under for cement',
      'Eye protection',
      'Hearing protection',
      'FFP3 mask in dust',
    ],
    emergency:
      'Building movement / cracking — STOP immediately, evacuate, contact engineer. Excavation collapse — DO NOT enter to rescue; dig from outside; 999. Service strike — STOP, isolate, 999 / 0800 111 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'stone_cladding',
    title: 'Stone cladding',
    trades: ['Bricklaying', 'Cladding'],
    description: 'Install thin or full-thickness stone cladding to walls — adhered or mechanically fixed.',
    defaultHazards: ['Manual handling', 'Working at height', 'COSHH (chemicals)', 'Silica dust', 'Slips / trips'],
    scope:
      'Install stone cladding to facade — thin engineered slip-stone (adhered) or full-thickness stone (mechanically fixed with cramps / brackets). Includes substrate prep, cladding, jointing, sealing.',
    hazards: [
      { hazard: 'Stone falling from facade if mechanical fixing fails', risk: 'High', controls: 'Engineer-designed bracket system; pull-test brackets in substrate before continuing; never use plug-and-screw on lightweight blockwork; full-thickness stone always mechanically fixed.' },
      { hazard: 'Manual handling of stone panels (heavy, awkward)', risk: 'High', controls: 'Two-person lift; suction handles; mechanical aid for full-height pieces; pre-position at platform.' },
      { hazard: 'Silica / RCS dust from cutting', risk: 'High', controls: 'Wet-cut saw outdoors only; FFP3 mask; on-tool extraction.' },
      { hazard: 'Working at height on full elevation', risk: 'High', controls: 'Scaffold tagged; harness for any unguarded; toe-boards; debris netting.' },
      { hazard: 'Adhesive / mortar burns and dermatitis', risk: 'Med', controls: 'Nitrile gloves under cut-resistant; immediate wash; eye protection.' },
      { hazard: 'Falling stone onto people below', risk: 'High', controls: 'Toe-boards; debris netting; no-go zone below; helmets mandatory below.' },
    ],
    sequence: [
      'Pre-start — substrate inspection; bracket pull-test if mechanical.',
      'Set out from datum line; mock-up first course.',
      'Adhered slip-stone — apply adhesive to back-buttered piece; press to wall; spacer joint.',
      'Mechanical full-thickness — install cramp / bracket; bolt / set in mortar; build up courses.',
      'Cut to abutments outdoors at the wet-saw.',
      'Joint with pointing mortar / sealant per spec.',
      'Final inspection; clean facade — never acid-clean limestone.',
    ],
    equipment: [
      'Wet bench saw',
      'Notched trowel for adhered system',
      'Cordless drill with diamond core for fixings',
      'Cramps / brackets per system',
      'Hawk and pointing trowel',
      'Suction handles for large pieces',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis',
      'Cut-resistant gloves; nitrile under for adhesive / mortar',
      'Eye protection',
      'FFP3 mask during cutting',
      'Hearing protection',
    ],
    emergency:
      'Stone falls from facade — clear area, 999 if injury, contact engineer. Cement / adhesive in eye — flush 15+ min, A&E. Cut to hand from saw — pressure, A&E. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'lightweight_block_partition',
    title: 'Lightweight blockwork partitions',
    trades: ['Bricklaying'],
    description: 'Internal aircrete (Celcon / Thermalite) blockwork partitions — non-load-bearing or light load.',
    defaultHazards: ['Manual handling', 'COSHH (chemicals)', 'Silica dust', 'Slips / trips'],
    scope:
      'Lay aircrete (Celcon / Thermalite) blockwork to internal partitions — non-load-bearing or light load. Lighter than dense concrete blocks but still requires correct ties and lintels.',
    hazards: [
      { hazard: 'Manual handling — aircrete blocks lighter but still 8–15 kg each, repetitive', risk: 'Med', controls: 'Rotate; mechanical aid for whole pallets; never overload arms.' },
      { hazard: 'Silica / aircrete dust during cutting', risk: 'High', controls: 'Wet-cut OR cut on a hand saw (aircrete cuts dry but use FFP3 minimum); on-tool extraction; HEPA vac for clean-down; never dry-sweep.' },
      { hazard: 'Cement / lime burns from thin-bed mortar / adhesive', risk: 'Med', controls: 'Nitrile gloves under cut-resistant; immediate wash.' },
      { hazard: 'Wall stability before mortar cure', risk: 'Med', controls: 'Brace tall partitions until cured; restraint to floor / ceiling per Building Regs Part A; no excessive height in single day.' },
      { hazard: 'Slips on offcut blocks and dust', risk: 'Med', controls: 'Continuous tidy; HEPA vac; offcut bin.' },
    ],
    sequence: [
      'Set out — sole DPC and floor track / kicker.',
      'Lay first course; check level and plumb.',
      'Continue with thin-bed mortar / adhesive per maker.',
      'Restraints / ties to existing structure at design centres.',
      'Lintels at openings.',
      'Cut at the bench with hand saw or wet saw; FFP3 worn.',
      'Strike joints; brush face.',
      'Brace tall partitions overnight.',
    ],
    equipment: [
      'Aircrete saw / hand saw',
      'Trowels for thin-bed adhesive',
      'Spirit level (long)',
      'Mixing paddle for thin-bed',
      'M-class extraction / HEPA vac',
    ],
    ppe: [
      'Hard hat (where site rules require)',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant grip gloves; nitrile under for mortar',
      'Eye protection',
      'FFP3 mask',
      'Hearing protection',
    ],
    emergency:
      'Mortar in eye — flush 15+ min, A&E. Wall topples — clear, 999 if injury. Cut from saw — pressure, A&E. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'fireplace_breast',
    title: 'Fireplace / chimney breast — install or removal',
    trades: ['Bricklaying', 'Carpentry & joinery'],
    description: 'Install or remove fireplace and / or chimney breast — gallows brackets, lintel, fire-stopping.',
    defaultHazards: ['Manual handling', 'Working at height', 'Silica dust', 'Asbestos', 'Hot works'],
    scope:
      'Install or remove fireplace and / or chimney breast — gallows brackets to support residual chimney, install / fit lintel, fire-stopping, hearth, fire surround. Removal requires structural sign-off.',
    hazards: [
      { hazard: 'Structural collapse of chimney during works', risk: 'High', controls: 'Engineer-signed gallows brackets BEFORE removing the chimney breast; never remove the breast without supporting the chimney above; survey existing flue and structure first.' },
      { hazard: 'Asbestos in flue lining / hearth (older properties)', risk: 'High', controls: 'Pre-1999 — assume potential AC; Refurb / Demolition Survey on file; do not break suspect material; if uncovered — STOP, contact licensed contractor.' },
      { hazard: 'Silica dust from breaking out brick / mortar', risk: 'High', controls: 'On-tool extraction; FFP3 mask; HEPA vac for clean-down; sheet down adjacent areas; never dry-sweep.' },
      { hazard: 'Manual handling of breast bricks / hearth', risk: 'High', controls: 'Two-person lifts; bagged debris in small loads; never over-load skip directly under upper-floor breast.' },
      { hazard: 'Live flue carrying combustion gases (if attached to active fire)', risk: 'High', controls: 'Confirm fire / boiler isolated; flue capped / decommissioned by Gas Safe before any breast removal; never breach a live flue.' },
      { hazard: 'Working at height through ceiling / upper levels', risk: 'High', controls: 'Tower; harness for unguarded; never balanced on the breast itself.' },
    ],
    sequence: [
      'Pre-start — structural engineer\'s gallows-bracket design; AC survey; Gas Safe sign-off.',
      'Sheet down room; cover finished surfaces.',
      'Install gallows brackets to engineer\'s drawings.',
      'Strip out fireplace, hearth and surround (controlled).',
      'Strike out breast progressively from top to bottom; bag debris.',
      'Lintel installed at top of opening if breast partially retained.',
      'Make good wall — patch plaster, fire-stopping at floor / ceiling levels (Part B).',
      'Final inspection by building control / engineer.',
    ],
    equipment: [
      'Mortar raker / breaker / SDS drill',
      'On-tool extraction (M-class)',
      'HEPA vacuum',
      'Sheets and floor protection',
      'Tower / scaffold',
      'Sash cramps, props',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves',
      'Eye protection',
      'FFP3 mask MANDATORY',
      'Disposable overalls Type 5/6 if AC suspected',
      'Hearing protection',
    ],
    emergency:
      'Suspected AC uncovered — STOP, isolate, contact site manager / licensed contractor. Structural movement / partial chimney collapse — clear, 999, contact engineer. Cut from breaker — pressure, A&E. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'brick_repair',
    title: 'Brick repair & replacement',
    trades: ['Bricklaying'],
    description: 'Replace damaged bricks; cut out and rebuild localised areas; resin / helibar crack repair.',
    defaultHazards: ['Working at height', 'Silica dust', 'Manual handling', 'COSHH (chemicals)'],
    scope:
      'Repair localised brick damage — cut out individual bricks and replace with matching units; resin / helibar repair to cracked walls; toothed-out rebuild for larger damage.',
    hazards: [
      { hazard: 'Silica dust from cutting / breaking bricks', risk: 'High', controls: 'On-tool extraction; FFP3 mask; HEPA vac; never dry-sweep dust.' },
      { hazard: 'Working at height on scaffold or tower', risk: 'High', controls: 'Tagged; toe-boards, double guard-rail; harness for unguarded.' },
      { hazard: 'Wall stability whilst bricks removed', risk: 'High', controls: 'Engineer-signed for >2-3 brick rebuild; props / strongbacks where structurally significant; never strip a large area without engineer sign-off.' },
      { hazard: 'Cement / lime burns', risk: 'Med', controls: 'Nitrile gloves; immediate wash.' },
      { hazard: 'Resin epoxy fume (helibar repair)', risk: 'Med', controls: 'Ventilate; A1P3 cartridge mask; nitrile gloves; eye protection.' },
      { hazard: 'Falling debris below', risk: 'Med', controls: 'Sheeting; no-go zone; helmets mandatory below.' },
    ],
    sequence: [
      'Survey damage; identify scope (single brick, area, structural crack).',
      'Source matching bricks (colour, texture, frog / no-frog).',
      'Cut out failed bricks with grinder + extraction.',
      'Clean joints; brush out; dampen.',
      'Lay replacement bricks with matching mortar.',
      'For helibar repair — rake out joint, drill holes, inject resin, place bar, repoint.',
      'Final point and brush off.',
      'Cover lime work with hessian.',
    ],
    equipment: [
      'Angle grinder with diamond blade + extraction',
      'On-tool M-class extractor',
      'HEPA vacuum',
      'Pointing trowels',
      'Drill / SDS for helibar holes',
      'Resin / helibar kit',
      'Mortar bench',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis',
      'Cut-resistant gloves; nitrile under for mortar / resin',
      'Eye protection',
      'FFP3 mask MANDATORY',
      'A1P3 cartridge mask if resin work',
      'Hearing protection',
    ],
    emergency:
      'Cement / resin in eye — flush 15+ min, A&E. Wall partial collapse during works — clear, 999, contact engineer. Cut from grinder — pressure, A&E. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'gas_install',
    title: 'Gas install / connection (Gas Safe)',
    trades: ['Plumbing'],
    description: 'Gas pipework, boiler / hob / fire connection, tightness test, commissioning. Gas Safe registered engineer only.',
    defaultHazards: ['Hot works', 'COSHH (chemicals)', 'Confined spaces', 'Manual handling'],
    scope:
      'Install or modify domestic gas pipework, terminate to boiler / hob / fire / cooker, perform tightness test and commissioning to GSIUR. Gas Safe registered engineer only.',
    hazards: [
      { hazard: 'Gas leak — explosion / asphyxiation', risk: 'High', controls: 'Tightness test on every joint per IGEM/UP/1B; never sign off without recorded test; CO alarm fitted; emergency isolation valve at appliance; never work on a live gas system without isolating first.' },
      { hazard: 'Fitter not Gas Safe registered', risk: 'High', controls: 'Gas Safe ID card checked on every gas fitter; only Gas Safe registered engineers carry out gas work — no exceptions; subbie carded for the specific work category.' },
      { hazard: 'CO from incomplete combustion (boiler / fire)', risk: 'High', controls: 'Flue gas analyser (calibrated) on every commission; CO₂/CO ratio per appliance manufacturer; flue route checked for terminations and clearances; CO alarm in occupied dwellings.' },
      { hazard: 'Hot works for capillary / brazed joints', risk: 'High', controls: 'Hot Works Permit if soldering near combustibles; CO₂ extinguisher; fire watch; combustibles cleared 1 m; never solder near insulation without protection.' },
      { hazard: 'Working in confined / poorly ventilated airing cupboards', risk: 'Med', controls: 'Ventilate; CO monitor on operative; pair work; phone on operator.' },
      { hazard: 'Old fittings / lead solder on existing pipework', risk: 'Med', controls: 'Replace / upgrade where in scope; never use lead solder on potable water; check existing for asbestos rope on old fires.' },
    ],
    sequence: [
      'Gas Safe ID and category check.',
      'Isolate gas at the ECV; lock off; warning sign at meter.',
      'Lay pipework — copper / TracPipe / steel per spec; concealed runs in safe zones; sleeves through walls / floors.',
      'Terminate at appliance per maker.',
      'Tightness test the new section; record on test certificate.',
      'Energise gas; let down; tightness test the whole system.',
      'Commission appliance — flue analysis recorded on commissioning sheet.',
      'CO alarm fitted in same room or adjacent.',
      'Issue Building Regs (Gas Safe) compliance certificate; copy to client.',
    ],
    equipment: [
      'Gas Safe registered engineer',
      'Manometer (calibrated)',
      'Flue gas analyser (calibrated, in date)',
      'Pipe-press / brazing kit',
      'Hot works kit (where brazing)',
      'CO meter on operative',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis (where site rules require)',
      'Cut-resistant gloves; nitrile under for compounds',
      'Eye protection',
      'A1P3 cartridge mask if working near solvents / sealants',
    ],
    emergency:
      'Gas leak — close ECV, ventilate, NO ignition sources, evacuate, call National Gas Emergency 0800 111 999. CO alarm activates / suspected — evacuate, fresh air, 999. Hot works fire — fight if confined (CO₂); otherwise 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'ev_charger_install',
    title: 'EV charger install (domestic)',
    trades: ['Electrical'],
    description: 'Domestic 7 kW or 22 kW EV charger install — supply upgrade if required, mounting, commissioning.',
    defaultHazards: ['Electrical', 'Working at height', 'Manual handling'],
    scope:
      'Install domestic EV charger — assess existing supply for capacity, run dedicated circuit, install Type-A or Type-B RCBO, mount unit, commission per OZEV grant requirements (if claiming).',
    hazards: [
      { hazard: 'Live circuit work at consumer unit', risk: 'High', controls: 'Isolate at supply origin where possible; lock-out and tag; voltage tester (proven dead-live-dead); never work alone on energised CU; insulated tools; arc-rated clothing during energised work.' },
      { hazard: 'Drilling through external wall — hidden services', risk: 'High', controls: 'CAT-detect both sides; check service drawings; never drill blind through cavity wall; survey for asbestos board on older pre-2000 properties.' },
      { hazard: 'Manual handling of charger unit and cable drums', risk: 'Med', controls: 'Charger lifted onto pre-installed bracket — two-person; cable drum on stand.' },
      { hazard: 'Working at height (charger mounted >2 m)', risk: 'Med', controls: 'Tower / podium; never the top step of a stepladder.' },
      { hazard: 'Wrong RCD type — DC fault current blinding', risk: 'High', controls: 'Type-A RCBO with DC detection (or dedicated Type-B) per BS 7671; verify charger has internal DC protection if Type-A used; recorded on EIC.' },
      { hazard: 'Outdoor cable rated incorrectly for the run', risk: 'Med', controls: 'Cable sized for distance and load (volt-drop calc); SWA / armoured for buried; sleeves at penetrations.' },
    ],
    sequence: [
      'Pre-survey — existing supply capacity, MPAN, demand calc; load study to DNO if upgrade needed.',
      'Isolate at origin; lock and tag.',
      'Mount charger bracket; CAT-detect penetrations.',
      'Run dedicated cable from CU to charger; SWA / armoured if external buried.',
      'Install Type-A or Type-B RCBO in CU per charger spec.',
      'Terminate at charger; bond to earth.',
      'Energise; commission per maker — pair to home network if smart-charging.',
      'Test — Zs, IR, RCD operation, charge cycle.',
      'Issue EIC and notify building control / register with OZEV if grant.',
    ],
    equipment: [
      'MFT (calibrated)',
      'Cable detector / CAT',
      'Insulated hand tools',
      'Cordless drill, hammer drill',
      'Tower / podium step',
      'Lock-off kit',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves',
      'Eye protection',
      'Insulated gloves rated for voltage during energised work',
      'Arc-rated clothing during CU work',
    ],
    emergency:
      'Electric shock — isolate, do not touch casualty until isolated, 999, CPR if needed. Cable strike to gas / water — isolate supply, 999 / 0800 111 999 (gas). Drill through hidden circuit — STOP, isolate, repair before energising. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'solar_pv',
    title: 'Solar PV install — rooftop',
    trades: ['Roofing', 'Electrical'],
    description: 'Rooftop solar PV array — mounting rails, panels, inverter, isolators, DC and AC commissioning.',
    defaultHazards: ['Working at height', 'Electrical', 'Manual handling', 'Wet weather'],
    scope:
      'Install rooftop PV array — mounting hooks / rails, panels, DC stringing to inverter, AC connection to consumer unit, MCS-compliant commissioning. MCS-registered installer for any FiT / SEG eligibility.',
    hazards: [
      { hazard: 'Falls from roof edge', risk: 'High', controls: 'Scaffold edge protection at gutter line tagged; harness for any unguarded; never work in wind >25 mph; pair work always.' },
      { hazard: 'Live DC at panel — even with grid isolated, panels generate when light hits them', risk: 'High', controls: 'Cover panels with opaque cloth before stringing; DC isolators at panel and inverter; never break a live DC string under load — isolate DC isolator first; arc rated kit during DC connect.' },
      { hazard: 'Manual handling of panels (each ~20 kg, awkward) and rails', risk: 'High', controls: 'Two-person lift up scaffold; hoist for batches; never throw or hand up scaffold individually.' },
      { hazard: 'Roof penetration leaks through hooks', risk: 'Med', controls: 'Hooks installed under tile / slate per maker; rubberised washer / lead apron; never bridge a tile.' },
      { hazard: 'Inverter installation (heavy)', risk: 'Med', controls: 'Two-person to mount; bracket pre-fixed; never attempt single lift.' },
      { hazard: 'Cable strain on DC and AC cables in roof void', risk: 'Med', controls: 'Cable trays / clips per maker; UV-rated cable for any sun exposure; routed away from sharp edges.' },
    ],
    sequence: [
      'Pre-survey — roof condition, structural capacity, shading, system design (MCS).',
      'Confirm scaffold edge protection in place.',
      'Set out hook positions; lift tiles / slates; install hooks under battens.',
      'Make tile / slate good around hooks; lead apron / weatherproof per maker.',
      'Install rails to hooks; verify level.',
      'Mount panels onto rails; DC string to spec; cover panels until ready to commission.',
      'Run DC cables to inverter location; run AC cables to CU.',
      'Install inverter; isolators (DC + AC); bond earthing.',
      'Energise — DC isolator on, then AC; verify inverter handshakes with grid.',
      'Test — voltage, current, MPPT; record on MCS commissioning cert.',
      'Issue cert and DNO notification (G98 / G99) where required.',
    ],
    equipment: [
      'PV-rated screwdrivers and crimps',
      'MFT (with PV functions ideal)',
      'DC clamp meter',
      'Mobile hoist for panels',
      'Insulated tools',
      'Opaque covers for panels',
      'Lock-off kit',
    ],
    ppe: [
      'Hard hat with chin-strap',
      'Safety boots',
      'Hi-vis Class 3',
      'Harness if unguarded',
      'Insulated gloves rated DC',
      'Eye protection',
      'Arc-rated kit during DC connection',
    ],
    emergency:
      'Fall from roof — 999, do not move. DC arc / electric shock — DO NOT touch casualty until isolated; 999. Wind exceeds 25 mph — STOP, secure panels, descend. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'ashp_install',
    title: 'Air Source Heat Pump install',
    trades: ['Plumbing', 'Electrical'],
    description: 'External ASHP unit, internal cylinder, pipework, electrical commissioning. MCS-compliant.',
    defaultHazards: ['Manual handling', 'Electrical', 'COSHH (chemicals)', 'Working at height'],
    scope:
      'Install Air Source Heat Pump (typically 5–16 kW domestic) — external unit on slab / wall, internal hot water cylinder, hydraulic pipework, electrical and refrigerant connection (refrigerant by F-Gas certified). MCS-registered for grant eligibility.',
    hazards: [
      { hazard: 'Refrigerant exposure / asphyxiation in confined space', risk: 'High', controls: 'F-Gas certified engineer for any refrigerant work; pre-charged units preferred; never break a charged line indoors without ventilation; F-Gas register record kept.' },
      { hazard: 'Manual handling of external unit (often 80–120 kg)', risk: 'High', controls: 'Two-person minimum, three for larger; mechanical aid (sack truck / hoist) for upper-floor or wall-mounted; planned route; secure to slab / bracket.' },
      { hazard: 'Manual handling of internal cylinder (large unvented)', risk: 'High', controls: 'Two-person lift; stair trolley; G3 unvented certificate engineer for pressurised systems.' },
      { hazard: 'Live electrical connection — high amp', risk: 'High', controls: 'Isolate at supply; lock and tag; insulated tools; voltage tester; competent electrician only; high-amp circuit (often 32A) — RCBO and correct cable sizing.' },
      { hazard: 'Hydraulic / glycol exposure during fill', risk: 'Med', controls: 'Nitrile gloves; eye protection; SDS on file for glycol; ventilate; barrier cream.' },
      { hazard: 'Working at height (wall-mounted external unit)', risk: 'Med', controls: 'Tower or scaffold; never single-lift onto bracket from a stepladder.' },
    ],
    sequence: [
      'Pre-start — system design (MCS), heat-loss calc, electrical capacity.',
      'External slab / wall bracket prepared.',
      'Position external unit (mechanical lift); secure to slab / bracket; level.',
      'Internal cylinder positioned; G3 certificate fitter for unvented.',
      'Hydraulic pipework — flow / return; manifolds; expansion vessel; pressure-test.',
      'Refrigerant line connection by F-Gas engineer.',
      'Electrical supply by competent electrician; bonding and earthing per Part P.',
      'Fill, dose, vent system; commission per maker.',
      'Issue MCS commissioning certificate and Building Regs notification.',
    ],
    equipment: [
      'Mechanical lift / sack truck',
      'F-Gas certified engineer for refrigerant',
      'G3 certificate engineer for unvented cylinder',
      'Pressure pump and gauge',
      'MFT (calibrated)',
      'Fault-finding refrigerant leak detector (if breaking lines)',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis (where site rules require)',
      'Cut-resistant gloves; nitrile under for chemicals',
      'Eye protection',
      'A1P3 cartridge mask for refrigerant work in any confined area',
    ],
    emergency:
      'Refrigerant leak in confined space — evacuate, ventilate, 999 if symptomatic. Electric shock — isolate, do not touch, 999. Cylinder pressure failure / scalding — isolate at the cold feed, 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'mvhr_install',
    title: 'MVHR install',
    trades: ['Plumbing', 'Electrical'],
    description: 'Mechanical Ventilation with Heat Recovery — central unit, ducting throughout, terminals, commissioning.',
    defaultHazards: ['Working at height', 'Manual handling', 'Loud noise (>85 dB)', 'Hand-arm vibration (HAVS)'],
    scope:
      'Install MVHR system — central heat exchanger unit, semi-rigid or rigid ducting to all wet rooms (extract) and habitable rooms (supply), valves, attenuators, commissioning to design air flows.',
    hazards: [
      { hazard: 'Working at height in lofts and ceiling voids', risk: 'High', controls: 'Crawl boards in lofts; tower for high ceilings; harness for any unguarded edge; lighting in loft.' },
      { hazard: 'Manual handling of long duct lengths and the MVHR unit', risk: 'Med', controls: 'Two-person carry of duct lengths; mechanical lift for the unit; pre-cut and label runs at the bench.' },
      { hazard: 'Drilling through structural elements / hidden services', risk: 'High', controls: 'Service drawings; CAT-detect; never cut into truss / engineered timber without engineer\'s sign-off; hole-saw with extraction.' },
      { hazard: 'Acoustic foam / glass-fibre fibres', risk: 'Med', controls: 'Disposable overalls Type 5 with hood; FFP3 mask; nitrile under work gloves.' },
      { hazard: 'Working in tight loft spaces — overheating', risk: 'Med', controls: 'Pair work; phone on operator; hydration; loft hatch open for ventilation.' },
      { hazard: 'Cable / electrical connection of unit', risk: 'Med', controls: 'Competent electrician; isolate at CU; spurred dedicated supply.' },
    ],
    sequence: [
      'Pre-start — design (MVHR specifier); duct layout.',
      'Position central unit on bracket / hangers; vibration mounts.',
      'Run main ducts to/from unit (supply and extract).',
      'Branch to terminals — bathroom / kitchen extracts; bedroom / lounge supplies.',
      'Install attenuators on supply and extract.',
      'Terminals fixed in ceilings; sealed with mastic.',
      'External terminations — separate supply intake (front) and extract exhaust (rear); insect mesh.',
      'Electrical connection by competent electrician.',
      'Commission to design flows (anemometer); record.',
      'Issue commissioning certificate.',
    ],
    equipment: [
      'Anemometer (calibrated) for commissioning',
      'Cordless drill / impact, hole saws',
      'Aviation snips for rigid duct',
      'Sealant gun',
      'Cable detector',
      'Ladder / tower / podium step',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis vest',
      'Type 5 overalls in lofts',
      'Cut-resistant gloves',
      'Eye protection',
      'FFP3 mask in loft / fibre-rich areas',
    ],
    emergency:
      'Fall from loft hatch / ladder — 999. Severe overheating — descend, hydrate, cool down, 999 if symptoms persist. Cut from snips — pressure, A&E. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'sprinkler_install',
    title: 'Domestic sprinkler install',
    trades: ['Plumbing'],
    description: 'BS 9251 domestic / residential sprinkler install — supply, pipework, heads, commissioning.',
    defaultHazards: ['Working at height', 'Manual handling', 'Hot works', 'COSHH (chemicals)'],
    scope:
      'Install domestic sprinkler system per BS 9251 Cat 1 / 2 — typically pressurised mains supply OR pump and tank, distribution pipework concealed in voids / chases, recessed pendant heads in habitable rooms, commissioning and pressure-test.',
    hazards: [
      { hazard: 'Pipe burst during pressure test causing water damage', risk: 'High', controls: 'Pressure test in stages; visible test gauge; isolate adjacent finished areas; sheet down before energising; never test full system without supports verified.' },
      { hazard: 'Working at height to install heads in ceilings', risk: 'High', controls: 'Tower / podium; never the top step of a stepladder; harness for unguarded.' },
      { hazard: 'Hot works for soldered joints', risk: 'Med', controls: 'Hot Works Permit; combustibles cleared; CO₂ extinguisher; fire watch.' },
      { hazard: 'Heads damaged during build-out', risk: 'High', controls: 'Heads installed LAST — protected with covers; never paint a head; commissioning verifies head visibility / alignment.' },
      { hazard: 'Manual handling of pump / tank if Cat 2', risk: 'Med', controls: 'Two-person lift; mechanical aid; secured to slab.' },
      { hazard: 'Frost damage in unheated areas', risk: 'Med', controls: 'Pipework routed within heated envelope; heat-traced or anti-freeze loops where unavoidable; insulation per spec.' },
    ],
    sequence: [
      'Pre-start — design per BS 9251 by competent specifier.',
      'Confirm supply — mains pressure / flow test OR pump and tank.',
      'Run distribution pipework concealed; supports per spec; sleeves at structural penetrations.',
      'Install heads to ceiling at design positions; protected with covers during fit-out.',
      'Pressure-test the system; record.',
      'Commission flow / pressure on each branch.',
      'Issue BS 9251 commissioning certificate to client / building control.',
      'Annual service / inspection schedule handed over.',
    ],
    equipment: [
      'Pipe-press / soldering kit',
      'Pressure pump and gauge',
      'Hot works kit (where soldering)',
      'Tower / podium step',
      'BS 9251 commissioning sheet',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves',
      'Eye protection',
      'FFP3 mask if cutting into ceilings',
    ],
    emergency:
      'Pipe burst / major leak — isolate at stop tap, contain. Hot works fire — fight if confined (CO₂); otherwise 999. Sprinkler head accidentally activated — isolate immediately; never re-set a discharged head — replace. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'lift_install',
    title: 'Domestic lift / through-floor lift install',
    trades: ['Carpentry & joinery', 'Electrical'],
    description: 'Through-floor or platform lift install — structural opening, lift install, electrical commissioning, LOLER.',
    defaultHazards: ['Working at height', 'Manual handling', 'Plant / pedestrian interaction', 'Electrical'],
    scope:
      'Install domestic through-floor lift, platform stairlift or vertical platform lift per maker — structural opening, lift carcass, drive unit, electrical, gates / doors, commissioning. LOLER / Supply of Machinery Safety Regs apply.',
    hazards: [
      { hazard: 'Falls into the unguarded shaft during install', risk: 'High', controls: 'Handrails / boarding across opening BEFORE breaking out floor; harness for any work over open shaft; opening boarded at end of every shift.' },
      { hazard: 'Lift unit / drive falling during install', risk: 'High', controls: 'Mechanical hoist / banksman for lift carcass; certified slings; never single-lift; props beneath until secured.' },
      { hazard: 'Cutting through structural floor', risk: 'High', controls: 'Engineer-signed trimmer joists installed BEFORE removing existing structure; never cut without trimmer.' },
      { hazard: 'Live electrical connection — drive supply', risk: 'High', controls: 'Isolate at CU; lock and tag; insulated tools; voltage tester; competent electrician.' },
      { hazard: 'Trapping fingers / hands in moving parts during commissioning', risk: 'High', controls: 'Mechanical lock-out during install; never adjust any moving part with power on; emergency-stop tested before any motion run.' },
      { hazard: 'Manual handling of lift carcass and rails', risk: 'High', controls: 'Two-person minimum; mechanical aid for sections; pre-position staging.' },
    ],
    sequence: [
      'Pre-start — manufacturer\'s install drawings; engineer\'s structural opening detail.',
      'Install trimmer joists; verify structurally complete.',
      'Erect handrails / barrier across opening.',
      'Cut floor opening; never leave open at end of shift without boarding.',
      'Erect lift carcass / rails; bolt to structural points per maker.',
      'Install drive unit; cable in.',
      'Electrical connection by competent electrician; isolators present.',
      'Mechanical commissioning — limits, brakes, emergency stop, alarm.',
      'Test under load to maker spec; LOLER certificate (where required).',
      'Hand over to user; issue maintenance schedule.',
    ],
    equipment: [
      'Manufacturer\'s install kit',
      'Mechanical hoist for carcass',
      'MFT for electrical commissioning',
      'Cordless impact, torque wrench',
      'Spirit level (long), plumb bob',
      'Lock-off kit',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves',
      'Eye protection',
      'Harness for unguarded shaft',
      'Hearing protection during cutting',
    ],
    emergency:
      'Fall into shaft — 999, do not move. Lift drops during install — clear, 999. Electric shock — isolate, 999. Trapped finger / hand — emergency stop, 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'asbestos_nnlw',
    title: 'Asbestos — non-licensed work (NNLW)',
    trades: ['Demolition'],
    description: 'Non-licensed asbestos removal — short-duration low-risk work on bonded ACMs (e.g. AC sheet, vinyl floor tiles).',
    defaultHazards: ['Asbestos', 'Manual handling', 'COSHH (chemicals)', 'Working at height'],
    scope:
      'Carry out non-licensed asbestos work (NNLW) on bonded asbestos-containing materials (asbestos cement sheet, AC pipe, vinyl floor tiles, textured coating). For any licensable work — STOP and use a licensed contractor.',
    hazards: [
      { hazard: 'Asbestos fibre release', risk: 'High', controls: 'Wet method MANDATORY — water suppression on all cuts / breaks; no power tools (hand tools only); FFP3 minimum, P3 cartridge mask preferred; full Type 5 disposable suit with hood; double-bag waste in red labelled asbestos bags; HEPA vac for clean-down ONLY.' },
      { hazard: 'Operative not trained for asbestos work', risk: 'High', controls: 'Category B training (UKATA / IATP) within last 12 months MANDATORY; NNLW notified to HSE if it falls within Notifiable NNLW criteria.' },
      { hazard: 'Public / other trades exposed during the work', risk: 'High', controls: 'Cordon area; signage; seal off the room with poly sheeting; NEVER allow anyone in without RPE; clear-up must HEPA vac and inspect before opening cordon.' },
      { hazard: 'Manual handling of AC sheets without breaking', risk: 'Med', controls: 'Two-person lift; never break or drop; lower controlled.' },
      { hazard: 'Working at height for soffits / roofing', risk: 'High', controls: 'Tower or scaffold; never break or grind to remove fixings — un-screw and lower whole.' },
      { hazard: 'Air monitoring / clearance certificate not obtained', risk: 'High', controls: 'Air monitoring + clearance air test by UKAS-accredited analyst on completion of any room / area; never reoccupy without clearance certificate.' },
    ],
    sequence: [
      'Refurb / Demolition Survey on file; confirm material is NNLW (not licensable).',
      'Notify HSE if Notifiable NNLW (>1 hr per person per shift involving textured coating breakage etc.).',
      'Cordon and sheet area; signage.',
      'Operatives — Type 5 suits, hoods, P3 mask, gloves, boots.',
      'Wet method — wet suppression on all cuts / fixings.',
      'Hand tools only — no powered breaking, no grinding.',
      'Un-screw and lower whole — never break to remove.',
      'Double-bag waste; label asbestos.',
      'HEPA vac all surfaces; inspect.',
      'Air monitoring / clearance certificate from UKAS analyst.',
      'Disposable suits and used masks bagged as asbestos waste.',
      'Operatives — decon as you exit; never wear suit outside the cordon.',
    ],
    equipment: [
      'Type 5 disposable suits, hoods, gloves, overshoes',
      'P3 cartridge masks (face-fit tested)',
      'Garden sprayer for water suppression',
      'Hand tools only — never powered',
      'Red asbestos bags + labels',
      'HEPA vacuum (Class H)',
    ],
    ppe: [
      'Type 5 disposable suit with hood',
      'P3 cartridge mask (face-fit tested annually)',
      'Disposable nitrile gloves over cut-resistant',
      'Wellies / disposable overshoes',
      'Eye protection',
    ],
    emergency:
      'Suspected fibre release outside the cordon — STOP, evacuate, contact UKATA contractor. Suit / mask compromised — exit cordon immediately, decon, replace, do not re-enter without face-fit RPE. Suspected exposure — bag clothing as asbestos waste, occupational health referral. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'confined_space',
    title: 'Confined space entry',
    trades: ['Groundworks', 'Plumbing'],
    description: 'Generic confined-space entry RAMS — chambers, tanks, voids. Permit-driven.',
    defaultHazards: ['Confined spaces', 'COSHH (chemicals)', 'Lone working', 'Slips / trips'],
    scope:
      'Generic RAMS for entry to confined spaces — manholes, drainage chambers, tanks, attics, voids. Permit-to-Enter system. To be used alongside the trade-specific RAMS for the work being done inside.',
    hazards: [
      { hazard: 'Lack of oxygen, toxic gas, or flammable atmosphere', risk: 'High', controls: 'Atmospheric monitoring (4-gas: O₂, LEL, CO, H₂S) BEFORE entry, continuously during; entry only if O₂ 19.5–23.5%, LEL <10%, CO <50 ppm, H₂S <10 ppm; never enter a permit space without monitoring.' },
      { hazard: 'Operative collapse inside — rescue without proper equipment', risk: 'High', controls: 'Rescue plan briefed before entry; tripod and harness for any vertical entry; rescuer at the surface in radio contact; NEVER enter to rescue without RPE — many incidents end with multiple casualties.' },
      { hazard: 'Trapped — engulfment from flowing material', risk: 'High', controls: 'Isolate any inflows (water, sewage); lock-out and tag; verify isolation by zero-flow check.' },
      { hazard: 'Permit-to-Enter not signed or out of date', risk: 'High', controls: 'Permit issued by competent person; signed by entrant and rescuer; valid for the shift only; closed on exit.' },
      { hazard: 'Lone working in chamber', risk: 'High', controls: 'NEVER alone; minimum 2 in confined space, 1 at surface as rescuer.' },
      { hazard: 'Slips on wet / debris-strewn surface', risk: 'Med', controls: 'Lighting; non-slip footwear; ladder secured.' },
    ],
    sequence: [
      'Pre-start — Permit-to-Enter issued; rescue plan briefed; gas monitor calibrated.',
      'Isolate inflows; lock and tag.',
      'Atmospheric monitoring at entry; record readings.',
      'Operative dons harness, attaches lifeline, enters.',
      'Continuous monitoring during occupation; rescuer in radio contact.',
      'Carry out the work (per the trade-specific RAMS).',
      'Exit; disconnect; close permit.',
      'Restore inflows after work checked.',
    ],
    equipment: [
      '4-gas monitor (calibrated)',
      'Tripod / davit, winch, harness',
      'Two-way radios',
      'Lifeline / rescue harness',
      'Lighting (intrinsically safe if flammable risk)',
      'Lock-off kit for inflows',
      'Permit-to-Enter document',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis Class 3',
      'Harness / lifeline',
      'Eye protection',
      'RPE per the atmosphere — escape set always available',
    ],
    emergency:
      'Operative collapse in space — DO NOT ENTER without RPE; raise via lifeline / tripod from outside; 999. Atmosphere alarm — exit immediately; close permit; investigate before next entry. Engulfment — clear, 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'deep_excavation',
    title: 'Deep excavation (>2m)',
    trades: ['Excavation', 'Groundworks'],
    description: 'Excavation deeper than 2m — basement, large foundation, deep service trench. Engineered shoring required.',
    defaultHazards: ['Excavation', 'Plant / pedestrian interaction', 'Manual handling', 'Confined spaces'],
    scope:
      'Excavate deeper than 2 m for basements, large foundations or deep service trenches — engineered shoring (sheet pile, contiguous pile, soldier pile, trench box, battered) per ground engineer.',
    hazards: [
      { hazard: 'Excavation collapse onto worker', risk: 'High', controls: 'Engineer-signed shoring system; daily inspection BEFORE entry by competent person; weekly recorded; never enter unsupported above 1.2 m, fully shored above 2 m; check after rain.' },
      { hazard: 'Plant operating near edge — collapse / fall', risk: 'High', controls: 'Excavator on stable ground; spoil heap >1 m from edge; no plant within 1 m of unsupported edge.' },
      { hazard: 'Persons / vehicles falling into excavation', risk: 'High', controls: 'Pedestrian barriers + lighting at all open excavations; vehicle barriers; access steps cut; never leave unattended without barriers.' },
      { hazard: 'Hidden services in dig area', risk: 'High', controls: 'Service drawings; CAT-scan; trial pit for unverified runs; Permit-to-Dig.' },
      { hazard: 'Confined-space conditions in deep dig (low O₂, gases)', risk: 'High', controls: 'Atmospheric monitoring on any deep dig with potential for gas accumulation; rescue plan; tripod / harness.' },
      { hazard: 'Water in excavation', risk: 'High', controls: 'Sump and pump; dewatering before entry; never enter standing water without dewater; consider sides destabilised if water is present.' },
    ],
    sequence: [
      'Pre-start — engineer\'s shoring design; ground survey; service plan; Permit-to-Dig.',
      'Set out; CAT-scan.',
      'Excavate in stages; install shoring as you go (e.g. trench box dropped in, sheet pile driven first then dig).',
      'Daily inspection by competent person before entry.',
      'Atmospheric monitoring on any deep dig before entry.',
      'Pedestrian / vehicle barriers around open dig at all times.',
      'Spoil heap controlled and >1 m from edge.',
      'Access — ladder / steps with handrails.',
      'Carry out work in dig (per the trade-specific RAMS).',
      'Backfill in compacted layers per engineer; remove shoring sequentially as backfill rises.',
    ],
    equipment: [
      'Excavator with operator',
      'Shoring system per engineer (trench box, sheet pile etc.)',
      'CAT-scan + Genny',
      'Atmospheric monitor',
      '4-gas monitor for confined-space conditions',
      'Tripod / harness if rescue plan requires',
      'Sump pump',
    ],
    ppe: [
      'Hard hat',
      'Safety boots with mid-sole and ankle support',
      'Hi-vis Class 3',
      'Harness if rescue plan requires',
      'Cut-resistant gloves',
      'Eye protection',
      'Hearing protection',
    ],
    emergency:
      'Excavation collapse — DO NOT enter to rescue; dig from outside while emergency services arrive (999). Service strike — STOP, isolate, ventilate if gas, 999 / 0800 111 999. Atmosphere alarm — exit, investigate. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'bathroom_install',
    title: 'Bathroom install',
    trades: ['Plumbing', 'Tiling'],
    description: 'Full bathroom install — sanitaryware, taps, tiling, sealing, electrical IP-rated.',
    defaultHazards: ['Manual handling', 'COSHH (chemicals)', 'Silica dust', 'Slips / trips'],
    scope:
      'Full bathroom install — strip out (separate RAMS); 1st & 2nd fix plumbing; tiling; sanitaryware; commission; electrical IP-rated fittings (extractor fan, lights, shaver socket).',
    hazards: [
      { hazard: 'Cement / silicone / chemical exposure', risk: 'Med', controls: 'Nitrile gloves; ventilation when using solvents; eye protection during overhead silicone; SDS on file.' },
      { hazard: 'Silica dust during tile / cement-board cutting', risk: 'High', controls: 'Wet cutting; FFP3 mask; on-tool extraction.' },
      { hazard: 'Manual handling of bath, sanitaryware, large tiles', risk: 'High', controls: 'Two-person lifts; suction handles for large tiles; stair trolley; route checked first.' },
      { hazard: 'Electrical work in wet zones', risk: 'High', controls: 'IP-rated fittings per zone (Zone 0 / 1 / 2); RCD on all bathroom circuits; bonding to earth verified; competent electrician.' },
      { hazard: 'Slips on wet floors during install', risk: 'Med', controls: 'Drips controlled; wet-floor signs; clean-as-you-go.' },
      { hazard: 'Pressure / scalding during commissioning', risk: 'Med', controls: 'TMV at every blended outlet (per Part G); outlet temperature checked at commissioning.' },
    ],
    sequence: [
      'Confirm strip-out / 1st fix complete; substrate ready.',
      'Tank / waterproof wet-zone areas (tanking membrane / cement board).',
      'Floor tile first — laid to falls in shower areas.',
      'Wall tile from datum upwards.',
      'Install bath / shower tray; sealed to floor / wall.',
      'Install WCs, basins, vanities; isolation valves at every appliance.',
      'Terminate taps, mixers, showers; flow / temperature check.',
      'Electrical accessories — extractor fan, IP-rated lights; commissioned by competent electrician.',
      'Silicone seal to perimeters; tooled.',
      'Final commission — TMV temperatures, drainage, leak check.',
    ],
    equipment: [
      'Wet bench saw for tiles',
      'Mixing paddle and adhesive trowels',
      'Pipe-press / push-fit tools',
      'Stair trolley for sanitaryware',
      'Silicone gun and finisher',
      'MFT for electrical commissioning',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis (where site rules require)',
      'Cut-resistant gloves; nitrile under for adhesive',
      'Eye protection',
      'FFP3 mask during cutting',
      'Knee pads',
    ],
    emergency:
      'Cement / adhesive in eye — flush 15+ min, A&E. Bath dropped on foot — assess, A&E if any deformity. Electric shock — isolate, 999. Major leak — isolate stop tap, contain. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'underfloor_heating',
    title: 'Underfloor heating install',
    trades: ['Plumbing'],
    description: 'Wet underfloor heating — manifold, pipework, thermostats, pressure-test before screed.',
    defaultHazards: ['Manual handling', 'Working at height', 'COSHH (chemicals)', 'Slips / trips'],
    scope:
      'Install wet underfloor heating system — manifold, pipework laid in clips / tracks over insulation, thermostats per zone, pressure-test BEFORE screed pour, commissioning after.',
    hazards: [
      { hazard: 'Pipe damage during install or screed pour', risk: 'High', controls: 'Pipework pressure-tested and pressurised continuously through the screed pour; pressure gauge visible during pour; never lift weights / wheelbarrows directly on unprotected pipe.' },
      { hazard: 'Manual handling of pipe coils, manifolds, insulation', risk: 'Med', controls: 'Two-person carry; pre-position insulation boards; rotate operators.' },
      { hazard: 'Cuts from pipe cutter / hand tools', risk: 'Med', controls: 'Sharp blades; clamp work; eye protection.' },
      { hazard: 'Working in cramped airing-cupboard / utility for manifold', risk: 'Med', controls: 'Pair work; phone on operator; ventilate.' },
      { hazard: 'Slips on insulation panels and clips before pipe is laid', risk: 'Med', controls: 'Boards laid for access; tidy as you go; stack offcuts not under foot.' },
    ],
    sequence: [
      'Pre-start — design (pipe layout, zone layout, manifold position).',
      'Lay insulation boards over substrate; tight to walls.',
      'Install perimeter expansion strip.',
      'Lay pipe in clips / tracks per design; minimum bend radius respected.',
      'Manifold installed and connected; zone valves wired (electrician).',
      'Pressure-test the loops; check no leaks.',
      'Pressurise and leave under pressure during screed pour.',
      'After screed cure — fill, dose system; commission flow / balance.',
      'Issue commissioning certificate.',
    ],
    equipment: [
      'Pipe cutter / pipe coil dispenser',
      'Pressure pump and gauge',
      'Manifold install kit',
      'Zone valves and actuators (wired by electrician)',
      'Spirit level',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis (where site rules require)',
      'Cut-resistant gloves',
      'Eye protection',
      'Knee pads',
    ],
    emergency:
      'Pipe burst during pressure test — isolate, contain. UFH pipe burst (boiling water) post-commission — isolate at manifold, 999 if scalding casualty. Cut from pipe cutter — pressure, A&E. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'fire_alarm_install',
    title: 'Fire alarm install',
    trades: ['Electrical'],
    description: 'BS 5839 grade D / mains-powered + battery interlinked smoke / heat alarm install or LD3 panelled system.',
    defaultHazards: ['Electrical', 'Working at height', 'Manual handling'],
    scope:
      'Install domestic fire alarm system per BS 5839-6 — grade D interlinked smoke / heat / CO alarms (mains-powered with battery back-up), or LD3 panelled system in larger property / HMO.',
    hazards: [
      { hazard: 'Live circuit work at consumer unit', risk: 'High', controls: 'Isolate at supply origin; lock and tag; voltage tester (proven dead-live-dead); never work alone on energised CU; insulated tools.' },
      { hazard: 'Drilling through hidden services / structural elements', risk: 'High', controls: 'CAT-detect; service drawings; never drill blind; safe zones per BS 7671.' },
      { hazard: 'Working at height (ceiling-mounted alarms)', risk: 'Med', controls: 'Tower / podium step; never the top step of a stepladder.' },
      { hazard: 'Wrong grade of system for the dwelling type', risk: 'High', controls: 'Specifier confirms grade D / LD per BS 5839 against dwelling category; HMO / over-3-storey may require LD2; check with building control.' },
      { hazard: 'Failure to interlink heads', risk: 'High', controls: 'Wired interlink (or manufacturer wireless interlink); functional test on every head; one alarm triggers all.' },
    ],
    sequence: [
      'Pre-start — design (BS 5839-6 grade per dwelling); locations on plan.',
      'Isolate at CU; lock and tag.',
      'Run permanent live, neutral, earth and interlink wire to each head position.',
      'Mount bases; terminate; commission interlink.',
      'Energise; test each head — push button; verify all heads sound.',
      'Test CO alarm (separate audio) where fitted.',
      'Issue BS 5839-6 commissioning certificate to client / building control.',
      'User briefing — testing schedule, replacement battery dates.',
    ],
    equipment: [
      'Cable detector',
      'Voltage tester',
      'MFT (calibrated)',
      'Cordless drill, hole saws',
      'Tower / podium step',
      'Lock-off kit',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves',
      'Eye protection',
      'Insulated gloves rated for voltage during energised work',
    ],
    emergency:
      'Electric shock — isolate, do not touch, 999. Cable strike to gas / water — isolate, 999 / 0800 111 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'cctv_intruder_alarm',
    title: 'CCTV / intruder alarm install',
    trades: ['Electrical'],
    description: 'Install IP CCTV cameras, recorder and intruder alarm system per BS EN 50131.',
    defaultHazards: ['Working at height', 'Electrical', 'Manual handling'],
    scope:
      'Install CCTV (IP cameras to NVR or cloud) and intruder alarm (control panel, sensors, sounder, keypad) per BS EN 50131. Includes cabling, mounting, commissioning, network setup.',
    hazards: [
      { hazard: 'Working at height (cameras at high level outside)', risk: 'High', controls: 'Tower / scaffold for any camera >2 m; never the top of a stepladder; pair work outside.' },
      { hazard: 'Drilling through hidden services / cavity walls', risk: 'High', controls: 'CAT-detect; service drawings; safe zones per BS 7671; survey for asbestos board on older soffits.' },
      { hazard: 'Live circuit work for control panel mains supply', risk: 'High', controls: 'Isolate at CU; lock and tag; insulated tools; voltage tester.' },
      { hazard: 'Manual handling of cable drums and recorder unit', risk: 'Low', controls: 'Drum stand for cables; pre-cut at the bench.' },
      { hazard: 'Wrong cabling for outdoor use (UV / weather degradation)', risk: 'Med', controls: 'UV-rated cat6e or specific CCTV cable; armoured for buried; sleeved at penetrations.' },
      { hazard: 'Privacy / GDPR — camera covering public space', risk: 'Low', controls: 'Camera angles assessed for proportionality; only domestic curtilage covered; signage if any neighbour overlap; advise client of GDPR responsibilities.' },
    ],
    sequence: [
      'Pre-start — design (camera positions, line of sight, GDPR); panel position.',
      'Run cables — Cat6 / RG59 / SWA per design; concealed where possible.',
      'Mount cameras / sensors at design positions; weatherproof gland for outside.',
      'Install NVR / control panel; cable in.',
      'Power up; commission — set zones, motion areas, time / date, network.',
      'Test alarm to monitoring centre (if monitored); verify sounder cut-off.',
      'Hand over — keypad codes, app config, user briefing.',
      'Issue commissioning certificate; GDPR notice to client.',
    ],
    equipment: [
      'Cable detector',
      'Punch-down tool, RJ45 crimper',
      'PoE injector / NVR',
      'Cordless drill, hole saws',
      'Tower / podium step',
      'Multimeter',
    ],
    ppe: [
      'Hard hat (where site rules require)',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves',
      'Eye protection',
      'FFP3 mask if drilling masonry',
    ],
    emergency:
      'Fall from height — 999. Electric shock — isolate, 999. Drill into hidden service — STOP, isolate, repair. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'data_cabling',
    title: 'Data / network cabling',
    trades: ['Electrical'],
    description: 'Cat6 / Cat6a structured cabling — points, patch panel, comms cabinet, certify per ISO 11801.',
    defaultHazards: ['Working at height', 'Manual handling', 'Slips / trips'],
    scope:
      'Install structured data cabling — Cat6 / Cat6a from comms cabinet / patch panel to outlet faceplates. Test and certify per ISO 11801 / TIA-568. Optional fibre uplinks.',
    hazards: [
      { hazard: 'Drilling through hidden services / structural elements', risk: 'High', controls: 'CAT-detect; service drawings; never drill blind; safe zones.' },
      { hazard: 'Working in lofts / ceiling voids', risk: 'High', controls: 'Crawl boards in lofts; lighting; pair work.' },
      { hazard: 'Manual handling of cable drums (heavy)', risk: 'Med', controls: 'Drum stand; pre-cut runs at the bench.' },
      { hazard: 'Cuts from punch-down tool / cable strippers', risk: 'Med', controls: 'Sharp tools used safely; first aid kit accessible.' },
      { hazard: 'Eye injury from cable strands when stripping', risk: 'Med', controls: 'Eye protection during stripping and termination.' },
      { hazard: 'Fibre splinters (if fibre work in scope)', risk: 'High', controls: 'Fibre-trained operator only; never touch fibre ends; offcuts disposed in sealed sharps container; eye protection mandatory; never eat / drink in fibre work area.' },
    ],
    sequence: [
      'Pre-start — design (point positions, cable routes, cabinet location).',
      'Mount comms cabinet / wall mount.',
      'Run cables from cabinet to point positions; cable trays / containment per design.',
      'Terminate at faceplates and patch panel; punch-down to T568A or T568B as standard.',
      'Test every link — wire-map, length, NEXT, attenuation; certify with cable certifier.',
      'Label both ends per scheme; record on as-built schedule.',
      'Power up active equipment; verify links.',
      'Issue commissioning certificate.',
    ],
    equipment: [
      'Cable certifier (Fluke or equivalent)',
      'Punch-down tool',
      'RJ45 crimper, modular plugs',
      'Cable detector',
      'Drum stand',
      'Cordless drill, hole saws',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves',
      'Eye protection',
      'FFP3 mask if drilling masonry / lofts',
    ],
    emergency:
      'Fibre splinter to eye — DO NOT rub; cover and to A&E. Cut from punch-down — pressure, A&E. Drill into hidden service — STOP, isolate. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'boiler_swap',
    title: 'Boiler replacement',
    trades: ['Plumbing'],
    description: 'Replace gas combi / system boiler — strip-out, install, gas connection, electrical, commissioning.',
    defaultHazards: ['Manual handling', 'COSHH (chemicals)', 'Hot works', 'Electrical'],
    scope:
      'Strip out existing boiler and replace — typically combi or system boiler. Includes flush of system, new pipework where required, flue, electrical, gas connection (Gas Safe), commissioning.',
    hazards: [
      { hazard: 'Gas leak during disconnection / reconnection', risk: 'High', controls: 'Isolate gas at the ECV; tightness test on every joint; Gas Safe registered engineer ONLY; never light or test without all joints tested.' },
      { hazard: 'CO from incorrectly fitted flue', risk: 'High', controls: 'Flue route per maker — clearances, terminations, gradient; flue gas analysis on commissioning; never sign off without recorded CO₂/CO ratio.' },
      { hazard: 'Manual handling of boiler (often >25 kg) and old boiler removal', risk: 'High', controls: 'Two-person lift; sack truck; mount bracket fixed first; old boiler drained before removal.' },
      { hazard: 'Hot works for capillary joints', risk: 'Med', controls: 'Hot Works Permit; combustibles cleared 1 m; CO₂ extinguisher; fire watch.' },
      { hazard: 'System sludge / old debris damaging new boiler', risk: 'Med', controls: 'System power-flush BEFORE new boiler fit; magnetic filter installed; inhibitor dosed; commissioning certificate notes flush.' },
      { hazard: 'Live electrical connection', risk: 'High', controls: 'Isolate at CU; lock and tag; insulated tools; voltage tester.' },
    ],
    sequence: [
      'Pre-start — Gas Safe ID; design / spec; isolate gas at ECV; isolate electrical at CU.',
      'Drain system; remove old boiler.',
      'Power-flush system; install magnetic filter.',
      'Mount new boiler bracket; offer up boiler.',
      'Flue installed per maker — clearances, terminations.',
      'Pipework adjustments — gas, flow / return, condensate, expansion vessel.',
      'Electrical connection by competent electrician.',
      'Tightness test gas; energise.',
      'Commission — flue analysis, pressure, hot water temp, central heating run-up.',
      'Issue Benchmark / commissioning certificate; Gas Safe notification.',
    ],
    equipment: [
      'Gas Safe registered engineer',
      'Manometer',
      'Flue gas analyser (calibrated)',
      'Power-flush pump',
      'Pipe-press / soldering kit',
      'Hot works kit',
      'Sack truck',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis (where site rules require)',
      'Cut-resistant gloves; nitrile under',
      'Eye protection',
      'A1P3 cartridge mask for flushing chemicals',
    ],
    emergency:
      'Gas leak — close ECV, ventilate, no ignition, evacuate, 0800 111 999. CO suspected — evacuate, fresh air, 999. Hot works fire — fight if confined (CO₂); otherwise 999. Electric shock — isolate, 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'duct_install',
    title: 'HVAC ductwork install',
    trades: ['Plumbing'],
    description: 'Galvanised / spiral / flexible ductwork install for ventilation and HVAC systems.',
    defaultHazards: ['Working at height', 'Manual handling', 'Loud noise (>85 dB)', 'Hand-arm vibration (HAVS)'],
    scope:
      'Install galvanised, spiral or flexible ductwork for ventilation, HVAC or industrial extract systems. Includes brackets / hangers, joints, attenuators, fire dampers, grilles / diffusers.',
    hazards: [
      { hazard: 'Working at height for ceiling / soffit-mounted ductwork', risk: 'High', controls: 'Mobile tower or scaffold; harness for unguarded; never the top step of a stepladder.' },
      { hazard: 'Manual handling of long duct sections', risk: 'High', controls: 'Two-person carry; mechanical hoist for upper sections; rotate handlers.' },
      { hazard: 'Sharp edges of cut metal', risk: 'High', controls: 'Cut-resistant gloves; deburr after cutting; eye protection.' },
      { hazard: 'Loud noise from cutting / drilling galvanised steel', risk: 'High', controls: 'SNR ≥27 hearing protection; cordon if >85 dB.' },
      { hazard: 'Hand-arm vibration from extended power-cutting', risk: 'Med', controls: 'Low-vibration tools; rotate operator; HAVS surveillance.' },
      { hazard: 'Fire-dampers fitted incorrectly', risk: 'High', controls: 'Fire dampers fitted in compartmentation walls / floors per design; fusible link tested; verified by building control / inspector.' },
    ],
    sequence: [
      'Pre-start — duct schedule, hanger schedule, fire-stop locations.',
      'Mark out hanger positions; install hangers / brackets.',
      'Hang main runs in sections; bolted / clipped per maker.',
      'Branch ducts to terminals.',
      'Install attenuators where specified.',
      'Fire dampers at compartmentation lines.',
      'Grilles and diffusers fitted.',
      'Leak / pressure test if spec requires.',
      'Commission air flows with anemometer.',
    ],
    equipment: [
      'Aviation snips / power shears',
      'Cordless drill, impact driver',
      'Riveter / self-tapping screws',
      'Mobile tower / scaffold',
      'Anemometer',
      'Sealant gun',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis',
      'Cut-resistant gloves',
      'Eye protection',
      'SNR ≥27 hearing protection',
      'FFP3 mask during cutting',
    ],
    emergency:
      'Fall from height — 999. Cut from sharp metal — pressure, A&E. Cut from snips / power shear — pressure, A&E. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'soundproofing',
    title: 'Acoustic / soundproofing install',
    trades: ['Drylining & plastering', 'Insulation'],
    description: 'Acoustic insulation, resilient bars, mass-loaded vinyl, floating floor — Part E retrofit / new build.',
    defaultHazards: ['Manual handling', 'COSHH (chemicals)', 'Loud noise (>85 dB)', 'Slips / trips'],
    scope:
      'Install acoustic insulation systems to walls, floors and ceilings — mineral wool batts, mass-loaded vinyl, resilient bars, floating floor systems — to meet Part E or higher acoustic targets.',
    hazards: [
      { hazard: 'Mineral wool / acoustic fibre — skin and respiratory irritation', risk: 'Med', controls: 'Disposable Type 5 overalls with hood; FFP3 mask; nitrile under work gloves; long sleeves cuffed; HEPA vac (never brush).' },
      { hazard: 'Manual handling of MLV (mass-loaded vinyl) — heavy and limp', risk: 'High', controls: 'Two-person lift; pre-cut at the bench; never single-handed up stairs.' },
      { hazard: 'Cuts from acoustic-rated plasterboard', risk: 'Med', controls: 'Cut-resistant gloves; sharp blade; eye protection.' },
      { hazard: 'Adhesive / sealant fume', risk: 'Med', controls: 'Ventilate; A1P3 cartridge mask if prolonged; nitrile gloves.' },
      { hazard: 'Acoustic detail compromised by penetrations', risk: 'High', controls: 'Acoustic mastic seal at every penetration / junction; resilient bar continuity; verify by sound test if Part E mandates Pre-Completion Test.' },
      { hazard: 'Slips on packaging / offcuts', risk: 'Med', controls: 'Continuous tidy; HEPA vac.' },
    ],
    sequence: [
      'Pre-start — design (Part E target); details for junctions, penetrations.',
      'Strip existing finish back to substrate where retrofit.',
      'Lay isolation layer / acoustic batts; never compressed.',
      'Resilient bars on walls / ceilings if specified.',
      'Mass-loaded vinyl OR acoustic plasterboard layered to spec.',
      'Floor — acoustic underlay / decoupling layer; floating floor on top.',
      'Acoustic mastic at all junctions.',
      'Sound test (Part E PCT) if dwelling separates flats.',
      'Tape and skim / decorate (separate trades).',
    ],
    equipment: [
      'Hand saw / reciprocating saw',
      'Acoustic adhesive gun',
      'Mass-loaded vinyl roller',
      'Cordless drill / impact',
      'Knife with extra blades',
      'M-class extractor',
    ],
    ppe: [
      'Hard hat (where site rules require)',
      'Safety boots',
      'Hi-vis vest',
      'Type 5 overalls with hood',
      'Nitrile gloves under cut-resistant',
      'Eye protection',
      'FFP3 mask',
    ],
    emergency:
      'Fibre / dust in eye — flush 15 min, A&E if irritation persists. Cut from saw — pressure, A&E. MLV roll dropped on foot — assess, A&E if any deformity. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'swimming_pool',
    title: 'Swimming pool install',
    trades: ['Foundations & concrete', 'Plumbing', 'Electrical'],
    description: 'In-ground or above-ground swimming pool install — shell, pipework, plant, electrical, finishes.',
    defaultHazards: ['Excavation', 'Electrical', 'COSHH (chemicals)', 'Manual handling', 'Slips / trips'],
    scope:
      'Install in-ground or above-ground domestic swimming pool — excavation, shell (concrete or composite), pipework, plant room, electrical (IP-rated), finishes (tile / liner), commissioning.',
    hazards: [
      { hazard: 'Excavation collapse around pool dig', risk: 'High', controls: 'Engineer-signed shoring above 1.2 m; daily inspection; never enter unsupported.' },
      { hazard: 'Drowning during fill / commissioning', risk: 'High', controls: 'Never alone during fill stages; safety equipment (life ring) on hand; cover when pool unattended; child-safe gates around perimeter.' },
      { hazard: 'Electrical work in / near water (Part P)', risk: 'High', controls: 'IP-rated equipment per zone (Zone 0 / 1 / 2); RCD on every pool circuit; supplementary bonding to all metallic parts; Part P competent electrician.' },
      { hazard: 'Pool chemicals (chlorine, acid)', risk: 'High', controls: 'Stored in vented secure cabinet; SDS on file; PPE for any handling — chemical goggles, nitrile gloves, A1P3 mask; never mix chemicals.' },
      { hazard: 'Manual handling of large liner / shell sections', risk: 'High', controls: 'Mechanical aid (telehandler / crane); two-person minimum; planned route.' },
      { hazard: 'Slips on wet pool surround', risk: 'Med', controls: 'Non-slip surround tiles; clean drips during install.' },
    ],
    sequence: [
      'Pre-start — engineer\'s drawings; permits; ground survey.',
      'Excavate to design depth; shore.',
      'Construct shell — reinforced concrete OR install pre-formed shell.',
      'Pipework — circulation, skimmers, returns; pressure-test.',
      'Plant room install — pump, filter, heater, dosing.',
      'Electrical install — IP-rated; RCD; Part P notify.',
      'Finishes — tile / liner / mosaic.',
      'Backfill in compacted layers.',
      'Fill pool; balance chemistry; commission plant.',
      'Hand over — chemistry log, dose schedule, child-safety briefing.',
    ],
    equipment: [
      'Excavator',
      'Concrete pump',
      'Pool-specific pipe and fittings',
      'Pump, filter, heater',
      'Chemical dosing kit',
      'MFT (calibrated)',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis Class 3',
      'Chemical goggles for any chemistry work',
      'Nitrile gloves under cut-resistant',
      'A1P3 cartridge mask for chemicals',
    ],
    emergency:
      'Drowning — call 999, casualty out of pool, CPR if needed; isolate any electrical first. Excavation collapse — DO NOT enter; dig from outside; 999. Chemical spill — ventilate, evacuate, 999, notify Environment Agency if any to drainage. Electric shock — isolate, 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'hot_tub_install',
    title: 'Hot tub install',
    trades: ['Electrical', 'Plumbing'],
    description: 'Domestic hot tub install — base, electrical (IP-rated), water connection, commissioning.',
    defaultHazards: ['Manual handling', 'Electrical', 'COSHH (chemicals)', 'Slips / trips'],
    scope:
      'Install domestic hot tub — concrete or paved base, electrical supply (typically 32A dedicated circuit, IP-rated), water connection, commissioning.',
    hazards: [
      { hazard: 'Electrical work near water — risk of shock / fault', risk: 'High', controls: 'IP-rated isolator outside hot tub area; RCD on dedicated circuit; supplementary bonding; Part P competent electrician; no mains accessible from inside the tub.' },
      { hazard: 'Manual handling of large hot tub shell', risk: 'High', controls: 'Mechanical aid (crane / telehandler) for placement; planned route; two-person minimum, more for larger.' },
      { hazard: 'Inadequate base — sinking or cracking', risk: 'High', controls: 'Concrete pad or paved base sized for full-water weight (typically 1.5–2 tonnes); engineer\'s spec for high-end large tubs; check ground bearing.' },
      { hazard: 'Hot tub chemicals (bromine, chlorine, pH adjusters)', risk: 'Med', controls: 'Stored in vented cabinet away from children; SDS on file; chemical goggles, nitrile gloves; never mix.' },
      { hazard: 'Drowning risk for children', risk: 'High', controls: 'Lockable cover supplied; advise client of supervision and lockout; child-safe install briefed.' },
      { hazard: 'Slips on wet surround', risk: 'Med', controls: 'Non-slip surround; advise client.' },
    ],
    sequence: [
      'Pre-start — design / spec; base capacity verified.',
      'Install electrical supply by Part P competent electrician — IP-rated isolator within reach, dedicated 32A circuit, RCD.',
      'Position hot tub on prepared base (mechanical lift).',
      'Connect to water (fill); drainage point identified.',
      'Energise; commission heater, jets, lighting.',
      'Balance water chemistry.',
      'Cover supplied with lockable straps.',
      'Hand over — chemistry log, weekly maintenance, child-safety briefing.',
    ],
    equipment: [
      'Crane / telehandler for placement',
      'MFT (calibrated)',
      'Chemical dosing kit',
      'Filling hose',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis (where site rules require)',
      'Chemical goggles for any chemistry work',
      'Nitrile gloves',
      'A1P3 cartridge mask for chemicals',
    ],
    emergency:
      'Electric shock — isolate, do not touch, 999. Drowning — out of water, CPR if needed. Chemical splash — flush 15 min, A&E. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'block_paving',
    title: 'Block paving — driveways & patios',
    trades: ['Groundworks'],
    description: 'Lay block-paved driveway or patio — sub-base, edge restraint, screed, blocks, kiln-dried sand.',
    defaultHazards: ['Manual handling', 'Loud noise (>85 dB)', 'Hand-arm vibration (HAVS)', 'Silica dust', 'Slips / trips'],
    scope:
      'Excavate, lay sub-base, install edge restraint, screed, lay blocks, cut to perimeter, kiln-dried sand and compact. SUDS-permeable system available.',
    hazards: [
      { hazard: 'Silica / RCS dust from cutting blocks with grinder', risk: 'High', controls: 'Wet cutting saw — never dry-cut indoors / near others; FFP3 mask; on-tool extraction; cut downwind; HEPA vac.' },
      { hazard: 'Loud noise from compactor and saw', risk: 'High', controls: 'SNR ≥30 hearing protection; cordon; signage if >85 dB; respect noise hours.' },
      { hazard: 'Hand-arm vibration from compactor / cut-saw', risk: 'High', controls: 'Low-vibration tools; trigger-time managed; rotate; HAVS surveillance for regular operatives.' },
      { hazard: 'Manual handling of block packs (often 50–100 kg in pack)', risk: 'High', controls: 'Mechanical aid (forklift / palletiser); never single-lift a pack; rotate handlers; pre-position to point of use.' },
      { hazard: 'Vehicle / pedestrian interaction on driveways', risk: 'Med', controls: 'Cordon work area from public footpath; signage; banksman if reversing wagons; never leave excavation unattended without barriers.' },
      { hazard: 'Cement / sand burns and dermatitis', risk: 'Low', controls: 'Nitrile gloves under cut-resistant for cement bedding; immediate wash if splashed.' },
    ],
    sequence: [
      'Pre-start — survey, falls plan, drainage point, kerb / edge line.',
      'Excavate to sub-base depth; check ground bearing.',
      'Lay and compact sub-base (Type 1 typical 100–200 mm).',
      'Install edge restraint — concrete haunching either side of edge kerb.',
      'Lay sharp sand screed; rule and trowel to falls.',
      'Lay blocks in pattern (herringbone / stretcher / random); chop in cuts at perimeter using wet saw.',
      'Brush kiln-dried sand into joints; compact with vibrating plate (rubber pad).',
      'Repeat sanding and compaction until joints filled.',
      'Final sweep; remove excess sand from drainage.',
    ],
    equipment: [
      'Mini-excavator',
      'Vibrating plate compactor (with rubber pad for blocks)',
      'Wet bench saw / cut-off saw with water suppression',
      'Mortar mixer (for haunching)',
      'String lines and pegs',
      'Trowels, rakes',
    ],
    ppe: [
      'Hard hat (where site rules require)',
      'Safety boots with mid-sole',
      'Hi-vis Class 2 minimum',
      'Cut-resistant gloves',
      'Eye protection',
      'FFP3 mask during cutting',
      'Hearing protection SNR ≥30',
    ],
    emergency:
      'Saw cut to hand — pressure dressing, A&E if deep. Block dropped on foot — assess, A&E if any deformity. Vehicle in work zone — STOP, cordon. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'tarmac_asphalt',
    title: 'Tarmac / asphalt surfacing',
    trades: ['Groundworks'],
    description: 'Hot-rolled or cold-mix tarmac driveways and surfacing.',
    defaultHazards: ['Manual handling', 'COSHH (chemicals)', 'Plant / pedestrian interaction', 'UV exposure', 'Hot works'],
    scope:
      'Lay tarmac / asphalt driveway or surfacing — base course, surface course, edges and finishing. Hot-rolled (delivered ~140°C) requires fast turnaround between drop and roll.',
    hazards: [
      { hazard: 'Burns from hot tarmac (delivered ~140°C)', risk: 'High', controls: 'Long sleeves cuffed; heavy heat-resistant gloves; never walk through fresh hot tarmac; never reach into hopper; first aid burn kit on hand.' },
      { hazard: 'Bitumen fume', risk: 'Med', controls: 'Work upwind; FFP3 minimum; rotate operator; never breathe directly over the spreader.' },
      { hazard: 'Plant / pedestrian interaction (paver, roller, tipper)', risk: 'High', controls: 'No-go zone around plant; banksman in radio contact; eye contact with operator; reverse alarms.' },
      { hazard: 'Manual handling of rakes / shovels of hot material', risk: 'Med', controls: 'Rotate operatives; lifts kept small; planned rest breaks in hot weather.' },
      { hazard: 'UV exposure during summer install', risk: 'Med', controls: 'SPF30+; long-sleeved hi-vis; hat with neck protection; hydration breaks.' },
      { hazard: 'Slips on freshly-rolled material', risk: 'Med', controls: 'Non-slip footwear; never stand on warm rolled tarmac (still soft); cones around new work to prevent vehicle access.' },
    ],
    sequence: [
      'Pre-start — survey, falls, drainage point, base course condition.',
      'Tipper banksman briefed; agreed signals.',
      'Tipper drops to paver hopper or directly to base.',
      'Paver lays in machine widths; raker hand-finishes edges.',
      'Roll within 10–15 minutes of laying (whilst material >100°C); pattern of passes per spec.',
      'Compact joints between widths; finish edges to kerb / abutment.',
      'Cordon off until material cools (typically 1–2 hr).',
      'Final inspection; clean kerbs / surroundings.',
    ],
    equipment: [
      'Tipper (delivery)',
      'Paver / box spreader',
      'Vibrating roller',
      'Rakes, shovels, lutes',
      'Spray bottle of release agent',
    ],
    ppe: [
      'Hard hat',
      'Safety boots with heat-rated sole',
      'Hi-vis Class 3',
      'Heavy heat-resistant gloves',
      'Eye protection',
      'FFP3 minimum (bitumen fume)',
      'Long-sleeved cotton overalls (cuffed)',
      'Sun protection',
    ],
    emergency:
      'Burn from hot tarmac — DO NOT remove anything stuck to skin; cool with water, A&E. Bitumen fume overcoming operator — fresh air, 999 if non-responsive. Reversing tipper — STOP, banksman re-brief. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'kerb_laying',
    title: 'Kerb laying',
    trades: ['Groundworks', 'Bricklaying'],
    description: 'Lay pre-cast or in-situ concrete kerbs with concrete haunching.',
    defaultHazards: ['Manual handling', 'COSHH (chemicals)', 'Plant / pedestrian interaction'],
    scope:
      'Lay pre-cast concrete kerbs to design line and falls — bull-nose, half-batter, full-batter, dropped, transitions. Includes haunching, jointing and back-fill.',
    hazards: [
      { hazard: 'Manual handling of kerbs (typically 30–80 kg each)', risk: 'High', controls: 'Two-person lift for kerbs >40 kg; mechanical aid (clamp + telehandler) for full-section kerbs; rotate; pre-position at point of use.' },
      { hazard: 'Cement burns and dermatitis', risk: 'Med', controls: 'Nitrile gloves under cut-resistant; long sleeves; immediate wash; barrier cream.' },
      { hazard: 'Vehicle / pedestrian interaction at road-side', risk: 'High', controls: 'Cones, signage, traffic management plan; high-vis Class 3; banksman if reversing wagons.' },
      { hazard: 'Crushing fingers between kerb and adjacent', risk: 'Med', controls: 'Set down with packers; use dumpy bar to adjust; never hand between kerbs whilst lowering.' },
      { hazard: 'Slips on muddy edge', risk: 'Med', controls: 'Edges scraped clean; non-slip footwear; tidy mortar drops.' },
    ],
    sequence: [
      'Set out — string lines to design level and falls.',
      'Excavate kerb foundation; concrete bed laid to design.',
      'Position first kerb; haunch behind with concrete.',
      'Continue laying; check level and falls every 3 metres.',
      'Joints — closed butt or 5 mm with sand pointing per spec.',
      'Back-fill behind haunching once cured.',
      'Final inspection; clean kerb faces.',
    ],
    equipment: [
      'Telehandler with kerb clamp',
      'Mortar mixer',
      'Concrete (ready-mix or mixer)',
      'Spirit levels, dumpy bar',
      'String lines',
    ],
    ppe: [
      'Hard hat',
      'Safety boots with mid-sole',
      'Hi-vis Class 3',
      'Cut-resistant grip gloves; nitrile under',
      'Eye protection',
      'FFP3 mask if cutting',
    ],
    emergency:
      'Kerb falls on foot — assess, A&E if any deformity. Vehicle into cones — STOP, traffic management re-plan. Cement in eye — flush 15+ min, A&E. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'fencing',
    title: 'Fencing — timber, mesh, palisade',
    trades: ['Groundworks', 'Carpentry & joinery'],
    description: 'Install boundary fencing — timber close-board, palisade, post-and-rail, security mesh.',
    defaultHazards: ['Manual handling', 'COSHH (chemicals)', 'Loud noise (>85 dB)', 'Hand-arm vibration (HAVS)'],
    scope:
      'Install boundary fencing — typically timber close-board, post-and-rail, palisade or weldmesh / chain-link. Includes posts (concreted or driven), rails / panels, gates and ironmongery.',
    hazards: [
      { hazard: 'Hidden services in post-hole locations', risk: 'High', controls: 'Service drawings; CAT-detect every post position; Permit-to-Dig where near known services; hand-dig if any uncertainty.' },
      { hazard: 'Manual handling of posts (full concrete posts heavy; 8 ft fence panels awkward)', risk: 'High', controls: 'Two-person lift for full panels; post-lifter or mechanical aid for concrete posts; rotate.' },
      { hazard: 'Cuts from saws and post-driver use', risk: 'Med', controls: 'Sharp blades; clamp work; eye protection; never hand-hold whilst sawing.' },
      { hazard: 'Concrete burns from post-mix', risk: 'Med', controls: 'Nitrile gloves under cut-resistant; immediate wash; eye protection.' },
      { hazard: 'Loud noise from post drivers', risk: 'Med', controls: 'SNR ≥27 hearing protection; respect noise hours.' },
      { hazard: 'Sharp wire ends on mesh / palisade', risk: 'Med', controls: 'Cut-resistant gloves; deburr; eye protection.' },
    ],
    sequence: [
      'Pre-start — line of fence, property boundary, services.',
      'CAT-detect and dig post holes; check service drawings.',
      'Set posts — concreted in OR driven (post-driver); plumb each.',
      'Install rails / arris rails between posts.',
      'Fix panels / palisade pales / mesh per design.',
      'Install caps / capping rail.',
      'Hang gates with adjustable hinges; verify swing and latch.',
      'Backfill and tamp.',
    ],
    equipment: [
      'Post-hole digger / auger / post-driver',
      'Concrete mixer (or post-mix bags)',
      'Cordless drill / impact, mitre saw',
      'String line and pegs',
      'CAT-detector',
      'Pop-rivet gun if mesh',
    ],
    ppe: [
      'Hard hat (where site rules require)',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves',
      'Eye protection',
      'Hearing protection during driving',
    ],
    emergency:
      'Service strike from post hole — STOP, isolate, 999 / 0800 111 999 (gas). Cut from saw / mesh — pressure, A&E. Cement in eye — flush 15+ min, A&E. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'tree_work',
    title: 'Tree work / arboriculture',
    trades: ['Groundworks'],
    description: 'Tree felling, pruning, climbing operations — chainsaw, MEWP or rope-and-harness.',
    defaultHazards: ['Working at height', 'Loud noise (>85 dB)', 'Hand-arm vibration (HAVS)', 'Plant / pedestrian interaction', 'Wet weather'],
    scope:
      'Tree felling, sectional dismantling, pruning, crown reduction — using ground saw, MEWP or climbing (rope-and-harness). All work by NPTC / LANTRA carded operatives.',
    hazards: [
      { hazard: 'Falls from tree / MEWP', risk: 'High', controls: 'Climbing — twin-rope system per ICOP; harness inspected within 6 months; never alone in tree; ground rescuer briefed; MEWP — IPAF carded operator, harness clipped to manufacturer point in basket.' },
      { hazard: 'Chainsaw injury', risk: 'High', controls: 'NPTC / LANTRA carded operator; chain brake working; chaps / Class 2 trousers; helmet with face screen and ear defenders; first aid kit with arterial dressing within 1 minute reach.' },
      { hazard: 'Falling timber onto people / property below', risk: 'High', controls: 'Drop zone defined by groundsman; banksman in radio contact with climber / operator; cordon below with signage; rope sections down rather than free-fall when near structures.' },
      { hazard: 'Power lines in / near tree canopy', risk: 'High', controls: 'Survey before work; minimum clearance from live OHL per ENA G39; never work within 10 m of live LV / 11 kV without DNO isolation; emergency stop briefed.' },
      { hazard: 'Hand-arm vibration from chainsaw use', risk: 'High', controls: 'Trigger-time managed; rotate operator; HAVS surveillance; anti-vibration mounts checked.' },
      { hazard: 'Adverse weather — wind / rain', risk: 'Med', controls: 'STOP at sustained wind >25 mph; never climb in lightning; reschedule if conditions unsafe.' },
    ],
    sequence: [
      'Pre-start — site survey, services, drop zone, weather; brief team.',
      'Cordon work area; signage.',
      'If climbing — climber kit checked; ground rescuer ready; first aid kit accessible.',
      'If MEWP — pre-use inspection; outriggers; ground rescue plan.',
      'Sectional dismantle from top down; rope down sections near structures.',
      'Chip / log stack; chip collected by groundsman from designated zone only.',
      'Make safe — stumps marked; debris cleared from public footpath.',
      'Inspect; release cordon.',
    ],
    equipment: [
      'Chainsaw (top-handle for climbers, longer for ground)',
      'Climbing kit — twin ropes, harness, helmet',
      'OR MEWP (pre-use inspected)',
      'Chipper',
      'Two-way radios',
      'First aid kit with arterial dressing',
    ],
    ppe: [
      'NPTC chainsaw helmet (face screen + ear defenders)',
      'Class 2 chainsaw trousers',
      'Chainsaw boots',
      'Chainsaw gloves',
      'Hi-vis Class 3',
      'Harness for climbing / MEWP',
    ],
    emergency:
      'Chainsaw injury — apply arterial dressing immediately; 999. Climber stuck / fall arrest activated — ground rescue plan; recover within 15 min; 999. MEWP failure — descend via emergency control; 999. Branch falls onto person — clear, 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'soft_landscaping',
    title: 'Soft landscaping — planting, turf, beds',
    trades: ['Groundworks'],
    description: 'Soft landscaping — bed prep, planting, tree planting, turf-laying, mulching.',
    defaultHazards: ['Manual handling', 'COSHH (chemicals)', 'UV exposure', 'Slips / trips'],
    scope:
      'Soft-landscape works — prepare beds with topsoil, plant shrubs / trees, lay turf or sow seed, mulch and edge, install irrigation if specified.',
    hazards: [
      { hazard: 'Manual handling of bagged topsoil / mulch / turf rolls', risk: 'High', controls: 'Two-person carry for full pallets; mechanical aid (telehandler) for bulk; rotate handlers.' },
      { hazard: 'Skin / eye irritation from topsoil chemistry, fertiliser, weedkiller', risk: 'Med', controls: 'Nitrile gloves; eye protection during dry-spreading; SDS on file for chemicals; wash before food.' },
      { hazard: 'UV exposure', risk: 'Low', controls: 'SPF30+; long-sleeved hi-vis; hat; hydration.' },
      { hazard: 'Cuts from secateurs / hand tools', risk: 'Low', controls: 'Sharp blades; cut-resistant gloves; first aid kit accessible.' },
      { hazard: 'Hidden services when planting trees deep / root barriers', risk: 'Med', controls: 'CAT-detect for any planting >300 mm; service drawings; trial pit if uncertain.' },
      { hazard: 'Manual handling — repetitive bending', risk: 'Med', controls: 'Long-handled tools where possible; rotate tasks; rest breaks.' },
    ],
    sequence: [
      'Pre-start — planting plan, bed locations, irrigation if any.',
      'Prepare beds — topsoil to depth, conditioner / fertiliser per spec.',
      'Plant trees — pit twice rootball width, mycorrhiza, stake & tie, water in.',
      'Plant shrubs to staggered pattern; firm in.',
      'Lay turf — staggered joints; root-water; roll if spec; mow at correct height.',
      'Mulch beds (75 mm minimum); edge to lawn.',
      'Irrigation install if specified.',
      'Hand-over — care notes, watering schedule.',
    ],
    equipment: [
      'Spades, forks, rakes',
      'Wheelbarrows',
      'Telehandler / sack truck for pallets',
      'Hose / water supply',
      'Lawn mower / strimmer',
      'CAT-detector for tree planting',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves',
      'Eye protection during dry-spreading',
      'FFP3 mask if dusty',
      'Sun protection',
    ],
    emergency:
      'Chemical splash to eye — flush 15+ min, A&E. Service strike — STOP, isolate, 999 / 0800 111 999. Heat exhaustion — shade, hydrate, 999 if non-responsive. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'garden_buildings',
    title: 'Garden buildings — sheds, summerhouses, log cabins',
    trades: ['Carpentry & joinery'],
    description: 'Install pre-fabricated or built-on-site garden buildings — base, walls, roof, glazing.',
    defaultHazards: ['Manual handling', 'Working at height', 'Wet weather', 'Slips / trips'],
    scope:
      'Install pre-fabricated or constructed-on-site garden building — concrete base or paving slabs, walls (timber panels or log), roof (shingles / felt / tile), glazing and door.',
    hazards: [
      { hazard: 'Manual handling of long timber panels and roof sections', risk: 'High', controls: 'Two-person carry; planned route; mechanical aid for large log cabin sections; never single-handed wide panels.' },
      { hazard: 'Working at height for roofs', risk: 'Med', controls: 'Tower / podium step; never the top step of a stepladder.' },
      { hazard: 'Cuts from saws and hand tools', risk: 'Med', controls: 'Sharp blades; clamp work; eye protection.' },
      { hazard: 'Inadequate base — building movement / damage', risk: 'Med', controls: 'Concrete pad or paving slabs to design loadings; level checked; never on bare soft soil for log cabins.' },
      { hazard: 'Glass breakage during install', risk: 'Med', controls: 'Two-person lift; never lay glass flat on ground; tape over panes during transit.' },
      { hazard: 'Wet weather — slips and incomplete weather seal', risk: 'Med', controls: 'Tarp ready; never leave open at end of shift; reschedule heavy rain.' },
    ],
    sequence: [
      'Pre-start — design / spec; base prepared.',
      'Lay floor frame to base; check level.',
      'Erect walls — fix to floor; corners checked plumb.',
      'Install roof structure / panels.',
      'Install windows and doors.',
      'Roof covering — felt shingles / mineral felt / tile per spec.',
      'Trim and finish — barge boards, fascia, soffit.',
      'External finish — preservative / stain (separate trades if any).',
    ],
    equipment: [
      'Cordless drill / impact',
      'Mitre saw, hand saw',
      'Tower / podium step',
      'Spirit level (long)',
      'Sealant gun',
      'Suction handles for glass',
    ],
    ppe: [
      'Hard hat (where site rules require)',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves',
      'Eye protection',
      'FFP3 mask during cutting / sanding',
    ],
    emergency:
      'Fall from roof — 999. Glass breakage — pressure dressing if cut, A&E. Cut from saw — pressure, A&E. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'secondary_glazing',
    title: 'Secondary glazing install',
    trades: ['Carpentry & joinery'],
    description: 'Install secondary glazing systems internally — listed buildings, acoustic, thermal upgrade.',
    defaultHazards: ['Manual handling', 'Working at height', 'Slips / trips'],
    scope:
      'Install secondary glazing — internal aluminium or timber frames behind existing windows for acoustic / thermal upgrade. Common in listed buildings where primary windows cannot be replaced.',
    hazards: [
      { hazard: 'Glass breakage during handling', risk: 'High', controls: 'Two-person lift for any pane >0.7 m²; suction cups; never lay flat; heavy gloves rated for glass.' },
      { hazard: 'Damage to listed / heritage primary window during install', risk: 'High', controls: 'Survey existing primary window; agree fixings strategy with conservation officer; chemical anchor where reversible; never damage timber sash mechanism.' },
      { hazard: 'Working at height (upper-storey internal windows)', risk: 'Med', controls: 'Tower or podium step; never the top step of a stepladder.' },
      { hazard: 'Manual handling of full-size frames', risk: 'Med', controls: 'Two-person carry; pre-position to point of install.' },
      { hazard: 'Cuts from frame edges / glass', risk: 'Med', controls: 'Cut-resistant gloves; deburr; eye protection.' },
    ],
    sequence: [
      'Survey existing primary window opening; verify size.',
      'Position fixing track or sub-frame; agreed fixings; never invasive on heritage if reversibility required.',
      'Lift glazed unit into track / sub-frame; secure.',
      'Adjust for smooth operation.',
      'Seal with breathable mastic where required.',
      'Brief client on operation and condensation management.',
    ],
    equipment: [
      'Suction cups for glass',
      'Cordless drill / impact',
      'Spirit level',
      'Sealant gun',
      'Tower / podium step',
    ],
    ppe: [
      'Safety boots',
      'Hi-vis (where site rules require)',
      'Heavy cut-resistant gloves rated for glass',
      'Eye protection',
    ],
    emergency:
      'Glass breakage / cut — pressure dressing, A&E. Damage to heritage window — STOP, contact conservation officer / client. Fall from tower — 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'loft_conversion',
    title: 'Loft conversion — structural works',
    trades: ['Carpentry & joinery'],
    description: 'Loft conversion structural — strengthening floor, dormer / Velux, stair install, fire compartmentation.',
    defaultHazards: ['Working at height', 'Manual handling', 'Wet weather', 'Slips / trips'],
    scope:
      'Convert existing loft to habitable space — strengthen floor structure, install steel beams where required, build dormer or rooflights (separate RAMS available), install staircase, fire compartmentation per Part B.',
    hazards: [
      { hazard: 'Falls through ceiling joists from above', risk: 'High', controls: 'Crawl boards across joists; never step off boards onto plasterboard; lighting in loft.' },
      { hazard: 'Falls down stair void during stair fitting', risk: 'High', controls: 'Stair void barriered with handrail until stair fitted; harness for any unguarded work; never leave open at end of shift.' },
      { hazard: 'Structural failure of existing roof / ceiling during alteration', risk: 'High', controls: 'Engineer-signed sequence; props in place BEFORE breakouts; never remove existing structural elements without engineer drawing.' },
      { hazard: 'Manual handling of long structural members', risk: 'High', controls: 'Mechanical hoist for steel beams (separate RAMS); two-person for trusses / rafters.' },
      { hazard: 'Working in tight low-pitch loft', risk: 'Med', controls: 'Lighting; pair work; phone on operator; rest breaks; ventilation.' },
      { hazard: 'Fire compartmentation broken — between flat ceilings of dwelling', risk: 'High', controls: 'Part B fire-stopping at every penetration; intumescent collars on cables; pink fire-rated plasterboard 2× layer to spec; verify by building control before plaster.' },
    ],
    sequence: [
      'Pre-start — engineer\'s drawings; building control approval.',
      'Strip insulation; expose existing structure.',
      'Install steel beams (separate RAMS — glulam_steel_install).',
      'Install new floor joists per engineer; deck.',
      'Open up stair void; install trimmers.',
      'Erect dormer / install Velux (separate RAMS — dormer_construction / rooflight_install).',
      'Install fire-stopping at compartmentation lines.',
      'Stud partitions for new rooms.',
      'Stair install (separate RAMS — stairs_install).',
      'Hand to plumber / electrician / plasterer.',
    ],
    equipment: [
      'Mechanical hoist',
      'Reciprocating saw, circular saw',
      'Cordless drill / impact',
      'Spirit level (long)',
      'Adjustable temp props',
      'Crawl boards',
    ],
    ppe: [
      'Hard hat with chin-strap',
      'Safety boots',
      'Hi-vis Class 3',
      'Harness for unguarded',
      'Cut-resistant gloves',
      'Eye protection',
      'FFP3 mask in loft / dust',
    ],
    emergency:
      'Fall through ceiling — 999, do not move. Structural movement — clear, 999, contact engineer. Wet weather mid-build — emergency tarp; never leave open. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'basement_waterproofing',
    title: 'Basement waterproofing / install',
    trades: ['Foundations & concrete'],
    description: 'Basement install or waterproofing — Type A / B / C tanking systems to BS 8102.',
    defaultHazards: ['Excavation', 'Confined spaces', 'COSHH (chemicals)', 'Manual handling', 'Slips / trips'],
    scope:
      'Construct or waterproof a basement / cellar to BS 8102 — Type A (barrier protection / tanking), Type B (structurally integral), Type C (drained protection cavity). Includes excavation, structural shell, drainage, sumps, finishes.',
    hazards: [
      { hazard: 'Excavation collapse', risk: 'High', controls: 'Engineer-signed shoring; daily inspection; never enter unsupported >1.2 m, fully shored >2 m.' },
      { hazard: 'Confined-space conditions (low O₂, methane, CO₂)', risk: 'High', controls: 'Atmospheric monitoring on entry; rescue plan; tripod and harness if vertical access only.' },
      { hazard: 'Tanking solvents / membranes — fume', risk: 'High', controls: 'A1P3 cartridge mask; ventilate; LEV in confined cellar; SDS on file; never alone whilst applying.' },
      { hazard: 'Manual handling in cramped basement', risk: 'Med', controls: 'Pre-cut at the bench; mechanical lift for blocks / sheets; rotate.' },
      { hazard: 'Pump failure — water rises', risk: 'High', controls: 'Sump and twin pumps with float switch + alarm; UPS backup; daily inspection during works.' },
      { hazard: 'Slips on wet membrane / SLC during install', risk: 'Med', controls: 'Wet floor signs; clean drips; non-slip footwear.' },
    ],
    sequence: [
      'Pre-start — engineer\'s waterproofing strategy; ground / water survey.',
      'Excavate to depth; shore.',
      'Construct shell (concrete / blockwork) per Type B requirements.',
      'Apply waterproofing — Type A (cementitious slurry / liquid / sheet), Type B (built-in additive / shutter), Type C (drained cavity membrane to walls and floor).',
      'Drainage — perimeter channel, sump pit, twin pumps, alarm.',
      'Finishes — drylining / floor screed.',
      'Commission pump system; test alarm.',
      'Issue certificate; hand over with maintenance schedule (sump pumps annual service).',
    ],
    equipment: [
      'Excavator',
      'Concrete pump',
      'Tanking applicator (trowel / spray)',
      'Sump pumps (twin) + float switch + alarm',
      'Drainage channel (cavity drainage system)',
      'Atmospheric monitor',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis Class 3',
      'Cut-resistant gloves; nitrile under for chemicals',
      'Eye protection',
      'A1P3 cartridge mask',
      'Long-sleeved overalls',
    ],
    emergency:
      'Excavation collapse — DO NOT enter; dig from outside; 999. Pump failure / flooding — evacuate basement, contact specialist. Solvent fume overcoming operator — fresh air, 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'mobile_tower_scaffold',
    title: 'Mobile tower scaffold use',
    trades: ['Scaffolding'],
    description: 'Use of pre-fabricated mobile aluminium tower for short-duration works at height.',
    defaultHazards: ['Working at height', 'Manual handling', 'Slips / trips'],
    scope:
      'Erect, use and dismantle a pre-fabricated aluminium mobile tower (PASMA) for short-duration access work at height. Tower height limits per maker — typically 4 m platform indoor / 8 m outdoor.',
    hazards: [
      { hazard: 'Tower overturn — wind / unstable ground / over-reach', risk: 'High', controls: 'Stabilisers / outriggers fully deployed; never use without; ground level and firm; never reach beyond the platform edges; never use outdoors in wind >25 mph.' },
      { hazard: 'Falls during erection / when on platform', risk: 'High', controls: 'PASMA-trained erector; double guard-rail and toe-boards on platform; access via internal ladder; trapdoor closed when on platform; never climb the outside.' },
      { hazard: 'Tower moved with person on it', risk: 'High', controls: 'NEVER move tower with person on platform; descend, move, redeploy stabilisers, re-ascend.' },
      { hazard: 'Manual handling of tower components', risk: 'Med', controls: 'Pre-stage at point of use; rotate handlers.' },
      { hazard: 'Use beyond design height / loading', risk: 'High', controls: 'Tower used per manufacturer\'s instructions; loading limits respected; never extend beyond rated height.' },
    ],
    sequence: [
      'Pre-start — PASMA card; manufacturer\'s instructions; ground assessment.',
      'Erect tower per maker — base, stabilisers / outriggers, lifts, platform with guard-rail and toe-boards.',
      'Inspect — handrails, toe-boards, brace pattern, locked wheels.',
      'Tag green — name and date.',
      'Ascend via internal ladder; trapdoor closed when on platform.',
      'Carry out the work.',
      'When moving — descend; reposition; relock wheels and stabilisers; re-ascend.',
      'Dismantle in reverse; check for damage.',
    ],
    equipment: [
      'PASMA-rated tower',
      'PASMA card holder',
      'Spirit level for ground / outriggers',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves',
      'Eye protection',
    ],
    emergency:
      'Tower overturn — clear, 999. Fall from platform — 999. Wind exceeds 25 mph (outdoor) — descend, lower platform if safe to do so, secure or dismantle. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'conservatory_install',
    title: 'Conservatory / orangery install',
    trades: ['Carpentry & joinery'],
    description: 'Conservatory or orangery — base, dwarf walls, structural frame, glazing, roof.',
    defaultHazards: ['Manual handling', 'Working at height', 'Wet weather', 'Slips / trips'],
    scope:
      'Construct conservatory / orangery — concrete base / dwarf walls per Building Regs (or exempt size), aluminium / uPVC / timber frame, polycarbonate / glass / solid roof, glazing, finishes.',
    hazards: [
      { hazard: 'Manual handling of large frame sections and glazing', risk: 'High', controls: 'Two-person lift; mechanical aid for full-height units; suction cups for glass; planned route.' },
      { hazard: 'Glass breakage during handling', risk: 'High', controls: 'Carry on edge; never lay flat; heavy gloves rated for glass; tape over panes during transit.' },
      { hazard: 'Working at height for roof bars and glazed roof', risk: 'High', controls: 'Tower or scaffold; harness for unguarded; never on top of frame uprights.' },
      { hazard: 'Sealant fume and silicone overhead', risk: 'Med', controls: 'Ventilate; nitrile gloves; eye protection during overhead silicone.' },
      { hazard: 'Wet weather mid-build — water ingress to existing property', risk: 'High', controls: 'Tarpaulins to seal abutment to house; never leave open at end of shift; reschedule heavy rain; weather flash to existing wall first.' },
      { hazard: 'Cuts from frame edges and trim', risk: 'Med', controls: 'Cut-resistant gloves; deburr; eye protection.' },
    ],
    sequence: [
      'Pre-start — design / drawings; existing wall flashing strategy.',
      'Construct base and dwarf wall (separate RAMS for excavation / brickwork).',
      'Erect frame uprights from dwarf wall; bolt to base; verify plumb.',
      'Install eaves beam tying to existing wall.',
      'Install roof bars between eaves and ridge.',
      'Install glazed units — wall panels then roof; sealant and weatherproof.',
      'Install doors; check operation.',
      'Trim and finish; flashing to existing wall to weather seal.',
      'Internal trim and decoration (separate trades).',
    ],
    equipment: [
      'Suction cups for glass',
      'Cordless drill / impact, mitre saw',
      'Sealant gun',
      'Mechanical hoist for upper sections',
      'Spirit level (long), plumb bob',
      'Tower / scaffold',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis vest',
      'Heavy cut-resistant gloves rated for glass',
      'Eye protection',
      'FFP3 mask if cutting',
    ],
    emergency:
      'Glass breakage / cut — pressure, A&E. Fall from roof — 999. Heavy rain mid-build — tarp; protect interior of house. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'small_demolition',
    title: 'Small-scale demolition (single room / outbuilding)',
    trades: ['Demolition'],
    description: 'Demolish single-room extension, outbuilding, garage or internal wall — non-structural or with prior engineer sign-off.',
    defaultHazards: ['Asbestos', 'Manual handling', 'Silica dust', 'Plant / pedestrian interaction', 'Slips / trips'],
    scope:
      'Demolish small structure — single-room extension, garage, outbuilding, internal wall — to ground / first-fix. Excludes complex structural works (separate RAMS). Refurb / Demolition Survey on file.',
    hazards: [
      { hazard: 'Asbestos in older buildings', risk: 'High', controls: 'Refurb / Demolition Survey on file; do not strip suspect material; if uncovered — STOP, isolate, contact licensed contractor; pre-1999 = assume potential ACM until proven otherwise.' },
      { hazard: 'Structural collapse (wrong wall taken / load not transferred)', risk: 'High', controls: 'Engineer-signed if any wall has structural function; props in place; never assume — verify with engineer; for internal walls — check joist direction.' },
      { hazard: 'Live services hidden in walls', risk: 'High', controls: 'Isolate gas, water, electric at supply; lock off; voltage tester / cable detector before any wall penetration; service drawings.' },
      { hazard: 'Silica / gypsum dust', risk: 'High', controls: 'On-tool extraction; FFP3 mask; HEPA vac; never dry-sweep; sheet down adjacent rooms.' },
      { hazard: 'Plant / pedestrian interaction (skip / mini-digger)', risk: 'High', controls: 'Banksman; no-go zone around plant; sheet route to skip.' },
      { hazard: 'Falling debris through floors / on people below', risk: 'High', controls: 'Bagged debris; chute or bucket-down; no-go zone below; helmets mandatory below; stair-route signed.' },
    ],
    sequence: [
      'Pre-start — Refurb / Demolition Survey, engineer\'s sign-off if any structural wall, isolation of services.',
      'Sheet down area; cordon.',
      'Strip fixtures, fittings, finishes (separate strip-out RAMS).',
      'Strike out walls / structures progressively top-down.',
      'Bag debris; chute or controlled lower; load skip.',
      'Make safe — patch junctions, fire-stop, weather seal.',
      'Final clean; HEPA vac.',
    ],
    equipment: [
      'SDS drill / breaker / hand tools',
      'On-tool M-class extraction',
      'HEPA vacuum',
      'Floor protection / sheets',
      'Skip + chute / bucket',
      'Adjustable temp props',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves',
      'Eye protection',
      'FFP3 mask MANDATORY',
      'Type 5 overalls if AC suspected',
      'Hearing protection',
    ],
    emergency:
      'Suspected AC uncovered — STOP, isolate, contact licensed contractor. Structural movement — clear, 999, contact engineer. Service strike — STOP, isolate, 999 / 0800 111 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'ewi',
    title: 'External Wall Insulation (EWI)',
    trades: ['Insulation', 'Drylining & plastering'],
    description: 'External wall insulation — adhesive / mechanical fix board, mesh, basecoat, render finish.',
    defaultHazards: ['Working at height', 'COSHH (chemicals)', 'Manual handling', 'Wet weather'],
    scope:
      'Apply external wall insulation system to facade — substrate prep, adhesive / mechanical fix EPS / mineral wool board, reinforcement mesh, basecoat, primer and finish render. PAS 2030 / 2035 compliant.',
    hazards: [
      { hazard: 'Working at height — full elevation', risk: 'High', controls: 'Scaffold tagged within current week; double guard-rail and toe-boards; harness for unguarded.' },
      { hazard: 'Manual handling of EPS / mineral wool boards', risk: 'Med', controls: 'Two-person carry; lift to platform with hoist; pre-cut at the bench.' },
      { hazard: 'Adhesive / basecoat / render dust and fume', risk: 'Med', controls: 'A1P3 cartridge mask if dusty / spraying; nitrile gloves; eye protection; SDS on file.' },
      { hazard: 'Mineral wool fibres', risk: 'Med', controls: 'Type 5 overalls with hood; FFP3 mask; nitrile under work gloves; long sleeves cuffed; HEPA vac.' },
      { hazard: 'Falling material / tools onto people below', risk: 'High', controls: 'Toe-boards; debris netting; no-go zone below; helmets mandatory; tools on lanyards.' },
      { hazard: 'Wet weather causes adhesive / render failure', risk: 'High', controls: 'No application if rain forecast in next 6 hrs; surfaces dry; cover finished work with sheeting.' },
    ],
    sequence: [
      'Pre-start — substrate prep, scaffold tagged.',
      'Install starter track at base.',
      'Apply adhesive to boards; press to wall; offset joints.',
      'Mechanical fix per design (every 6 boards typical).',
      'Embed reinforcement mesh into basecoat; lap joints.',
      'Apply second coat to embed mesh fully.',
      'Primer and finish render coat.',
      'Final inspection; weather protection; remove scaffold once cured.',
      'Issue PAS 2030 install record / EPC update.',
    ],
    equipment: [
      'Scaffold (independently tagged)',
      'Notched trowels for adhesive',
      'Mechanical fixings (EWI screws + caps)',
      'Mortar mixer for basecoat / render',
      'Hawk and float',
      'Cordless drill',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis',
      'Type 5 overalls',
      'Nitrile gloves under cut-resistant',
      'Eye protection',
      'FFP3 mask MANDATORY',
      'A1P3 cartridge mask if spraying',
    ],
    emergency:
      'Fall from scaffold — 999. Adhesive / render in eye — flush 15+ min, A&E. Heavy rain mid-application — STOP, cover. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'timber_cladding',
    title: 'External timber cladding',
    trades: ['Cladding', 'Carpentry & joinery'],
    description: 'Cedar, larch, thermowood or composite timber cladding to facades — boards on battens.',
    defaultHazards: ['Working at height', 'Manual handling', 'COSHH (chemicals)', 'Slips / trips'],
    scope:
      'Install external timber or composite cladding to facade — counter-batten substrate, breathable membrane, vertical / horizontal cladding boards (cedar, larch, thermowood, composite), trim and corner detail.',
    hazards: [
      { hazard: 'Working at height on full elevation', risk: 'High', controls: 'Scaffold tagged; double guard-rail and toe-boards; harness for unguarded.' },
      { hazard: 'Manual handling of long boards', risk: 'High', controls: 'Two-person carry; lift to platform with hoist; pre-cut at the bench.' },
      { hazard: 'Cuts from saws and hand tools', risk: 'Med', controls: 'Sharp blades; clamp work; eye protection.' },
      { hazard: 'CCA / treated timber dust', risk: 'Med', controls: 'Cut outdoors; FFP3 mask; on-tool extraction; do not burn offcuts.' },
      { hazard: 'Falling boards / tools onto people below', risk: 'High', controls: 'Toe-boards; netting; no-go zone below; helmets mandatory; tools on lanyards.' },
      { hazard: 'Solvent / preservative fume during finishing', risk: 'Med', controls: 'A1P3 cartridge mask if applying solvent finishes; nitrile gloves; ventilate.' },
    ],
    sequence: [
      'Pre-start — design / drawings; scaffold tagged.',
      'Install breathable membrane to substrate; lap and tape.',
      'Install vertical counter-battens at design centres.',
      'Install horizontal battens (if cladding vertical) or vice versa.',
      'Set out first board to a level datum.',
      'Fix successive boards with concealed or face-fix per spec; respect expansion gap.',
      'Cut at corners and openings; detail trim.',
      'Apply finish (preservative / stain) per spec.',
    ],
    equipment: [
      'Scaffold',
      'Mitre saw, circular saw',
      'Cordless impact / nail gun',
      'Spirit level (long), plumb bob',
      'M-class extraction',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis',
      'Cut-resistant gloves',
      'Eye protection',
      'FFP3 mask during cutting',
      'A1P3 cartridge mask for solvent finishes',
    ],
    emergency:
      'Fall from scaffold — 999. Cut from saw — pressure, A&E. Solvent fume overcoming operator — fresh air, 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'composite_cladding',
    title: 'Composite / metal cladding',
    trades: ['Cladding'],
    description: 'Composite panel, fibre-cement, aluminium or zinc cladding to facade — rainscreen system.',
    defaultHazards: ['Working at height', 'Manual handling', 'Loud noise (>85 dB)', 'Hand-arm vibration (HAVS)'],
    scope:
      'Install rainscreen cladding to facade — engineer-designed sub-frame, ventilated cavity, breather membrane and composite / fibre-cement / aluminium / zinc panels. Includes corners, reveals and movement joints.',
    hazards: [
      { hazard: 'Panels falling from facade if mechanical fixing fails', risk: 'High', controls: 'Engineer-signed bracket / sub-frame system; pull-test brackets; never use plug-and-screw on lightweight blockwork; CWCT / NHBC compliant.' },
      { hazard: 'Working at height on full elevation', risk: 'High', controls: 'Scaffold tagged; harness for unguarded; toe-boards.' },
      { hazard: 'Manual handling of large panels (composite / fibre-cement heavy)', risk: 'High', controls: 'Two-person lift; suction handles for panels; mechanical hoist.' },
      { hazard: 'Loud noise from cutting / drilling', risk: 'High', controls: 'SNR ≥30 hearing protection; cordon; signage.' },
      { hazard: 'Cuts from sharp panel edges', risk: 'High', controls: 'Heavy cut-resistant gloves; deburr; eye protection.' },
      { hazard: 'Fibre-cement dust contains crystalline silica', risk: 'High', controls: 'On-tool M-class extraction; FFP3 mask MANDATORY; cut outdoors only; never dry-cut indoors.' },
    ],
    sequence: [
      'Pre-start — design / drawings; bracket pull-test on substrate.',
      'Install brackets per design; verify level and plumb.',
      'Install sub-frame rails.',
      'Install breather membrane.',
      'Mount panels to rails per design — concealed or visible fix per system.',
      'Install corners, reveals, movement joints.',
      'Inspect; document.',
    ],
    equipment: [
      'Scaffold',
      'Diamond saw / cutters',
      'Cordless drill / impact',
      'Suction handles',
      'M-class extraction',
      'Spirit level (long)',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis Class 3',
      'Heavy cut-resistant gloves',
      'Eye protection',
      'FFP3 mask MANDATORY for fibre-cement',
      'Hearing protection SNR ≥30',
    ],
    emergency:
      'Panel falls from facade — clear area, 999 if injury, contact engineer. Cut from panel edge — pressure, A&E. Fall from scaffold — 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'pergola_carport',
    title: 'Pergola / carport / canopy install',
    trades: ['Carpentry & joinery'],
    description: 'Timber, aluminium or composite pergola, carport or canopy — posts, beams, roof.',
    defaultHazards: ['Manual handling', 'Working at height', 'Wet weather', 'Slips / trips'],
    scope:
      'Construct freestanding or wall-attached pergola, carport or canopy — post bases, posts, beams, rafters, roof covering (slats / polycarbonate / metal sheet).',
    hazards: [
      { hazard: 'Falls from structure during install', risk: 'Med', controls: 'Tower or scaffold; never the top step of a stepladder; harness for unguarded.' },
      { hazard: 'Manual handling of long beams and posts', risk: 'Med', controls: 'Two-person lift; mechanical aid for steel; planned route.' },
      { hazard: 'Inadequate post fixing', risk: 'High', controls: 'Post bases on concrete pad to engineer\'s spec; never directly on lawn / soil; pull-test on completion.' },
      { hazard: 'Cuts from saws', risk: 'Med', controls: 'Sharp blades; clamp work; eye protection.' },
      { hazard: 'Hidden services in post-hole locations', risk: 'High', controls: 'CAT-detect; service drawings.' },
      { hazard: 'Wind loading on canopy', risk: 'Med', controls: 'Engineer-designed for wind zone; full bracing per spec; never erect in wind >25 mph.' },
    ],
    sequence: [
      'Pre-start — design; CAT-detect post locations.',
      'Excavate / install post bases; concrete in.',
      'Erect posts; plumb and brace.',
      'Install beams and rafters.',
      'Install roof covering (slats / sheet) per spec.',
      'Trim and finish; pull-test posts.',
    ],
    equipment: [
      'Auger / digging tools',
      'Concrete (post-mix)',
      'Mitre saw, circular saw',
      'Cordless drill / impact',
      'Spirit level (long)',
      'Tower / podium step',
      'CAT-detector',
    ],
    ppe: [
      'Hard hat (where site rules require)',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves',
      'Eye protection',
      'FFP3 mask during cutting',
    ],
    emergency:
      'Service strike — STOP, isolate, 999 / 0800 111 999. Cut from saw — pressure, A&E. Fall from height — 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'garage_build',
    title: 'Garage construction',
    trades: ['Bricklaying', 'Carpentry & joinery'],
    description: 'Single or double garage — foundations, masonry shell, roof, door, services.',
    defaultHazards: ['Excavation', 'Manual handling', 'Working at height', 'Plant / pedestrian interaction'],
    scope:
      'Construct single or double garage — foundations, oversite slab, masonry shell, roof structure and covering, garage door, services. Excludes fit-out (separate RAMS).',
    hazards: [
      { hazard: 'Excavation collapse', risk: 'High', controls: 'Battered or shored above 1.2 m; daily inspection; never enter unsupported.' },
      { hazard: 'Manual handling of bricks / blocks / lintels / roof structure', risk: 'High', controls: 'Two-person carry; mechanical lift; rotate.' },
      { hazard: 'Working at height for upper masonry and roof', risk: 'High', controls: 'Scaffold tagged; harness for unguarded; toe-boards.' },
      { hazard: 'Plant / pedestrian interaction (telehandler, mixer)', risk: 'High', controls: 'No-go zone; banksman; reverse alarms.' },
      { hazard: 'Garage door install — heavy and spring-loaded', risk: 'High', controls: 'Two-person install; never disassemble spring-loaded door without trained operative; eye protection.' },
      { hazard: 'Hidden services beneath excavation', risk: 'High', controls: 'CAT-detect; service drawings; Permit-to-Dig.' },
    ],
    sequence: [
      'Pre-start — drawings, services.',
      'Excavate foundations; pour concrete.',
      'Construct masonry shell (separate RAMS — brickwork).',
      'Install roof structure (separate RAMS — cut_roof / trussed_roof).',
      'Roof covering (separate RAMS — roof_tiling / flat_roof_felt).',
      'Install garage door — typically up-and-over or sectional.',
      'Services — power, lighting per Part P.',
      'Final inspection; weather seal.',
    ],
    equipment: [
      'Excavator',
      'Concrete mixer / pump',
      'Telehandler',
      'Mortar mixer',
      'Cordless drill / impact',
      'Tower / scaffold',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis Class 3',
      'Cut-resistant gloves',
      'Eye protection',
      'FFP3 mask during cutting',
      'Hearing protection',
    ],
    emergency:
      'Excavation collapse — 999. Service strike — STOP, isolate, 999 / 0800 111 999. Garage door spring failure — clear, 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'garden_room',
    title: 'Garden room / outbuilding (habitable)',
    trades: ['Carpentry & joinery'],
    description: 'Garden room, home office, studio — full habitable outbuilding with insulation and services.',
    defaultHazards: ['Manual handling', 'Working at height', 'Wet weather', 'Slips / trips'],
    scope:
      'Construct garden room / habitable outbuilding — concrete pad / piled base, timber-frame / SIPs / glulam structure, fully insulated walls / floor / roof, fully glazed bi-folds, electrical and data services.',
    hazards: [
      { hazard: 'Manual handling of structural panels (SIPs / glulam)', risk: 'High', controls: 'Mechanical hoist; two-person minimum; planned route; never single-handed.' },
      { hazard: 'Working at height for roof and high glazing', risk: 'Med', controls: 'Tower / scaffold; harness for unguarded.' },
      { hazard: 'Wet weather mid-build', risk: 'Med', controls: 'Tarp ready; never leave open at end of shift; reschedule heavy rain.' },
      { hazard: 'Mineral wool / insulation fibres', risk: 'Med', controls: 'Type 5 overalls; FFP3 mask; nitrile under cut-resistant.' },
      { hazard: 'Cuts from saws and hand tools', risk: 'Med', controls: 'Sharp blades; clamp work; eye protection.' },
      { hazard: 'Live electrical connection (Part P)', risk: 'Med', controls: 'Competent electrician; isolate at supply; RCD; sub-main from house CU per spec.' },
    ],
    sequence: [
      'Pre-start — design; base prepared.',
      'Floor cassette / structural sole; level checked.',
      'Erect wall panels / frame; brace.',
      'Roof structure; weather-tight.',
      'Insulation and breather membrane.',
      'Electrical 1st & 2nd fix (Part P).',
      'External cladding (separate RAMS).',
      'Internal lining, plaster, decoration.',
      'Glazing — bi-folds / windows installed weather-tight.',
      'Final inspection.',
    ],
    equipment: [
      'Mechanical hoist for panels',
      'Cordless drill / impact, mitre saw, circular saw',
      'Sequential-trip nailer',
      'Spirit level (long)',
      'Tower / scaffold',
      'M-class extraction',
    ],
    ppe: [
      'Hard hat',
      'Safety boots',
      'Hi-vis vest',
      'Type 5 overalls during insulation',
      'Cut-resistant gloves',
      'Eye protection',
      'FFP3 mask during cutting / insulation',
    ],
    emergency:
      'Fall from height — 999. Cut from saw — pressure, A&E. Heavy rain mid-build — emergency tarp; never leave open. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'garden_walls',
    title: 'Garden / boundary walls',
    trades: ['Bricklaying'],
    description: 'Brick or stone garden / boundary walls — typically 1.0–2.0 m high.',
    defaultHazards: ['Manual handling', 'Working at height', 'Silica dust', 'COSHH (chemicals)'],
    scope:
      'Build brick or stone boundary / garden wall — typically 1.0–2.0 m high — including foundations, courses, expansion joints (long runs), copings.',
    hazards: [
      { hazard: 'Manual handling of bricks / blocks / coping stones', risk: 'High', controls: 'Two-person carry for blocks >20 kg; mechanical aid for coping stones; rotate; pre-position at point of use.' },
      { hazard: 'Working at height for upper courses', risk: 'Med', controls: 'Scaffold tagged or trestle / boards for short walls; toe-boards if scaffold; harness for any unguarded >2 m.' },
      { hazard: 'Silica dust from cutting', risk: 'High', controls: 'Wet cutting; FFP3 mask; on-tool extraction.' },
      { hazard: 'Cement / lime burns', risk: 'Med', controls: 'Nitrile gloves under cut-resistant; immediate wash; eye protection.' },
      { hazard: 'Wall stability before mortar cure (especially long high walls)', risk: 'Med', controls: 'Brace tall walls overnight; expansion joints to design; never excessive height in single day.' },
      { hazard: 'Footing on existing trees roots / hidden services', risk: 'Med', controls: 'CAT-detect; service drawings; never excavate near tree roots without arboricultural advice.' },
    ],
    sequence: [
      'Pre-start — boundary line; CAT-detect; existing tree survey.',
      'Excavate foundation trench; concrete strip.',
      'First course; lay to line; level checked.',
      'Continue laying; ties / movement joints per design.',
      'Cap with copings / coping bricks; bed in mortar.',
      'Strike joints; brush face.',
      'Cover lime mortar with hessian to slow cure.',
    ],
    equipment: [
      'Concrete mixer',
      'Mortar mixer',
      'Wet bench saw / cut-off saw',
      'Trowels, lines, plumb-bob, levels',
      'Tower or trestles',
    ],
    ppe: [
      'Hard hat (where site rules require)',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant grip gloves; nitrile under',
      'Eye protection',
      'FFP3 mask during cutting',
    ],
    emergency:
      'Cement in eye — flush 15+ min, A&E. Wall topples — clear, 999 if injury. Cut from saw — pressure, A&E. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'external_steps_ramps',
    title: 'External steps & ramps',
    trades: ['Bricklaying', 'Foundations & concrete'],
    description: 'External steps, treads / risers and ramps — concrete, brick, stone or timber. Part M compliant.',
    defaultHazards: ['Manual handling', 'COSHH (chemicals)', 'Working at height', 'Slips / trips'],
    scope:
      'Construct external steps / ramps — concrete, brick, stone or timber. Part M compliant where required. Includes handrails to step / ramp.',
    hazards: [
      { hazard: 'Manual handling of stone / pre-cast steps', risk: 'High', controls: 'Two-person lift for blocks >25 kg; mechanical aid (telehandler); rotate.' },
      { hazard: 'Cement burns / dermatitis', risk: 'Med', controls: 'Nitrile gloves under cut-resistant; immediate wash; eye protection.' },
      { hazard: 'Falls during install of upper steps', risk: 'Med', controls: 'Tower or boards; harness for unguarded >2 m; never balanced on uncured step.' },
      { hazard: 'Slips on wet concrete / stone during install', risk: 'Med', controls: 'Anti-slip nosing per Part M; non-slip footwear; clean drips.' },
      { hazard: 'Part M non-compliance — wrong rise / going / handrail', risk: 'Med', controls: 'Set out checked against Part M — rise ≤170 going ≥250 (max for new build steps); handrail 900–1000mm; ramp gradient ≤1:12.' },
    ],
    sequence: [
      'Pre-start — design / Part M dimensions verified.',
      'Excavate to step / ramp foundation depth.',
      'Pour foundation slab; cure.',
      'Construct treads / risers (concrete in-situ, brickwork, pre-cast or stone).',
      'Anti-slip nosing per Part M.',
      'Install handrails / balustrades; pull-test.',
      'Final point; brush off.',
    ],
    equipment: [
      'Concrete mixer / formwork',
      'Mortar mixer',
      'Telehandler / sack truck',
      'Spirit level',
      'Cordless drill / impact for handrail brackets',
    ],
    ppe: [
      'Hard hat (where site rules require)',
      'Safety boots',
      'Hi-vis vest',
      'Cut-resistant gloves; nitrile under',
      'Eye protection',
      'FFP3 mask if cutting',
    ],
    emergency:
      'Cement in eye — flush 15+ min, A&E. Stone dropped on foot — assess, A&E if any deformity. Fall from upper step during install — 999. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  {
    id: 'driveway_excavate',
    title: 'Driveway excavation & sub-base',
    trades: ['Groundworks'],
    description: 'Excavate driveway, set falls and lay Type 1 sub-base ready for surfacing.',
    defaultHazards: ['Plant / pedestrian interaction', 'Excavation', 'Loud noise (>85 dB)', 'Manual handling'],
    scope:
      'Excavate existing driveway / new driveway area to depth; set out falls; lay and compact Type 1 / 803 sub-base ready for tarmac, block paving, slabbing or resin-bound finish.',
    hazards: [
      { hazard: 'Hidden services beneath driveway', risk: 'High', controls: 'Service drawings; CAT-scan over the area; Permit-to-Dig; hand-dig in service zones.' },
      { hazard: 'Plant / pedestrian interaction', risk: 'High', controls: 'Banksman; cordoned work area; signage from public footpath; no-go zone around plant.' },
      { hazard: 'Manual handling of edge stone / kerbs', risk: 'Med', controls: 'Two-person carry; pre-position; mechanical aid for kerbs.' },
      { hazard: 'Loud noise from compactor', risk: 'High', controls: 'SNR ≥30 hearing protection; cordon; respect noise hours.' },
      { hazard: 'Hand-arm vibration from compactor', risk: 'Med', controls: 'Low-vibration plate; rotate operator; trigger-time managed.' },
      { hazard: 'Excavation collapse if deep dig', risk: 'Med', controls: 'Battered or shored above 1.2 m; never enter unsupported.' },
    ],
    sequence: [
      'Pre-start — survey, falls plan, drainage point, services.',
      'CAT-scan; service drawings; Permit-to-Dig.',
      'Strip existing surface (if refurb); skip / chute.',
      'Excavate to design depth; check ground bearing; reduce if soft.',
      'Set out falls and edges.',
      'Lay Type 1 / 803 sub-base in 100 mm layers; compact each.',
      'Verify compaction; final levels.',
      'Hand to surfacing trade (block paving, tarmac, resin-bound).',
    ],
    equipment: [
      'Mini-excavator',
      'Vibrating plate compactor',
      'Tipper / dumper',
      'Spirit level (long), laser level',
      'CAT-scan + Genny',
      'String line and pegs',
    ],
    ppe: [
      'Hard hat',
      'Safety boots with mid-sole',
      'Hi-vis Class 3',
      'Cut-resistant gloves',
      'Eye protection',
      'Hearing protection SNR ≥30',
      'FFP3 mask if dusty',
    ],
    emergency:
      'Service strike — STOP, isolate, 999 / 0800 111 999 (gas). Excavation collapse — 999. Vehicle in work zone — STOP, cordon. Site emergency contacts in welfare cabin.',
  },

  // ============================================================
  // LANDSCAPING / GROUNDS — added per Will's request, May 2026
  // ============================================================

  {
    id: 'patios_paths_driveways',
    title: 'Patios, paths & driveways — block paving, flagstones, slabs',
    trades: ['Landscaping', 'Bricklaying'],
    description: 'External hard surfaces — sub-base prep, edge restraint, laying block paving / flagstones / slabs / setts, jointing.',
    defaultHazards: ['Manual handling', 'Cutting (silica dust)', 'Plant / pedestrian interaction', 'Slips / trips'],
    scope:
      'Construct external hard surfaces per drawings — excavate to formation, lay sub-base (Type 1, MOT), set edge restraints, lay block paving / flagstones / slabs / setts, cut to fit, joint with kiln-dried sand or resin slurry.',
    hazards: [
      { hazard: 'Manual handling of slabs and aggregate', risk: 'High', controls: 'Two-person lifts above 25 kg; mechanical aid (mini digger / bobcat) for bulk material; stack at waist height; slab-grabbers for large flags; correct lifting technique briefed.' },
      { hazard: 'Silica dust from cutting blocks / slabs', risk: 'High', controls: 'On-tool water suppression for cut-off saws; never dry-cut on site; FFP3 RPE; positioned downwind of others; cut outside or in well-ventilated area.' },
      { hazard: 'Cement burns / dermatitis from mortar and grout', risk: 'Med', controls: 'Nitrile gloves; wash skin promptly with cool water; barrier cream; eye-wash kit nearby; long sleeves.' },
      { hazard: 'Trips on uneven sub-base / partially-laid surface', risk: 'Med', controls: 'Edge-mark exposed work areas; complete sections in same shift where practical; no tools left in walkways.' },
      { hazard: 'Plant manoeuvring on tight access', risk: 'High', controls: 'Banksman in hi-vis for any reverse / lift; pedestrians cleared from operating zone; reverse alarm working before any movement; defined exclusion zone marked.' },
      { hazard: 'Working below ground (services / drainage)', risk: 'Med', controls: 'Service drawings reviewed; CAT-scan before any excavation > 300 mm; Permit-to-Dig for connections; existing drainage protected.' },
      { hazard: 'Vibration injury (HAVS) from compaction plate', risk: 'Med', controls: 'Trigger time monitored; rotate operators every 30 min; anti-vibration gloves; report tingling / numbness immediately.' },
    ],
    sequence: [
      'Pre-start — drawing review, access route, material drop point, Permit-to-Dig for any new excavation.',
      'Set out levels, falls and edge lines.',
      'Excavate to formation; dispose of arisings.',
      'Lay sub-base (Type 1) and compact in 100 mm layers with whacker.',
      'Set edge restraints in concrete haunching.',
      'Lay paving / flagstones / slabs to bond pattern; cut units off-site or with on-tool water; never dry-cut.',
      'Joint with kiln-dried sand (block paving) or resin slurry / mortar (slabs) per spec.',
      'Hose / sweep clean; remove arisings; tools secured.',
    ],
    equipment: [
      'Mini digger / bobcat (CPCS-carded operator)',
      'Whacker plate (PUWER inspected)',
      'Cut-off saw with on-tool water (PAT in date)',
      'Slab-grabbers / suction lifters',
      'String lines, levels, mortar boards',
    ],
    ppe: [
      'Hi-vis · safety boots · gloves · safety glasses',
      'FFP3 RPE for any cutting',
      'Knee pads when laying',
      'Hearing protection during cutting / compaction',
    ],
    emergency:
      'Service strike — STOP, isolate, 999 / 0800 111 999 (gas). Cement in eye — irrigate 15 min, A&E. Crush injury from heavy slabs — 999. Site emergency contacts in welfare cabin.',
  },

  {
    id: 'kerbs_retaining_walls',
    title: 'Kerbs & low retaining walls',
    trades: ['Landscaping', 'Bricklaying'],
    description: 'Lay kerbs, edging stones and small retaining walls (up to 1.2 m) — concrete haunching, brick / block / sleeper walls, drainage behind.',
    defaultHazards: ['Manual handling', 'Cement burns', 'Plant / pedestrian interaction', 'Trapped fingers'],
    scope:
      'Set kerbs / edging stones along surfacing edges; build small retaining walls in brick, block or timber sleeper up to 1.2 m high. Includes concrete haunching, drainage gravel and weep-holes behind, capping where specified.',
    hazards: [
      { hazard: 'Manual handling of kerbs / sleepers / blocks', risk: 'High', controls: 'Two-person carry above 25 kg; kerb-lifters or mechanical aid (mini digger with chain) for full-size kerbs and sleepers; stack at waist height.' },
      { hazard: 'Trapped fingers when bedding kerbs', risk: 'Med', controls: 'Crowbar / pinch bar to position the last 50 mm; never push with bare fingers; rigger gloves; gloves checked for cuts and replaced when worn.' },
      { hazard: 'Cement burns from concrete haunching', risk: 'Med', controls: 'Nitrile / wellie gloves; wash skin promptly; eye-wash on hand; long sleeves; barrier cream.' },
      { hazard: 'Wall collapse during build (low retaining)', risk: 'High', controls: 'Build to engineer\'s detail; never exceed 1.2 m without engineer sign-off; foundations dug to spec; weep-holes and drainage gravel installed; brace timber sleeper walls until top fixings cured.' },
      { hazard: 'Working in trench at base of retaining wall', risk: 'High', controls: 'Battered or shored sides for excavations > 1.2 m; daily inspection; no entry below shoring; ladder access secured.' },
      { hazard: 'Plant lifting kerbs over operatives', risk: 'High', controls: 'Banksman directs; nobody under suspended load; chains / slings LOLER inspected; tagline used to control swing.' },
      { hazard: 'Cutting kerbs / sleepers (silica / saw kickback)', risk: 'Med', controls: 'On-tool water for stone; never dry-cut; FFP3 RPE; saw guards in place; cut on stable surface, not on the leg.' },
    ],
    sequence: [
      'Pre-start — drawings, foundation depth, drainage detail, kerb / wall material on site.',
      'Excavate trench foundation to spec — battered or shored if > 1.2 m.',
      'Pour concrete foundation; allow to set.',
      'Lay first course — kerbs in concrete haunching, or brick / block on mortar bed, or sleepers fixed to footings.',
      'Course up — full bond, weep-holes per spec, drainage gravel and geo-textile behind retaining walls.',
      'Cap or coping installed; pointing finished.',
      'Backfill behind retaining wall in 200 mm layers; compact lightly.',
      'Clean tools, remove arisings.',
    ],
    equipment: [
      'Mini digger with chain / sling (LOLER cert)',
      'Kerb-lifter / pinch bar / sleeper claws',
      'Mortar boards, trowels, spirit levels',
      'Cut-off saw with on-tool water',
      'Trench shoring kit if depth > 1.2 m',
      'Drainage gravel and geo-textile',
    ],
    ppe: [
      'Hi-vis · safety boots · rigger gloves · safety glasses · helmet',
      'FFP3 RPE for cutting',
      'Wellies for concrete work',
      'Knee pads when bedding kerbs',
    ],
    emergency:
      'Wall collapse / falling material — STOP, 999. Cement in eye — irrigate 15 min, A&E. Trapped finger crush — first aid, A&E if loss of feeling / blood. Trench collapse — STOP, 999, do NOT enter to rescue. Site emergency contacts in welfare cabin.',
  },

  {
    id: 'turf_laying',
    title: 'Turf laying',
    trades: ['Landscaping'],
    description: 'Final-grade preparation, fertiliser, turf-laying and watering-in for lawn establishment.',
    defaultHazards: ['Manual handling', 'Slips / trips', 'Working at edges'],
    scope:
      'Lay rolled turf to a prepared topsoil base — final grade, light fertilisation, lay turves to bond pattern, butt-joint, roll, water-in. Includes seeded areas where specified.',
    hazards: [
      { hazard: 'Manual handling — turves are heavy when wet', risk: 'Med', controls: 'Two-person carry of pallets; barrow turves to laying point in singles; stretch / take breaks; rotate jobs.' },
      { hazard: 'Slips on wet turf / muddy access', risk: 'Med', controls: 'Defined access route; boards over soft ground; non-slip footwear; no running.' },
      { hazard: 'Manual rolling — back / repetitive strain', risk: 'Low', controls: 'Light water-roller or push-roller; alternate operators every 30 min; avoid heavy water in roller on slopes.' },
      { hazard: 'Skin contact with fertiliser', risk: 'Low', controls: 'Gloves when applying; wash hands before eating; apply per maker spec; do not over-apply near watercourses.' },
      { hazard: 'Plant / public on adjacent paths', risk: 'Low', controls: 'Cordon laying area; signage if work is on a public verge / shared garden.' },
    ],
    sequence: [
      'Pre-start — confirm topsoil to spec, depth and grading.',
      'Light tilling / raking to final grade; clear stones >25 mm.',
      'Apply pre-turf fertiliser per spec; rake in.',
      'Lay turves in stretcher bond, butt-jointed; cut at edges with knife.',
      'Roll lightly with garden roller (NOT plant roller).',
      'Water-in to penetrate root zone; brief client on watering schedule.',
      'Clean access route; remove pallets and offcuts.',
    ],
    equipment: [
      'Garden roller (manual)',
      'Wheelbarrows',
      'Turfing knife / hand saw',
      'Rake, mattock, stiff broom',
      'Hose with rose / sprinkler',
    ],
    ppe: ['Hi-vis · gloves · boots · long trousers', 'Knee pads when laying', 'Sun screen / hat for outdoor work'],
    emergency:
      'Manual handling injury — STOP, first aid, A&E if back / acute. Allergic reaction to turf / fertiliser — first aid, A&E. Site emergency contacts in welfare cabin.',
  },

  {
    id: 'planting_trees_shrubs',
    title: 'Planting — trees, shrubs, hedges',
    trades: ['Landscaping'],
    description: 'Tree pit excavation, planting, staking, shrub bed planting, hedge planting and irrigation install.',
    defaultHazards: ['Manual handling', 'Hidden services', 'Cuts from tools', 'Slips / trips'],
    scope:
      'Plant supplied trees, shrubs and hedges per planting plan. Includes tree pit excavation, root-ball placement, staking and tying, mulching, watering-in. Where specified, install drip-line irrigation.',
    hazards: [
      { hazard: 'Hidden services beneath tree pits', risk: 'High', controls: 'Service drawings + CAT-scan for any pit > 300 mm; trial-pit by hand for first 300 mm if uncertain; STOP and confirm if any service detected.' },
      { hazard: 'Manual handling of root-balled trees', risk: 'High', controls: 'Mechanical aid (telehandler / digger with chains) for trees > 1.5 m; two-person lift below that with proper grip; never lift by trunk alone.' },
      { hazard: 'Cuts from secateurs / pruning saws', risk: 'Med', controls: 'Sharp tools cut better and safer than blunt; cut-resistant gloves when pruning; tools sheathed when not in use.' },
      { hazard: 'Allergic reaction to plants / sap', risk: 'Low', controls: 'Brief on irritant species (e.g. Euphorbia, Rue, Prunus laurocerasus); cover skin; eye protection when pruning at face level; wash promptly if sap contact.' },
      { hazard: 'Working near boundaries / public access', risk: 'Med', controls: 'Cordon planting zones; signage if work on public verge; stakes flagged hi-vis.' },
      { hazard: 'Stake hammering — flying debris / hand strikes', risk: 'Med', controls: 'Eye protection; never hammer into ground over a pipe-run; second person holds stake with tongs / lump hammer rest.' },
    ],
    sequence: [
      'Pre-start — planting plan, set-out marks, service drawings reviewed, CAT-detector ready.',
      'Mark tree pits; CAT-scan before any pit > 300 mm.',
      'Excavate pits — by hand for first 300 mm if services likely, then mini-digger.',
      'Place tree root-ball; check rootflare at finish level; backfill with topsoil + compost.',
      'Drive stakes (twin or single) outside root-ball; tie with rubber tree tie.',
      'Plant shrubs / hedge whips at correct spacing; firm in.',
      'Mulch beds 50–75 mm; keep clear of stems.',
      'Install irrigation drip-line if specified.',
      'Water-in thoroughly; brief client on after-care.',
    ],
    equipment: [
      'Mini digger (CPCS A58)',
      'Telehandler with lifting chain (LOLER cert)',
      'Spades, forks, mattock',
      'Sharp secateurs and pruning saw',
      'CAT-detector for any planting > 300 mm depth',
      'Tree stakes, ties, mulch fork',
    ],
    ppe: ['Hi-vis · gloves · safety boots · safety glasses', 'Cut-resistant gloves when pruning', 'Long sleeves around irritant species'],
    emergency:
      'Service strike — STOP, isolate, 999 / 0800 111 999 (gas). Eye / skin injury from sap or pruning — irrigate, first aid, A&E if persists. Manual handling injury — STOP, A&E. Site emergency contacts in welfare cabin.',
  },

  {
    id: 'grounds_maintenance',
    title: 'Grounds maintenance — mowing, hedging, weeding',
    trades: ['Landscaping'],
    description: 'Ongoing maintenance of soft and hard landscape — mowing, strimming, hedge cutting, weeding, leaf clearance.',
    defaultHazards: ['Cutting (rotating blades)', 'Flying debris', 'Slips / trips', 'Manual handling', 'Noise'],
    scope:
      'Routine grounds-maintenance visits per agreed schedule — mow lawns, strim edges, cut hedges, clear weeds and arisings, blow / collect leaves, light pruning. Excludes major tree work and chemical herbicide application unless covered by separate RAMS.',
    hazards: [
      { hazard: 'Rotating blades — mower / strimmer / hedgecutter', risk: 'High', controls: 'Operator trained on each machine; guards in place before any start-up; never adjust with engine running; isolate / remove key before clearing blockages.' },
      { hazard: 'Flying debris — stones, twigs, glass', risk: 'High', controls: 'Site walked / cleared of foreign objects before mowing; eye protection mandatory; visor for strimming; public / pets cleared 15 m from line of cut.' },
      { hazard: 'Vibration injury (HAVS) from extended hand-held use', risk: 'Med', controls: 'Trigger time monitored — max 2 hr/day on hedgecutter; rotate operators; anti-vibration gloves; report tingling / numbness immediately.' },
      { hazard: 'Noise above 85 dB(A)', risk: 'Med', controls: 'Hearing protection mandatory for petrol kit; warn nearby occupants; quieter electric kit where practical.' },
      { hazard: 'Working near pedestrians / pets / children', risk: 'High', controls: 'Cordon work area; pause and wait for people to clear; never strim with anyone within 15 m; client briefed on schedule.' },
      { hazard: 'Petrol storage / refuelling', risk: 'Med', controls: 'Refuel on cool engine away from ignition; suitable container with breather cap; spill kit in van; no smoking.' },
      { hazard: 'Slips / trips on slopes and wet grass', risk: 'Med', controls: 'Mow across slopes (not up / down) on ride-ons; non-slip boots; do not work in heavy rain; assess wet conditions each visit.' },
    ],
    sequence: [
      'Arrive on site — visual walk-around, clear foreign objects, identify hazards (sleeping children\'s toys, dog faeces, broken glass).',
      'Set out cordons / signage if working in public-access areas.',
      'Pre-use check on each machine — guards, fuel, sharp blades, no leaks.',
      'Mow / strim / hedge-cut per schedule; rotate tasks for HAVS / noise control.',
      'Collect arisings into wheeled bins; remove from site or to designated heap.',
      'Final walk-around — check kit, lock van, brief client on anything spotted.',
    ],
    equipment: [
      'Petrol mower / ride-on (PUWER, in date)',
      'Strimmer / hedgecutter (anti-vib model preferred)',
      'Leaf blower',
      'Wheelbarrows / wheeled bins',
      'Petrol container (BS 2050) + spill kit',
    ],
    ppe: [
      'Hi-vis · steel-toe boots · gloves · safety glasses or visor',
      'Hearing protection (mandatory for petrol kit)',
      'Anti-vibration gloves for hedgecutter / strimmer',
      'Cut-resistant trousers / chaps when chainsaw used',
    ],
    emergency:
      'Cut from blade — pressure to wound, 999 if arterial. Eye injury from debris — irrigate, A&E. Petrol fire — extinguisher in van, 999, evacuate downwind. Vibration symptoms — stop work, log, GP referral. Site emergency contacts in welfare cabin / van.',
  },

  {
    id: 'manhole_installation',
    title: 'Manhole / inspection chamber installation',
    trades: ['Groundworks', 'Drainage'],
    description: 'Excavate, install and benching pre-cast or plastic manhole / inspection chamber on a drainage run.',
    defaultHazards: ['Excavation collapse', 'Falls into excavation', 'Manual handling', 'Hidden services', 'Confined space'],
    scope:
      'Excavate a chamber to depth, install pre-cast concrete or plastic manhole / inspection chamber sections, benching in concrete, fit cover and frame to finished level. Connect inlet / outlet drainage pipework. Pressure / air test where required before backfill.',
    hazards: [
      { hazard: 'Excavation collapse — chamber pits often deeper than open trenches', risk: 'High', controls: 'Battered sides, trench box, or proprietary shoring for depths > 1.2 m or unstable ground; soils competent person review; daily inspection log per CDM 2015 reg 22.' },
      { hazard: 'Falls into open excavation', risk: 'High', controls: 'Heras fence + plywood toe-board around perimeter; ladder secured 1 m above lip; never leave open overnight without barriers and lights.' },
      { hazard: 'Hidden services around drainage runs', risk: 'High', controls: 'Service drawings AND CAT-scan; trial pit by hand to expose existing services within 1 m; Permit-to-Dig signed before any plant excavation.' },
      { hazard: 'Manual handling of pre-cast rings (200–500 kg each)', risk: 'High', controls: 'Excavator with chain/sling and lifting eye — NEVER manual placement; banksman directs; nobody under suspended load; CPCS-carded operator.' },
      { hazard: 'Confined-space risk if depth > 1.5 m or atmosphere uncertain', risk: 'High', controls: 'Treat as confined space — Permit-to-Enter, gas monitor (O₂, CO, H₂S, LEL), tripod and rescue plan, attendant at top; never enter alone.' },
      { hazard: 'Cement burns from benching concrete', risk: 'Med', controls: 'Nitrile / wellie gloves; eye-wash; wash skin promptly; rotate concrete duties.' },
      { hazard: 'Lifting plant on soft ground / near excavation edge', risk: 'High', controls: 'Excavator stabilised on level firm ground or mats; minimum 1 m from excavation edge; no slewing over operatives.' },
    ],
    sequence: [
      'Pre-start — drawings, service info, CAT-scan, Permit-to-Dig signed; lift plan agreed; rescue plan if confined.',
      'Set out chamber position; mark exclusion zone for plant.',
      'Excavate to depth — battered or shored as soils require; daily inspection log.',
      'Lay concrete bedding to base; level.',
      'Lift pre-cast rings or position plastic chamber with excavator + chains; banksman directs.',
      'Form benching to channels and incoming / outgoing pipes per gradient.',
      'Connect drainage runs in / out; test (air or water) per spec.',
      'Cover and frame set to finished level on engineering brick or polymer adjustment.',
      'Backfill in 200 mm layers, compacted with whacker; reinstate surface to match.',
    ],
    equipment: [
      '8-ton+ excavator with chain / sling (LOLER)',
      'Trench box or proprietary shoring kit',
      'Whacker plate',
      'Pipe-bedding gravel',
      'Concrete (hand-mix or ready-mix for benching)',
      'Heras fence + plywood toe-board',
      'Confined-space kit if applicable — gas monitor, tripod, harness, rescue plan',
      'Drainage test plugs / air-test kit',
    ],
    ppe: ['Hi-vis · safety boots (rigger) · gloves · safety glasses · helmet', 'Wellies for concrete benching', 'Confined-space PPE if entry required'],
    emergency:
      'Trench collapse — STOP, 999, do NOT enter to rescue. Service strike — STOP, isolate, 999 / 0800 111 999 (gas). Suspended load failure — clear area, 999 if injury. Confined-space casualty — DO NOT ENTER, alert attendant, follow rescue plan, 999. Site emergency contacts in welfare cabin.',
  },

  {
    id: 'golf_course_greenskeeping',
    title: 'Golf course greenskeeping — greens, tees, fairways, bunkers',
    trades: ['Greenskeeping', 'Groundskeeping', 'Golf course maintenance'],
    description: 'Daily and weekly maintenance of a golf course — mowing greens, tees and fairways, pin changing, bunker raking, topdressing, light verticutting / hollow tining.',
    defaultHazards: ['Rotating blades', 'Flying debris', 'Working near members of the public (golfers)', 'Manual handling', 'Noise', 'Working alone'],
    scope:
      'Routine course-presentation work — greens, tees and fairway mowing with pedestrian and ride-on mowers; pin and cup changing; bunker raking; light topdressing and brushing-in; pedestrian-operated verticutting / hollow tining; divot filling; collection of arisings. Excludes pesticide / herbicide / fungicide application (separate RAMS), major irrigation work, and any tree work.',
    hazards: [
      { hazard: 'Rotating blades on greens / fairway mowers and pedestrian units', risk: 'High', controls: 'Operator certified per LANTRA / City & Guilds NPTC unit relevant to the machine class (pedestrian mower / ride-on / triplex); pre-use guard check signed off on daily inspection sheet; never adjust or unblock with engine running; key removed and parking brake applied before any clearance; turn-on-edge only when blades disengaged.' },
      { hazard: 'Flying debris (stones, acorns, lost balls) from rotaries and rough cutters', risk: 'High', controls: 'Walk-over course area before mowing — pick up debris and lost balls; eye protection mandatory; never mow when golfers within 50 m of line of cut; warning signs / cones at entry to the green being worked.' },
      { hazard: 'Working alongside golf play — risk of being struck by a ball', risk: 'High', controls: 'Daily comms with starter / pro shop on tee times and group locations; hi-vis worn at all times; right-of-way etiquette per R&A guidance — work moves aside for play, NEVER work into the line of an approaching group; "Fore" called and acknowledged on parallel holes; no headphones / earbuds on the course.' },
      { hazard: 'Lone working on remote parts of the course', risk: 'Med', controls: 'GPS-tracker or lone-worker app (e.g. Workrest / StaySafe / equivalent) issued to every operative working alone, with automatic check-in interval set; two-way radio or mobile phone as backup; written lone-worker SOP — never on the back nine alone without tracker confirmation; emergency contact tree in the shed.' },
      { hazard: 'Ride-on mower roll-over on slopes / bunker edges', risk: 'High', controls: 'ROPS / seat belt in use on every ride-on; operator holds LANTRA / NPTC unit for ride-on (e.g. City & Guilds 0095 or equivalent); mow up-and-down slopes < 15° only; on steeper banks switch to pedestrian / hand-held; bunker faces walked, not mown; speed reduced near pond / lake edges.' },
      { hazard: 'Vibration (HAVS) from extended use of pedestrian mowers / hand tools', risk: 'Med', controls: 'Trigger-time monitored against HSE exposure action / limit values (2.5 / 5 m/s² A(8)); rotation between operatives; anti-vibration gloves; report tingling / numbness immediately; annual HAVS health surveillance for greenkeepers per HSG88.' },
      { hazard: 'Brush cutter / strimmer operation', risk: 'Med', controls: 'Operator certified under City & Guilds NPTC unit 31 (brush cutter / strimmer) or equivalent; correct head fitted for task; 15 m exclusion zone maintained while running.' },
      { hazard: 'Manual handling of pin flags, hole cups, bunker rakes, topdressing bags', risk: 'Med', controls: 'Topdressing bags split or moved on barrow / utility cart; correct lifting technique briefed (MAC tool reference); cups changed in pairs where ground is hard.' },
      { hazard: 'Petrol storage / refuelling in the shed and on the course', risk: 'Med', controls: 'Refuel on cool engine away from ignition; BS 2050 cans with breather cap; spill kit on every utility cart; no smoking; bulk petrol stored in a vented metal cabinet covered by a documented Fire Risk Assessment under the Regulatory Reform (Fire Safety) Order 2005; FRA reviewed annually or after any incident.' },
      { hazard: 'Disease cross-contamination between greens (dollar spot, take-all patch, anthracnose)', risk: 'Med', controls: 'Cylinder mowers and pedestrian units cleaned down with brush + low-pressure water between greens during a known disease pressure period (per Course Manager / agronomist guidance); separate kit allocated to any quarantined green; cleaning log maintained.' },
      { hazard: 'Slips / trips on dewy greens, wet bunkers, irrigation covers', risk: 'Med', controls: 'Boots with aggressive tread; cup-cutter spikes never left exposed; bunker rakes laid flat at the back of the bunker; irrigation valve box lids checked closed at end of shift.' },
    ],
    sequence: [
      'Pre-start in the shed — daily team brief, weather check, machinery walk-round, fuel / oil / blade condition signed off on the inspection sheet.',
      'Confirm tee times with starter / pro shop — sequence cutting to stay ahead of play.',
      'Move to first task — typically greens mowing at dawn (no golfers, dew still on grass).',
      'On each green — walk it, remove debris, change pin position to the day\'s pattern, mow / brush, finish edges, refit flag.',
      'Bunker work — rake to course standard, check for animal damage, replace any worn rakes.',
      'Tees — divot fill, mow, move tee markers to today\'s position; pull broken tees from box.',
      'Fairways and rough — mow with ride-on; never into oncoming play; pause and wave through any group catching you.',
      'End of round — return all kit to shed, clean down, brief next-day pin / cup positions on the board, log any course incidents (struck by ball, near miss, irrigation leak, member complaint).',
    ],
    equipment: [
      'Pedestrian greens mower (cylinder) — daily',
      'Triplex / ride-on greens mower',
      'Tee mower (cylinder, pedestrian)',
      'Fairway mower (ride-on, multi-deck)',
      'Rough / surround rotary mower (ride-on)',
      'Hand-held strimmer / blower',
      'Hollow-tiner / verticutter (pedestrian + tractor-mounted)',
      'Topdressing spreader',
      'Utility cart (e.g. Cushman / Toro Workman) with rakes, brooms, divot mix, water',
      'Two-way radio per operative',
      'Petrol container (BS 2050) + spill kit on every cart',
    ],
    ppe: [
      'Hi-vis vest or jacket — mandatory at all times on the course',
      'Safety boots with aggressive tread',
      'Safety glasses (mandatory for rotary, strimmer, blower work)',
      'Hearing protection (mandatory for petrol / ride-on kit)',
      'Anti-vibration gloves for hand-held kit',
      'Sunhat + factor-30 sun cream issued in summer; waterproofs in winter',
    ],
    emergency:
      'Struck by ball — STOP, assess casualty (head injury rules apply), 999 if loss of consciousness or persistent symptoms. Lone-worker no-check-in — radio first, drive out to last known position if no response within 15 min. Cut from blade — pressure to wound, 999 if arterial. Eye injury from debris — irrigate, A&E. Heat exhaustion in summer — shade, water, course manager notified. Petrol fire — extinguisher on each cart, 999, evacuate downwind. Vibration symptoms — stop work, log, GP referral. Course emergency contacts on the shed board and on every utility cart.',
  },

  {
    id: 'golf_course_chemical_application',
    title: 'Golf course chemical application — fungicide, herbicide, wetting agent',
    trades: ['Greenskeeping', 'Groundskeeping', 'Golf course maintenance'],
    description: 'Application of approved fungicides, selective herbicides, wetting agents and growth regulators to greens, tees and fairways using boom sprayer or knapsack. Requires PA1 + PA6 certification.',
    defaultHazards: ['Chemical exposure', 'Spray drift', 'Member / public exposure', 'Slips on wet greens', 'Manual handling of concentrate'],
    scope:
      'Application of HSE-approved Plant Protection Products (pesticide / herbicide / fungicide), wetting agents, and liquid fertilisers to playing surfaces, in line with the product label, an individual COSHH assessment for each product on the agreed list, the requirement for a current City & Guilds NPTC PA1 (foundation) + PA6 (knapsack) or PA2A (boom on transported sprayer) certificate held by the applicator, and Voluntary Initiative / Amenity Forum Best Management Practice for amenity pesticide use. Excludes any application of products not on the agreed list, and any application without a current and matching applicator certificate.',
    hazards: [
      { hazard: 'Skin / inhalation / eye exposure to concentrate during measuring and mixing', risk: 'High', controls: 'Full chemical-handling PPE (nitrile gauntlets, splash apron, face shield, vapour-rated mask per label) worn during measuring and tank fill; mixing done over a bunded tray in the chemical store, never on the course; eye-wash bottle present; spill kit on the bench; product-specific COSHH sheet open and visible during mixing.' },
      { hazard: 'Spray drift to non-target areas — golfer locations, ponds, ditches, neighbouring property, catchment / SuDS receiving water', risk: 'High', controls: 'No application above wind speed on the product label (typically 8 mph); air-induction (low-drift) nozzles fitted as default; minimum 5 m no-spray buffer to surface water (10 m or more for some products — check LERAP category on the label); CRD / Defra LERAP buffer applied; applicator monitors wind direction continuously; spray boom shut off at headlands; drift kept off any SuDS feature feeding the course catchment, per Environment Agency catchment protection guidance.' },
      { hazard: 'Members / public on the course during or shortly after application', risk: 'High', controls: 'Application scheduled outside playing hours (dawn / dusk / weekly closure); minimum 24-hour and preferably 48-hour written / pro-shop / website notice to members ahead of application per R&A guidance; affected areas signed "Chemical application — keep off until [date / time]"; re-entry interval per product label respected; comms agreed with pro shop and course manager before application; full record of notice issued kept on file.' },
      { hazard: 'Slips on freshly sprayed greens or wet surfaces', risk: 'Med', controls: 'Aggressive-tread boots; no walking across just-applied greens until dry; pin / flag handling delayed until product has dried in or watered in.' },
      { hazard: 'Manual handling of full knapsack (16-20 kg) or chemical drums', risk: 'Med', controls: 'Two-person lift for 25 L drums; knapsack capacity limited to 15 L unless operator is fit-tested for the higher weight; rotate operators to limit single-day exposure; back-support harness on knapsack.' },
      { hazard: 'Spillage during transport on the course', risk: 'High', controls: 'Tanker / sprayer secured on utility cart with strap; spill kit (absorbent granules, polythene, gloves, disposal bag) on every cart; spill log + Environment Agency notification process briefed (incident hotline 0800 80 70 60); never carry concentrate on the course, only diluted in-tank mixture.' },
      { hazard: 'Operator fatigue / heat stress under full PPE in summer', risk: 'Med', controls: 'Application scheduled for cooler parts of the day; water and shaded rest breaks every 60 min; second operative present (no lone working with chemical) for full days; fluid intake monitored.' },
      { hazard: 'Empty container / wash-water disposal', risk: 'Med', controls: 'Triple-rinse all empty containers per Voluntary Initiative / Amenity Forum guidance, store in lockable cage pending licensed waste collection (waste transfer note kept); never wash sprayer down into a drain — wash-down on dedicated bunded pad with appropriate consented disposal route; full COSHH disposal log maintained.' },
      { hazard: 'Chemical store fire / leak in storage', risk: 'High', controls: 'Lockable, ventilated, signed chemical store built / sited per HSE / CRD guidance; bunded floor; product inventory + SDS folder kept at the door; documented Fire Risk Assessment under the Regulatory Reform (Fire Safety) Order 2005 covering the store; fire extinguisher (foam + dry powder) outside the door; access restricted to certificated staff; annual stock audit and out-of-date product disposal.' },
    ],
    sequence: [
      'Pre-application — confirm product is on the agreed list; check applicator certificate (PA1 + PA6/PA2) is current; review product label, COSHH assessment, weather forecast.',
      'Notify pro shop / starter of the time slot; affected holes signed off the rota; signage prepared.',
      'In the chemical store — measure concentrate over the bunded tray; mix into sprayer tank with clean water; record batch in the spray records log.',
      'Travel to the course — sprayer secured; route avoids member areas where possible.',
      'On the target area — walk the boundary, identify buffers (water, neighbouring property), confirm wind direction; calibrate sprayer pace.',
      'Apply per label rate; boom shut off at headlands; constant attention to drift and overlap.',
      'On completion — return to wash-down pad; triple-rinse tank; clean nozzles and filters; clean PPE; return PPE to store.',
      'Log entry — product, batch number, area, rate, weather, applicator name, time of re-entry permission for course. Empty containers triple-rinsed and stored for collection.',
    ],
    equipment: [
      'Tractor-mounted boom sprayer (calibrated, LOLER if lifted) OR backpack knapsack sprayer',
      'Air-induction nozzles (low-drift)',
      'Bunded mixing tray and measuring jugs in the chemical store',
      'Eye-wash bottle / station',
      'Spill kit (absorbent granules, polythene, disposal bag) on every cart',
      'Lockable chemical store ventilated to BS 9999',
      'Spray records log (legal requirement)',
      'Wind meter / weather station',
      'Signage — "Chemical application in progress / Keep off until [time]"',
    ],
    ppe: [
      'Nitrile gauntlets (full chemical resistance, NOT thin disposable)',
      'Chemical-resistant apron or splash suit',
      'Face shield / chemical splash goggles',
      'Vapour-rated mask (per product label — A1/A2/P2/P3 as required)',
      'Wellington-style chemical boots',
      'Coveralls (Tyvek-style for higher-toxicity products)',
      'Hi-vis worn over PPE on the course',
    ],
    emergency:
      'Chemical splash to skin — remove contaminated clothing, irrigate skin under running water for at least 15 min, A&E with product label / SDS. Splash to eye — 15-min eye-wash, 999. Inhalation — fresh air, 999 if symptoms persist. Ingestion — DO NOT induce vomiting, 999 with product label / SDS. Spill on course — STOP, contain with absorbent, photograph, report to Environment Agency if reaching water (0800 80 70 60). Sprayer overturn or major leak — evacuate downwind 50 m, 999, course manager notified. SDS / product labels are kept in the chemical store + a copy in each applicator\'s pack.',
  },

  {
    id: 'golf_course_tree_work',
    title: 'Golf course tree work — pruning, felling, chainsaw, chipper',
    trades: ['Greenskeeping', 'Groundskeeping', 'Golf course maintenance', 'Arboriculture'],
    description: 'Managing trees on the golf course — light pruning of overhanging limbs, removal of dead / dangerous trees, chainsaw work, wood chipping. NPTC-certified operators only.',
    defaultHazards: ['Chainsaw kickback', 'Falling timber', 'Working at height', 'Working near members', 'Manual handling', 'Wood chipper entanglement'],
    scope:
      'Tree-management work on the course — formative pruning, deadwood removal, removal of dangerous limbs over greens / paths / member areas, sectional felling of dead or unsafe trees, processing of arisings through a wood chipper. Excludes major tree felling on protected (TPO / conservation area) trees, and any work outside the operator\'s NPTC certification.',
    hazards: [
      { hazard: 'Chainsaw operation — kickback, contact injury, fatigue', risk: 'High', controls: 'Operator holds City & Guilds NPTC unit CS30 (maintenance + cross-cutting) plus the appropriate task units — CS31 (felling up to 380 mm), CS32 (above 380 mm), CS38/CS39 (climbing + aerial cutting / pole saw); refresher within last 5 years; chain brake working; pre-use inspection signed off; trigger-time limited per HAVS exposure values; chainsaw trousers (Type A or C as appropriate) worn.' },
      { hazard: 'Falling limbs / timber on members, staff, course features', risk: 'High', controls: 'Holes affected closed / re-routed before work starts — agreed with pro shop and noted on tee-sheet; minimum 2× tree height exclusion zone around drop zone; banksman / lookout posted on any path within fall reach; "Fore" called on any limb release; no work over occupied buildings or carts.' },
      { hazard: 'Working at height — pruning beyond ground reach', risk: 'High', controls: 'Tree-climbing arborist certified under NPTC CS38 + CS39 with twin-rope system and current LOLER inspection of harness / ropes; MEWP (cherry picker) operator IPAF-certified for the machine type (1B / 3A / 3B); rescue plan documented + ground crew briefed; no ladders against the trunk except for light pruning by trained operative.' },
      { hazard: 'Wood chipper — entanglement / ejected material', risk: 'High', controls: 'Operator certified under NPTC CS40 / equivalent; never feed material wearing loose clothing, gloves with loose cuffs, scarves or dangling cords; feed butt-end first; in-feed bar / kill cord checked daily; second operative present whenever chipper is running; PPE includes face shield, ear defenders, snag-free overall.' },
      { hazard: 'Chainsaw start-up / refuelling fire risk', risk: 'Med', controls: 'Drop-start banned; refuel on cool engine away from ignition / flammable material; petrol can BS 2050; spill kit on cart; chainsaw fuel only carried in approved fuel cans, not glass / soft-drinks bottles.' },
      { hazard: 'Lone working in remote tree zones', risk: 'Med', controls: 'NEVER perform chainsaw or climbing work alone — minimum two competent operatives, one ground crew with first-aid + chainsaw rescue training; lone-worker GPS-tracker as secondary backup; mobile signal verified before start.' },
      { hazard: 'HAVS from chainsaw / pole-saw extended use', risk: 'Med', controls: 'Daily trigger-time logged against HSE A(8) exposure values (2.5 / 5 m/s²); rotation between operatives; anti-vibration saws preferred; annual HAVS health surveillance.' },
      { hazard: 'Disposal of arisings — fire risk if burning, environmental impact', risk: 'Med', controls: 'Chipped material distributed as mulch where permitted; bonfire only with prior agreement from Course Manager AND member-notification, never in fire-season, never within 10 m of any tree, fence, or building; burning material attended at all times; bucket of water / fire extinguisher on hand.' },
    ],
    sequence: [
      'Pre-start — tree-specific risk assessment AND rescue plan; check no TPO / conservation status restricting work; confirm operator NPTC certifications match the task; confirm holes can be closed for the duration.',
      'Mark out drop zone, exclusion zone, escape routes; cone off / close affected holes.',
      'Notify pro shop / starter; agree comms while work is in progress.',
      'Pre-use checks on chainsaw + chipper + climbing kit / MEWP.',
      'Take limbs / sections per cut plan; ground crew clears the drop zone between cuts.',
      'Feed arisings into chipper or stack for later disposal; never leave a running chipper unattended.',
      'On completion — sweep the area, inspect the tree for any hanging limbs, leave site safe; release the hole back to play.',
      'Log job — operatives, trees worked, arisings disposal route, any incidents / near-misses.',
    ],
    equipment: [
      'Chainsaw (CE-marked, sharpened, chain brake tested) — class size matched to operator unit',
      'Pole saw / pole pruner',
      'Wood chipper (PUWER + LOLER for any towed unit)',
      'Climbing kit — twin-rope harness, lanyards, hand saw — LOLER inspected within 6 months',
      'MEWP if used (cherry picker class 1B / 3A / 3B)',
      'First-aid kit including bleed-control / haemorrhage pack on every chainsaw cart',
      'Hi-vis cones / barrier tape to mark exclusion zone',
      'Whistle / radio per operative',
    ],
    ppe: [
      'Chainsaw trousers (Type A for ground work, Type C for climbing) — EN 381 / EN ISO 11393',
      'Chainsaw boots with steel toe and chainsaw protection — EN ISO 17249',
      'Forestry helmet with integrated visor + ear defenders — EN 397 + EN 1731 + EN 352',
      'Cut-resistant gloves',
      'Hi-vis worn over all PPE on the course',
      'Harness + lanyards if climbing — LOLER inspected',
    ],
    emergency:
      'Chainsaw cut — apply pressure with haemorrhage pack, 999, do NOT remove saw from wound if embedded. Fall from tree / MEWP — DO NOT MOVE casualty unless airway-critical, 999. Crush from falling limb — clear airway, 999, do NOT move further. Chipper entanglement — STOP the machine via kill cord / feed bar, 999, DO NOT attempt to free until power off and chipper has stopped completely. Course Manager notified and incident logged for RIDDOR review.',
  },

  {
    id: 'golf_course_irrigation_maintenance',
    title: 'Golf course irrigation maintenance — repairs, sprinklers, valve boxes',
    trades: ['Greenskeeping', 'Groundskeeping', 'Golf course maintenance', 'Irrigation'],
    description: 'Maintaining the course irrigation system — sprinkler head replacement, valve repairs, leak finding and small excavations, pump-house inspection. Includes pressurised water hazards and isolation procedures.',
    defaultHazards: ['High-pressure water', 'Electrical (irrigation controllers)', 'Confined space (pump houses, large valve boxes)', 'Hidden services', 'Manual handling of valve box lids'],
    scope:
      'Routine and reactive maintenance of the golf course irrigation system — sprinkler head adjustment / replacement, valve repairs, small-diameter pipe repairs, leak investigation involving shallow excavation (< 600 mm), pump-house pressure / leak checks. Excludes new pipework installation under turf (separate RAMS), any deep excavation, and any high-voltage electrical work on the pump-station mains supply (qualified electrician only).',
    hazards: [
      { hazard: 'Pressurised water — system can hold 8-12 bar; head replacement under pressure causes serious injury', risk: 'High', controls: 'STRICT lock-out / tag-out procedure: isolate the relevant zone at the controller AND close the upstream valve AND open a downstream drain to relieve pressure before any work; pressure gauge confirms zero before unscrewing; tag with operative name + time + reason hung on the controller; only the operative who fitted the tag can remove it.' },
      { hazard: 'Electrical hazard from 24 V solenoid wiring and mains-fed controllers', risk: 'Med', controls: 'Controller / pump-house panel isolated at the local switch before opening; only City & Guilds 2382-22 (or equivalent electrically competent) staff work inside the panel; 24 V solenoid wiring tested with multimeter before disconnecting; never work on energised circuits.' },
      { hazard: 'Confined space — pump houses and large valve / valve-cluster chambers below ground', risk: 'High', controls: 'Treat any below-ground chamber > 1.2 m deep or with restricted ventilation as a confined space; Permit-to-Enter, gas monitor (O₂, CO, H₂S, LEL), tripod / man-rated harness, attendant at top, rescue plan; never enter alone; staff trained per Confined Spaces Regulations 1997.' },
      { hazard: 'Hidden services around irrigation runs — power, gas, drainage, fibre', risk: 'High', controls: 'Service drawings AND CAT-scan before any excavation; trial pit by hand to expose existing services within 1 m; Permit-to-Dig signed before any mechanical excavation; especially relevant near clubhouse / pump house / car park where multiple services converge.' },
      { hazard: 'Manual handling — valve box lids (cast iron, often 20-40 kg)', risk: 'Med', controls: 'Two-person lift on cast-iron lids; lifting keys used to break the seal; lid moved clear of the hole — never balanced on the edge; replace lid before leaving the box even temporarily; back-support brief for repeat-lift days.' },
      { hazard: 'Slips into open valve box or pump-house pit', risk: 'High', controls: 'Never leave a box open and unattended; barrier / cone around any open box; if work must pause, lid replaced or full barrier with reflective top board fitted; final walk-round to confirm all lids closed.' },
      { hazard: 'Working near live golf play (irrigation faults reported during open hours)', risk: 'Med', controls: 'Hi-vis worn; comms with pro shop; if work blocks a fairway or path, hole closed temporarily; vehicle parked off the line of play.' },
      { hazard: 'Legionella risk from stagnant water in dead-leg pipework or storage tanks', risk: 'Med', controls: 'L8 / HSG274 awareness — flushing schedule for any storage tanks documented; never directly inhale fine spray from a long-stagnant line; storage tanks inspected per a written Legionella management plan.' },
    ],
    sequence: [
      'Pre-start — confirm fault location / scope; review system schematic; agree isolation steps; agree comms with pro shop if play is affected.',
      'Travel to the area; mark out work zone with cones if near play / paths.',
      'Isolate — close upstream valve, isolate at controller, open downstream drain, confirm zero pressure on gauge, hang lock-out tag.',
      'Excavate by hand to expose the fault if needed (CAT-scan + Permit-to-Dig first if mechanical).',
      'Carry out the repair — sprinkler / valve / fitting replacement.',
      'Pressurise gradually, check for leaks, test the affected zone runs cleanly.',
      'Reinstate the ground — backfill, compact, replace turf cap or top-dress.',
      'Remove cones / tags; brief pro shop / Course Manager that the area is back in service; log the job in the irrigation maintenance record.',
    ],
    equipment: [
      'Lock-out / tag-out kit (padlocks, tags, hasps)',
      'Pressure gauge',
      'Lifting keys for cast-iron valve box lids',
      'Hand-dig tools (spade, fork, plumb bob, trowel)',
      'CAT-scan + Genny if any mechanical excavation needed',
      'Pipe-cutting / fitting kit appropriate to the pipework material (HDPE, MDPE, copper, PVC)',
      'Confined-space kit — gas monitor, tripod, harness, rescue plan — for pump house / deep chamber entry',
      'Multimeter for 24 V solenoid testing',
      'Bucket / pump for removing water from boxes',
    ],
    ppe: [
      'Hi-vis · safety boots · gloves · safety glasses',
      'Gauntlet gloves for valve / pipe work',
      'Hearing protection if pump house running',
      'Confined-space PPE (full body harness, escape set) if entry required',
      'Insulating gloves Class 0 (1000 V) when working on or near controllers',
    ],
    emergency:
      'Pressurised water injection injury — STOP work, A&E with description of pressure and fluid (NOT just clean water — irrigation lines may contain fertiliser / wetter dosing). Electric shock — isolate first, 999 if any loss of consciousness or burns. Confined-space casualty — DO NOT ENTER, alert attendant, follow rescue plan, 999. Service strike — STOP, isolate, 999 / 0800 111 999 (gas) / 105 (electricity). Backflow of contaminated water into mains — STOP, isolate, Course Manager + water supplier notified.',
  },
];

/**
 * Blank RAMS template — used by the "Blank RAMS" option on the RAMS list.
 *
 * Lives separately from RAMS_TEMPLATES so it doesn't appear in the trade-grouped
 * preset picker. RamsPresetForm.tsx falls through to this object when
 * templateId === 'blank'.
 *
 * Purpose: for users who already know exactly what they want to write — or who
 * want to dictate / type the whole thing from scratch — without being nudged
 * by a pre-filled draft.
 */
export const BLANK_RAMS_TEMPLATE: RamsTemplate = {
  id: 'blank',
  title: 'Blank RAMS — start from scratch',
  trades: ['Other'],
  description: 'A blank Risk Assessment & Method Statement form for when you know exactly what you want to write.',
  defaultHazards: [],
  scope: '',
  // Seed ONE empty hazard card so the HIGH/MED/LOW risk-pill UI is visible
  // the moment the form loads. Without this seed, blank-RAMS users saw the
  // form with no hazard cards at all (template.hazards was an empty array →
  // no cards mapped) — the resulting PDF format then diverged from preset-
  // based RAMS. With this seed, blank and preset RAMS share an identical
  // structure from first paint and the PDF is a proper, risk-rated RAMS
  // either way. Med is the default — user taps High or Low to change.
  hazards: [{ risk: 'Med', hazard: '', controls: '' }],
  sequence: [],
  equipment: [],
  ppe: [],
  emergency: '',
};
